import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Circle, CircleMarker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_URL, getValidToken } from '../utils/authUtils';

// Phase 10: Brokerage UI Components
import {
  RelationshipMemoryViewer,
  OutreachTemplateEditor,
  BrokerageAnalyticsDashboard,
  BulkOperationsPanel,
  StaffPerformanceView,
  RequestWorkflowModal
} from './admin/brokerage';

// ============================================
// ADMIN EXECUTIVE INTELLIGENCE DASHBOARD
// ============================================
// Main admin interface for the Executive Alumni
// Intelligence Platform. Tab-based layout covering:
//   - Dashboard (overview stats)
//   - Search (full-text + faceted)
//   - Imports (batch management)
//   - Reviews (role classification queue)
//   - Scoring (leadership scoring runs)
//   - Reports (18 analytical reports)
//   - Analytics (population trends)
// ============================================

// Captures a map click to set the radius-filter center. Must be a child of
// <MapContainer> so it can access the Leaflet map via the useMapEvents hook.
const RadiusClickHandler = ({ active, onPick }) => {
  useMapEvents({
    click(e) {
      if (active) onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Pans/zooms the Leaflet map to the radius center whenever it (or the zoom) changes.
// Child of <MapContainer> so it can reach the map via useMap.
const MapFlyTo = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView([center.lat, center.lng], zoom);
  }, [center, zoom, map]);
  return null;
};

const AdminExecutiveIntelligence = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Tab state ---
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');

  // --- Dashboard state ---
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // --- Search state ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({
    levels: [], companies: [], functions: [], industries: [],
    locations: [], skills: [], score_bands: [], open_to_work: null,
    min_leadership_score: null, universities: [], hbcu_only: false, linkedin_follower: null,
    exclude_self_employed: false, company_types: [], company_size_bands: [],
  });
  const [searchSort, setSearchSort] = useState({ field: null, dir: null }); // { field: 'score'|'band', dir: 'asc'|'desc' }
  const [searchSelected, setSearchSelected] = useState(new Set());
  const [showSearchAssignModal, setShowSearchAssignModal] = useState(false);
  const [searchAssignForm, setSearchAssignForm] = useState({ assigned_to_id: '', goal: 'engagement', priority: 'normal', due_date: '', purpose: '' });
  const [searchAssigning, setSearchAssigning] = useState(false);

  // --- Imports state ---
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchPage, setBatchPage] = useState(1);
  const [batchTotal, setBatchTotal] = useState(0);

  // --- Reviews state ---
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewStatusFilter, setReviewStatusFilter] = useState('pending');

  // --- Scoring state ---
  const [scoringQueue, setScoringQueue] = useState([]);
  const [scoringQueueLoading, setScoringQueueLoading] = useState(false);
  const [scoringQueueTotal, setScoringQueueTotal] = useState(0);
  const [scoringRunning, setScoringRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [scoringRuns, setScoringRuns] = useState([]);
  const [scoringRunsLoading, setScoringRunsLoading] = useState(false);
  const [scoringEnabled, setScoringEnabled] = useState(false);
  const [scoringEnabledLoading, setScoringEnabledLoading] = useState(false);

  // --- Reports state ---
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [footprintSort, setFootprintSort] = useState({ key: null, dir: 'desc' });
  const [footprintSearch, setFootprintSearch] = useState('');

  // --- Alumni Search state ---
  const [alumniSearchData, setAlumniSearchData] = useState(null);
  const [alumniSearchLoading, setAlumniSearchLoading] = useState(false);
  const [alumniSearchFilters, setAlumniSearchFilters] = useState({
    company: '', industries: [], levels: [], current_roles_only: false, search: '',
    university: '', university_search_all: false,
  });
  const [alumniSearchSort, setAlumniSearchSort] = useState({ key: 'full_name', dir: 'asc' });
  const [alumniSearchPage, setAlumniSearchPage] = useState(1);
  const [showAlumniSearch, setShowAlumniSearch] = useState(false);

  // --- Company Industries state ---
  const [companiesMissingIndustries, setCompaniesMissingIndustries] = useState([]);
  const [companiesWithIndustries, setCompaniesWithIndustries] = useState([]);
  const [companiesDiagnostics, setCompaniesDiagnostics] = useState(null);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [showIndustryManager, setShowIndustryManager] = useState(false);
  const [industryUpdateLoading, setIndustryUpdateLoading] = useState(false);
  const [showCompaniesWithIndustries, setShowCompaniesWithIndustries] = useState(false);
  const [csvImportFile, setCsvImportFile] = useState(null);
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportResults, setCsvImportResults] = useState(null);
  const [csvForceOverwrite, setCsvForceOverwrite] = useState(false);

  // --- Company Enrichment state ---
  const [enrichmentStats, setEnrichmentStats] = useState(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [enrichmentJobId, setEnrichmentJobId] = useState(null);
  const [enrichmentProgress, setEnrichmentProgress] = useState(null);
  const [enrichmentResult, setEnrichmentResult] = useState(null);

  // --- Public Company Classification state ---
  const [publicCompanyStats, setPublicCompanyStats] = useState(null);
  const [publicCompanyLoading, setPublicCompanyLoading] = useState(false);
  const [publicCompanyResult, setPublicCompanyResult] = useState(null);
  const [tier2ReviewList, setTier2ReviewList] = useState([]);  // [{role_name, matched, approved}]
  const publicCompanyFileRef = useRef(null);

  // --- LinkedIn Comments Import state ---
  const [showCommentImportModal, setShowCommentImportModal] = useState(false);
  const [commentImporting, setCommentImporting] = useState(false);
  const [commentImportMaxPages, setCommentImportMaxPages] = useState(1);
  const [commentCostSummary, setCommentCostSummary] = useState(null);
  const [commentImportResult, setCommentImportResult] = useState(null);
  const [commentSingleImporting, setCommentSingleImporting] = useState({});

  // --- Analytics state ---
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [trendDays, setTrendDays] = useState(90);

  // --- Calibration state ---
  const [calibrationRuns, setCalibrationRuns] = useState([]);
  const [calibrationRunsLoading, setCalibrationRunsLoading] = useState(false);
  const [calibrationAlerts, setCalibrationAlerts] = useState([]);
  const [calibrationAlertsLoading, setCalibrationAlertsLoading] = useState(false);
  const [calibrationAlertSummary, setCalibrationAlertSummary] = useState(null);
  const [calibrationTriggerLoading, setCalibrationTriggerLoading] = useState(false);

  // --- Reference scores state ---
  const [referenceScores, setReferenceScores] = useState([]);
  const [referenceScoresLoading, setReferenceScoresLoading] = useState(false);
  const [referenceStats, setReferenceStats] = useState(null);
  const [showAddReferenceModal, setShowAddReferenceModal] = useState(false);
  const [referenceFormData, setReferenceFormData] = useState({
    profile_id: '',
    human_score: '',
    rationale: '',
    trajectory_score: '',
    velocity_score: '',
    scope_score: '',
    recency_score: '',
  });
  const [referenceFormLoading, setReferenceFormLoading] = useState(false);
  const [profileSearchQuery, setProfileSearchQuery] = useState('');
  const [profileSearchResults, setProfileSearchResults] = useState([]);
  const [profileSearchLoading, setProfileSearchLoading] = useState(false);

  // --- Modal state ---
  const [showScoreBandsModal, setShowScoreBandsModal] = useState(false);

  // --- Unmatched profiles state ---
  const [unmatchedProfiles, setUnmatchedProfiles] = useState(null);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);

  // --- Bulk link profiles to contacts state ---
  const [bulkLinkLoading, setBulkLinkLoading] = useState(false);
  const [bulkLinkResult, setBulkLinkResult] = useState(null);

  // --- Classification state ---
  const [classificationStats, setClassificationStats] = useState(null);
  const [classificationStatsLoading, setClassificationStatsLoading] = useState(false);
  const [classificationRunning, setClassificationRunning] = useState(false);

  // --- Bookmarks/Favorites state ---
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);

  // --- Brokerage state (Phase 10) ---
  const [brokerageView, setBrokerageView] = useState('queue'); // queue, templates, analytics
  const [brokerageQueue, setBrokerageQueue] = useState([]);
  const [brokerageQueueLoading, setBrokerageQueueLoading] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [showMemoryViewer, setShowMemoryViewer] = useState(false);
  const [selectedExecId, setSelectedExecId] = useState(null);
  const [selectedExecName, setSelectedExecName] = useState('');
  const [showStaffPerformance, setShowStaffPerformance] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedStaffName, setSelectedStaffName] = useState('');
  const [staffMembers, setStaffMembers] = useState([]);
  const [bookmarkActionLoading, setBookmarkActionLoading] = useState({});
  // Request detail modal state
  const [showRequestDetail, setShowRequestDetail] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  // Queue filters
  const [queueFilters, setQueueFilters] = useState({
    status: [],          // multi-select status filter
    request_type: [],    // multi-select type filter
    assigned_to: null,   // null=all, 0=unassigned, N=specific staff
    sort_by: 'priority_score',
    sort_dir: 'desc',
  });
  // Per-row assignment state
  const [assigningRequestId, setAssigningRequestId] = useState(null);
  const [assignStaffSelection, setAssignStaffSelection] = useState({});
  // Quick tier upgrade state (inline in queue)
  const [upgradingRequestId, setUpgradingRequestId] = useState(null);
  const [upgradeTierSlug, setUpgradeTierSlug] = useState('');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [queueTiers, setQueueTiers] = useState([]);

  // --- Access control state ---
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentAdminId, setCurrentAdminId] = useState(null);
  const [adminPermissions, setAdminPermissions] = useState(null);

  // --- Map state ---
  const [mapProfiles, setMapProfiles] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [geocodingStats, setGeocodingStats] = useState(null);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [geocodingRunning, setGeocodingRunning] = useState(false);
  const [missingStateData, setMissingStateData] = useState(null);
  const [showMissingStateModal, setShowMissingStateModal] = useState(false);

  // --- Search Map Panel state ---
  const [showSearchMapPanel, setShowSearchMapPanel] = useState(false);
  const [searchMapProfiles, setSearchMapProfiles] = useState([]);
  const [searchMapLoading, setSearchMapLoading] = useState(false);

  // --- Map Location Filters state ---
  const [mapLocationOptions, setMapLocationOptions] = useState({ cities: [], states: [], countries: [] });
  const [mapLocationFilters, setMapLocationFilters] = useState({ cities: [], states: [], countries: [] });
  const [mapLocationOptionsLoading, setMapLocationOptionsLoading] = useState(false);
  const [mapExportLoading, setMapExportLoading] = useState(false);

  // --- Radius filter state ---
  const [radiusCenter, setRadiusCenter] = useState(null);   // { lat, lng } or null
  const [radiusMiles, setRadiusMiles] = useState(50);
  const [radiusActive, setRadiusActive] = useState(false);  // radius currently applied to results
  const [radiusPickMode, setRadiusPickMode] = useState(false); // next map click sets the center
  const [radiusSearchText, setRadiusSearchText] = useState('');
  const [radiusSearchLoading, setRadiusSearchLoading] = useState(false);

  // --- General state ---
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Restore search state from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('execSearchState');
      if (saved) {
        const state = JSON.parse(saved);
        if (state.query != null) setSearchQuery(state.query);
        if (state.filters) {
          // Merge saved filters with defaults to handle new fields
          setSearchFilters(prev => ({
            ...prev,
            ...state.filters,
            // Ensure new fields have defaults if missing from old saved data
            universities: state.filters.universities || [],
            hbcu_only: state.filters.hbcu_only || false,
          }));
        }
        if (state.results) setSearchResults(state.results);
        if (state.total != null) setSearchTotal(state.total);
        if (state.page != null) setSearchPage(state.page);
        if (state.sort) setSearchSort(state.sort);
      }
    } catch (e) {
      // Ignore corrupted sessionStorage data
    }
  }, []);

  // Sync tab with URL
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // Auto-open Unprovisioned Contacts section if section=unprovisioned
    if (searchParams.get('section') === 'unprovisioned' && tabFromUrl === 'imports') {
      setShowUnprovisionedSection(true);
      if (!unprovisionedData) fetchUnprovisioned(1);
    }
  }, [searchParams]);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    setError(null);
  };

  // Auth check
  useEffect(() => {
    const token = getValidToken();
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Permission check: only admins with Alumni Management access can view this page
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = getValidToken();
        if (!token) return;
        const res = await axios.get(`${API_URL}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.hide_alumni_management) {
          setAccessDenied(true);
        }
        setIsSuperAdmin(res.data?.is_super_admin || false);
        setCurrentAdminId(res.data?.id || null);
        setAdminPermissions(res.data);
      } catch (err) {
        console.error('Permission check failed:', err);
        setAccessDenied(true);
      } finally {
        setAccessChecked(true);
      }
    };
    checkAccess();
  }, []);

  // Redirect to first visible tab if current tab is hidden
  useEffect(() => {
    if (adminPermissions && visibleTabs.length > 0 && !visibleTabs.find(t => t.key === activeTab)) {
      changeTab(visibleTabs[0].key);
    }
  }, [adminPermissions, activeTab]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboard();
    else if (activeTab === 'search') {
      // Re-execute search if there are existing results (e.g., navigating back from profile viewer)
      if (searchResults.length > 0 || searchQuery) executeSearch(searchPage);
      fetchEnrichmentStats();
      fetchPublicCompanyStats();
    }
    else if (activeTab === 'favorites') fetchBookmarks();
    else if (activeTab === 'map') { fetchMapProfiles(); fetchGeocodingStats(); fetchMapLocationOptions(); }
    else if (activeTab === 'imports') fetchBatches();
    else if (activeTab === 'reviews') fetchReviews();
    else if (activeTab === 'scoring') { fetchScoringSettings(); fetchScoringQueue(); fetchScoringRuns(); }
    else if (activeTab === 'calibration') { fetchCalibrationRuns(); fetchCalibrationAlerts(); fetchCalibrationAlertSummary(); fetchReferenceScores(); fetchReferenceStats(); }
    else if (activeTab === 'analytics') fetchAnalytics();
  }, [activeTab]);

  // --- API helpers ---
  const authHeaders = () => {
    const token = getValidToken();
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // ============================================
  // BACKGROUND IMPORT JOB POLLING
  // ============================================
  const pollImportJob = async (jobId, onProgress) => {
    const maxAttempts = 300; // 5 minutes at 1s intervals
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, i < 10 ? 1000 : 2000)); // 1s first 10, then 2s
      try {
        const res = await axios.get(`${API_URL}/api/exec/import-jobs/${jobId}`, authHeaders());
        const job = res.data;
        if (onProgress) onProgress(job);
        if (job.status === 'completed') return job.result;
        if (job.status === 'failed') throw new Error(job.error || 'Import failed');
      } catch (err) {
        if (err.response?.status === 404) throw new Error('Import job expired or not found');
        // Network blip — keep polling
        if (i > 5) throw err;
      }
    }
    throw new Error('Import timed out after 5 minutes');
  };

  // ============================================
  // DASHBOARD TAB
  // ============================================
  const fetchDashboard = async () => {
    setDashboardLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/exec/dashboard`, authHeaders());
      setDashboardData(res.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchUnmatchedProfiles = async () => {
    setUnmatchedLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/exec/unmatched-profiles`, authHeaders());
      setUnmatchedProfiles(res.data);
    } catch (err) {
      console.error('Error fetching unmatched profiles:', err);
      setError('Failed to load unmatched profiles: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUnmatchedLoading(false);
    }
  };

  const bulkLinkProfilesToContacts = async () => {
    setBulkLinkLoading(true);
    setBulkLinkResult(null);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/exec/profiles/link-to-contacts`, {}, authHeaders());
      setBulkLinkResult(res.data);
      // Refresh dashboard stats and unmatched profiles
      fetchDashboard();
      if (unmatchedProfiles) {
        fetchUnmatchedProfiles();
      }
    } catch (err) {
      console.error('Error bulk linking profiles:', err);
      setError('Failed to link profiles: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBulkLinkLoading(false);
    }
  };

  // ============================================
  // SEARCH TAB
  // ============================================
  const executeSearch = async (page = 1, sortOverride) => {
    setSearchLoading(true);
    setError(null);
    setSearchSelected(new Set());
    try {
      const sort = sortOverride !== undefined ? sortOverride : searchSort;
      const body = {
        query: searchQuery || null,
        page,
        page_size: 25,
      };
      // Add non-empty filters
      if (searchFilters.levels.length) body.levels = searchFilters.levels;
      if (searchFilters.companies.length) body.companies = searchFilters.companies;
      if (searchFilters.functions.length) body.functions = searchFilters.functions;
      if (searchFilters.industries.length) body.industries = searchFilters.industries;
      if (searchFilters.locations.length) body.locations = searchFilters.locations;
      if (searchFilters.skills.length) body.skills = searchFilters.skills;
      if (searchFilters.score_bands.length) body.score_bands = searchFilters.score_bands;
      if (searchFilters.open_to_work !== null) body.open_to_work = searchFilters.open_to_work;
      if (searchFilters.min_leadership_score !== null) body.min_leadership_score = searchFilters.min_leadership_score;
      if (searchFilters.universities.length) body.universities = searchFilters.universities;
      if (searchFilters.hbcu_only) body.hbcu_only = true;
      if (searchFilters.linkedin_follower !== null) body.linkedin_follower = searchFilters.linkedin_follower;
      if (searchFilters.exclude_self_employed) body.exclude_self_employed = true;
      if (searchFilters.company_types.length) body.company_types = searchFilters.company_types;
      if (searchFilters.company_size_bands.length) body.company_size_bands = searchFilters.company_size_bands;
      if (sort.field) {
        body.sort_by = sort.field;
        body.sort_dir = sort.dir || 'desc';
      }

      const res = await axios.post(`${API_URL}/api/exec/search`, body, authHeaders());
      const items = res.data.items || [];
      const total = res.data.total || 0;
      setSearchResults(items);
      setSearchTotal(total);
      setSearchPage(page);
      // Persist search state for back-navigation
      try {
        sessionStorage.setItem('execSearchState', JSON.stringify({
          query: searchQuery,
          filters: searchFilters,
          sort: searchSort,
          results: items,
          total,
          page,
        }));
      } catch (e) {
        // sessionStorage may be full or unavailable
      }
      // Update map panel if visible
      if (showSearchMapPanel) {
        fetchSearchMapProfiles();
      }
    } catch (err) {
      console.error('Error searching:', err);
      setError('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchTotal(0);
    setSearchPage(1);
    setSearchFilters({
      levels: [], companies: [], functions: [], industries: [],
      locations: [], skills: [], score_bands: [], open_to_work: null,
      min_leadership_score: null, universities: [], hbcu_only: false, linkedin_follower: null,
      exclude_self_employed: false, company_types: [], company_size_bands: [],
    });
    setSearchSort({ field: null, dir: null });
    sessionStorage.removeItem('execSearchState');
    setSearchMapProfiles([]);
  };

  // Fetch map profiles matching current search filters
  const fetchSearchMapProfiles = async () => {
    setSearchMapLoading(true);
    try {
      // Build query params from current filters (backend-supported filters)
      const params = new URLSearchParams();
      if (searchFilters.companies.length) params.set('companies', searchFilters.companies.join(','));
      if (searchFilters.score_bands.length) params.set('score_bands', searchFilters.score_bands.join(','));
      if (searchFilters.min_leadership_score) params.set('min_score', searchFilters.min_leadership_score);
      // Use map location filters if set
      if (mapLocationFilters.cities.length) params.set('cities', mapLocationFilters.cities.join(','));
      if (mapLocationFilters.states.length) params.set('states', mapLocationFilters.states.join(','));
      if (mapLocationFilters.countries.length) params.set('countries', mapLocationFilters.countries.join(','));

      const res = await axios.get(`${API_URL}/api/exec/map/profiles?${params}`, authHeaders());
      let profiles = res.data.items || [];

      // Client-side filter for criteria not supported by map endpoint
      // Filter by search query if present
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        profiles = profiles.filter(p =>
          (p.full_name && p.full_name.toLowerCase().includes(q)) ||
          (p.headline && p.headline.toLowerCase().includes(q)) ||
          (p.current_company && p.current_company.toLowerCase().includes(q)) ||
          (p.current_title && p.current_title.toLowerCase().includes(q))
        );
      }

      // Filter by locations text if specified (from search tab location filter)
      if (searchFilters.locations.length) {
        profiles = profiles.filter(p => {
          if (!p.location_raw) return false;
          const loc = p.location_raw.toLowerCase();
          return searchFilters.locations.some(l => loc.includes(l.toLowerCase()));
        });
      }

      setSearchMapProfiles(profiles);
    } catch (err) {
      console.error('Error fetching search map profiles:', err);
    } finally {
      setSearchMapLoading(false);
    }
  };

  // ============================================
  // BOOKMARKS / FAVORITES
  // ============================================
  const fetchBookmarks = async () => {
    setBookmarksLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/bookmarks`, authHeaders());
      setBookmarks(res.data.items || []);
    } catch (err) {
      console.error('Error fetching bookmarks:', err);
      setError('Failed to load bookmarks');
    } finally {
      setBookmarksLoading(false);
    }
  };

  const toggleBookmark = async (profileId, isCurrentlyBookmarked) => {
    setBookmarkActionLoading(prev => ({ ...prev, [profileId]: true }));
    try {
      if (isCurrentlyBookmarked) {
        await axios.delete(`${API_URL}/api/exec/bookmarks/${profileId}`, authHeaders());
      } else {
        await axios.post(`${API_URL}/api/exec/bookmarks/${profileId}`, {}, authHeaders());
      }
      // Update search results to reflect new bookmark status
      setSearchResults(prev => prev.map(r =>
        r.id === profileId ? { ...r, is_bookmarked: !isCurrentlyBookmarked } : r
      ));
      // Update map profiles to reflect new bookmark status (for marker color)
      setMapProfiles(prev => prev.map(p =>
        p.id === profileId ? { ...p, is_bookmarked: !isCurrentlyBookmarked } : p
      ));
      // Refresh bookmarks list if on favorites tab
      if (activeTab === 'favorites') {
        fetchBookmarks();
      }
    } catch (err) {
      console.error('Error toggling bookmark:', err);
      setError('Failed to update bookmark');
    } finally {
      setBookmarkActionLoading(prev => ({ ...prev, [profileId]: false }));
    }
  };

  // ============================================
  // MAP TAB
  // ============================================
  const fetchMapLocationOptions = async (filterCountry = null, filterState = null) => {
    setMapLocationOptionsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCountry) params.set('country', filterCountry);
      if (filterState) params.set('state', filterState);
      const res = await axios.get(`${API_URL}/api/exec/map/locations?${params}`, authHeaders());
      setMapLocationOptions(res.data || { cities: [], states: [], countries: [] });
    } catch (err) {
      console.error('Error fetching map location options:', err);
    } finally {
      setMapLocationOptionsLoading(false);
    }
  };

  // Build radius query params. radiusOverride: undefined => read from state,
  // null => explicitly no radius, {lat,lng,miles} => use that radius.
  const radiusParamsFor = (radiusOverride) => {
    const r = radiusOverride !== undefined
      ? radiusOverride
      : (radiusActive && radiusCenter ? { lat: radiusCenter.lat, lng: radiusCenter.lng, miles: radiusMiles } : null);
    return r;
  };

  const fetchMapProfiles = async (filters = null, radiusOverride = undefined) => {
    setMapLoading(true);
    try {
      const params = new URLSearchParams();
      const f = filters || mapLocationFilters;
      if (f.cities.length) params.set('cities', f.cities.join(','));
      if (f.states.length) params.set('states', f.states.join(','));
      if (f.countries.length) params.set('countries', f.countries.join(','));

      const r = radiusParamsFor(radiusOverride);
      if (r) {
        params.set('center_lat', r.lat);
        params.set('center_lng', r.lng);
        params.set('radius_miles', r.miles);
      }

      const res = await axios.get(`${API_URL}/api/exec/map/profiles?${params}`, authHeaders());
      setMapProfiles(res.data.items || []);
    } catch (err) {
      console.error('Error fetching map profiles:', err);
      setError('Failed to load map data');
    } finally {
      setMapLoading(false);
    }
  };

  // --- Radius filter handlers ---
  const handleRadiusPick = (lat, lng) => {
    const center = { lat, lng };
    setRadiusCenter(center);
    setRadiusActive(true);
    setRadiusPickMode(false);
    fetchMapProfiles(null, { lat, lng, miles: radiusMiles });
  };

  const applyRadius = () => {
    if (!radiusCenter) return;
    setRadiusActive(true);
    fetchMapProfiles(null, { lat: radiusCenter.lat, lng: radiusCenter.lng, miles: radiusMiles });
  };

  const clearRadius = () => {
    setRadiusCenter(null);
    setRadiusActive(false);
    setRadiusPickMode(false);
    fetchMapProfiles(null, null);
  };

  // Map radius (miles) → a sensible zoom level so the circle fits the viewport.
  const radiusZoom = (miles) => (miles <= 10 ? 10 : miles <= 25 ? 9 : miles <= 50 ? 8 : miles <= 100 ? 7 : 6);

  // Geocode a typed city/address (via Nominatim) and use it as the center.
  const geocodeRadiusCenter = async () => {
    const q = radiusSearchText.trim();
    if (!q) return;
    setRadiusSearchLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/exec/geocode/search?q=${encodeURIComponent(q)}`,
        authHeaders()
      );
      const { lat, lng } = res.data;
      if (typeof lat === 'number' && typeof lng === 'number') {
        handleRadiusPick(lat, lng); // sets center + applies radius (+ map flies to it)
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError(`Could not find a location for "${q}"`);
      } else {
        console.error('Error geocoding location:', err);
        setError('Failed to look up that location');
      }
    } finally {
      setRadiusSearchLoading(false);
    }
  };

  // Export the constituents matching the current map filters as a CSV.
  // Uses the same filter params as fetchMapProfiles so the export mirrors the map.
  const downloadMapExport = async () => {
    setMapExportLoading(true);
    try {
      const params = new URLSearchParams();
      const f = mapLocationFilters;
      if (f.cities.length) params.set('cities', f.cities.join(','));
      if (f.states.length) params.set('states', f.states.join(','));
      if (f.countries.length) params.set('countries', f.countries.join(','));

      if (radiusActive && radiusCenter) {
        params.set('center_lat', radiusCenter.lat);
        params.set('center_lng', radiusCenter.lng);
        params.set('radius_miles', radiusMiles);
      }

      const res = await axios.get(
        `${API_URL}/api/exec/map/profiles/export?${params}`,
        { ...authHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `executive_map_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting map profiles:', err);
      setError('Failed to export map data');
    } finally {
      setMapExportLoading(false);
    }
  };

  const fetchGeocodingStats = async () => {
    setGeocodingLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/geocode/stats`, authHeaders());
      setGeocodingStats(res.data);
    } catch (err) {
      console.error('Error fetching geocoding stats:', err);
    } finally {
      setGeocodingLoading(false);
    }
  };

  const runGeocoding = async (limit = 100, force = false) => {
    setGeocodingRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/exec/geocode/batch`, { limit, force }, authHeaders());
      // Refresh stats, map, and location options after geocoding
      await Promise.all([fetchGeocodingStats(), fetchMapProfiles(), fetchMapLocationOptions()]);
      return res.data;
    } catch (err) {
      console.error('Error running geocoding:', err);
      setError('Failed to run geocoding');
    } finally {
      setGeocodingRunning(false);
    }
  };

  const fetchMissingStateData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/geocode/missing-state?limit=50`, authHeaders());
      setMissingStateData(res.data);
      setShowMissingStateModal(true);
    } catch (err) {
      console.error('Error fetching missing state data:', err);
      setError('Failed to fetch missing state data');
    }
  };

  const [parsingLocations, setParsingLocations] = useState(false);

  const parseLocationFields = async () => {
    setParsingLocations(true);
    try {
      const res = await axios.post(`${API_URL}/api/exec/geocode/parse-locations`, { limit: 500 }, authHeaders());
      // Refresh stats and location options after parsing
      await Promise.all([fetchGeocodingStats(), fetchMapLocationOptions()]);
      alert(`Parsed ${res.data.total} profiles: ${res.data.updated} updated with state, ${res.data.no_state_found} could not extract state`);
    } catch (err) {
      console.error('Error parsing location fields:', err);
      setError('Failed to parse location fields');
    } finally {
      setParsingLocations(false);
    }
  };

  const [normalizingCities, setNormalizingCities] = useState(false);

  const normalizeCityNames = async () => {
    setNormalizingCities(true);
    try {
      const res = await axios.post(`${API_URL}/api/exec/geocode/normalize-cities`, { limit: 1000 }, authHeaders());
      // Refresh location options after normalizing
      await fetchMapLocationOptions();
      alert(`Processed ${res.data.total} profiles: ${res.data.changed} city names cleaned up`);
    } catch (err) {
      console.error('Error normalizing city names:', err);
      setError('Failed to normalize city names');
    } finally {
      setNormalizingCities(false);
    }
  };

  // Custom marker icon for exec profiles
  const createExecMarkerIcon = (profile) => {
    const color = profile.is_bookmarked ? '#EAB308' : // yellow for bookmarked
                  profile.score_band === 'executive' ? '#7C3AED' : // purple for executive
                  profile.score_band === 'senior' ? '#2563EB' : // blue for senior
                  profile.score_band === 'established' ? '#059669' : // green for established
                  '#6B7280'; // gray for others/unscored

    return L.divIcon({
      className: 'custom-exec-marker',
      html: `<div style="
        background-color: ${color};
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
  };

  // ============================================
  // IMPORTS TAB
  // ============================================
  const fetchBatches = async (page = 1) => {
    setBatchesLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, page_size: 20 });
      const res = await axios.get(`${API_URL}/api/exec/import/batches?${params}`, authHeaders());
      setBatches(res.data.batches || []);
      setBatchTotal(res.data.total || 0);
      setBatchPage(page);
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError('Failed to load import batches');
    } finally {
      setBatchesLoading(false);
    }
  };

  const [jsonImportLoading, setJsonImportLoading] = useState(false);
  const [jsonImportResult, setJsonImportResult] = useState(null);
  const [jsonImportFileName, setJsonImportFileName] = useState('');
  const [jsonImportProfileCount, setJsonImportProfileCount] = useState(0);

  const handleJsonImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError(null);
    setJsonImportLoading(true);
    setJsonImportResult(null);
    setJsonImportFileName(file.name);
    try {
      const text = await file.text();
      const profiles = JSON.parse(text);
      const profileArray = Array.isArray(profiles) ? profiles : [profiles];
      setJsonImportProfileCount(profileArray.length);
      const res = await axios.post(`${API_URL}/api/exec/import/json`, {
        profiles: profileArray,
      }, authHeaders());
      setJsonImportResult(res.data);
      fetchBatches();
    } catch (err) {
      console.error('Error importing JSON:', err);
      setError('JSON import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setJsonImportLoading(false);
      event.target.value = '';
    }
  };

  // ============================================
  // ALUMNI CSV IMPORT (Multi-Source)
  // ============================================
  const [alumniImportSourceType, setAlumniImportSourceType] = useState('');
  const [alumniImportFile, setAlumniImportFile] = useState(null);
  const [alumniImportFileName, setAlumniImportFileName] = useState('');
  const [alumniImportLoading, setAlumniImportLoading] = useState(false);
  const [alumniImportPreview, setAlumniImportPreview] = useState(null);
  const [alumniImportResult, setAlumniImportResult] = useState(null);
  const [alumniImportOptions, setAlumniImportOptions] = useState({
    updateExisting: true,
    createNew: true,
    dryRun: false,
    includeLowConfidence: false, // If true, include all matches including name-only (55%)
  });

  const SOURCE_TYPES = [
    { key: 'alumni_db_1', name: 'Alumni Database #1 (NACME iContact)', description: 'With [bracketed] columns: [email], [fname], [lname], [university]...' },
    { key: 'alumni_db_2', name: 'Alumni Database #2 (AlumniSync)', description: 'With spaced columns: EMAIL_ADDR, First Name, Last Name, School...' },
    { key: 'alumni_db_3', name: 'Alumni Database #3 (PCI - Dual Email)', description: 'With Email_Address_1 and Email_Address_2 columns (both imported)' },
  ];

  // ============================================
  // LINKEDIN FOLLOWERS IMPORT
  // ============================================
  const [linkedinFollowersFile, setLinkedinFollowersFile] = useState(null);
  const [linkedinFollowersFileName, setLinkedinFollowersFileName] = useState('');
  const [linkedinFollowersLoading, setLinkedinFollowersLoading] = useState(false);
  const [linkedinFollowersResult, setLinkedinFollowersResult] = useState(null);
  const [linkedinFollowersStats, setLinkedinFollowersStats] = useState(null);
  const [showLinkedinFollowersSection, setShowLinkedinFollowersSection] = useState(false);

  const handleLinkedinFollowersFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLinkedinFollowersFile(file);
    setLinkedinFollowersFileName(file.name);
    setLinkedinFollowersResult(null);
  };

  const runLinkedinFollowersImport = async () => {
    if (!linkedinFollowersFile) {
      setError('Please select a CSV file');
      return;
    }

    setLinkedinFollowersLoading(true);
    setError(null);
    setImportProgress(null);
    try {
      const csvContent = await linkedinFollowersFile.text();
      const res = await axios.post(`${API_URL}/api/exec/linkedin-followers/import`, {
        csv_content: csvContent,
      }, authHeaders());

      if (res.data.job_id) {
        // Background processing — poll for results
        setImportProgress({ progress: 0, total: 0, job_type: 'linkedin_followers' });
        const result = await pollImportJob(res.data.job_id, (job) => {
          setImportProgress({ progress: job.progress, total: job.total, job_type: 'linkedin_followers' });
        });
        setLinkedinFollowersResult(result);
        fetchLinkedinFollowersStats();
      } else {
        setLinkedinFollowersResult(res.data);
        fetchLinkedinFollowersStats();
      }
    } catch (err) {
      console.error('Error importing LinkedIn followers:', err);
      setError('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLinkedinFollowersLoading(false);
      setImportProgress(null);
    }
  };

  const fetchLinkedinFollowersStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/linkedin-followers/stats`, authHeaders());
      setLinkedinFollowersStats(res.data);
    } catch (err) {
      console.error('Error fetching LinkedIn followers stats:', err);
    }
  };

  const downloadLinkedinFollowersExport = () => {
    if (!linkedinFollowersResult?.export_csv) return;

    const blob = new Blob([linkedinFollowersResult.export_csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `linkedin_followers_matched_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadUnlinkedLinkedInUrls = async (format = 'txt') => {
    try {
      const res = await axios.get(
        `${API_URL}/api/exec/linkedin-followers/export-urls?format=${format}`,
        {
          ...authHeaders(),
          responseType: 'blob',
        }
      );
      const blob = new Blob([res.data], {
        type: format === 'csv' ? 'text/csv' : 'text/plain'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `linkedin_urls_for_ingestion_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading URLs:', err);
      alert('Failed to download URLs');
    }
  };

  // State for relink operation
  const [relinkLoading, setRelinkLoading] = useState(false);
  const [relinkResult, setRelinkResult] = useState(null);
  const [showLinkageWorkflowModal, setShowLinkageWorkflowModal] = useState(false);

  const relinkFollowersToProfiles = async () => {
    setRelinkLoading(true);
    setRelinkResult(null);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/exec/linkedin-followers/relink`, {}, authHeaders());
      setRelinkResult(res.data);
      // Refresh stats after relinking
      fetchLinkedinFollowersStats();
    } catch (err) {
      console.error('Error relinking followers:', err);
      setError('Relink failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRelinkLoading(false);
    }
  };

  const handleAlumniFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setAlumniImportFile(file);
    setAlumniImportFileName(file.name);
    setAlumniImportResult(null);
    setAlumniImportPreview(null);

    // If source type is selected, auto-preview
    if (alumniImportSourceType) {
      await previewAlumniImport(file, alumniImportSourceType);
    }
  };

  const previewAlumniImport = async (file, sourceType) => {
    if (!file || !sourceType) return;

    setAlumniImportLoading(true);
    setError(null);
    try {
      const csvContent = await file.text();
      const res = await axios.post(`${API_URL}/api/exec/contacts/import/alumni-csv/preview`, {
        source_type: sourceType,
        csv_content: csvContent,
      }, authHeaders());
      setAlumniImportPreview(res.data);
    } catch (err) {
      console.error('Error previewing CSV:', err);
      setError('Preview failed: ' + (err.response?.data?.detail || err.message));
      setAlumniImportPreview(null);
    } finally {
      setAlumniImportLoading(false);
    }
  };

  const handleAlumniSourceTypeChange = async (sourceType) => {
    setAlumniImportSourceType(sourceType);
    setAlumniImportResult(null);
    if (alumniImportFile && sourceType) {
      await previewAlumniImport(alumniImportFile, sourceType);
    }
  };

  const runAlumniImport = async (dryRun = false) => {
    if (!alumniImportFile || !alumniImportSourceType) {
      setError('Please select a source type and file');
      return;
    }

    setAlumniImportLoading(true);
    setError(null);
    setResolvedReviews({}); // Reset resolved reviews for new import
    setImportProgress(null);
    try {
      const csvContent = await alumniImportFile.text();
      const res = await axios.post(`${API_URL}/api/exec/contacts/import/alumni-csv`, {
        source_type: alumniImportSourceType,
        csv_content: csvContent,
        update_existing: alumniImportOptions.updateExisting,
        create_new: alumniImportOptions.createNew,
        dry_run: dryRun,
        include_low_confidence: alumniImportOptions.includeLowConfidence,
      }, { ...authHeaders(), timeout: 300000 });

      if (res.data.job_id) {
        // Background processing — poll for results
        setImportProgress({ progress: 0, total: 0, job_type: 'alumni_csv' });
        const result = await pollImportJob(res.data.job_id, (job) => {
          setImportProgress({ progress: job.progress, total: job.total, job_type: 'alumni_csv' });
        });
        setAlumniImportResult(result);
        if (result.success) {
          setAlumniImportFile(null);
          setAlumniImportFileName('');
          setAlumniImportPreview(null);
        }
      } else {
        // Synchronous response (dry_run mode)
        setAlumniImportResult(res.data);
        if (!dryRun && res.data.success) {
          setAlumniImportFile(null);
          setAlumniImportFileName('');
          setAlumniImportPreview(null);
        }
      }
    } catch (err) {
      console.error('Error importing alumni CSV:', err);
      setError('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAlumniImportLoading(false);
      setImportProgress(null);
    }
  };

  // State for review resolution
  const [reviewResolutionLoading, setReviewResolutionLoading] = useState({});
  const [resolvedReviews, setResolvedReviews] = useState({});

  const resolveReviewItem = async (reviewItem, action) => {
    // action: 'accept' | 'reject'
    const key = `${reviewItem.row}-${reviewItem.potential_match.id}`;
    setReviewResolutionLoading(prev => ({ ...prev, [key]: true }));

    // Collect all emails from the review item (supports multi-email sources)
    const emails = reviewItem.csv_data.emails || [reviewItem.csv_data.email].filter(Boolean);

    try {
      await axios.post(`${API_URL}/api/exec/contacts/import/alumni-csv/resolve-review`, {
        contact_id: reviewItem.potential_match.id,
        csv_emails: emails,  // Array of all emails from this row
        action: action,
        notes: `Resolved via import review - Row ${reviewItem.row}, ${reviewItem.method} match at ${(reviewItem.confidence * 100).toFixed(0)}%`,
      }, authHeaders());

      setResolvedReviews(prev => ({ ...prev, [key]: action }));

      // Update the import result stats
      if (action === 'accept') {
        setAlumniImportResult(prev => ({
          ...prev,
          matched: (prev.matched || 0) + 1,
          updated: (prev.updated || 0) + 1,
        }));
      } else if (action === 'create_new') {
        setAlumniImportResult(prev => ({
          ...prev,
          created: (prev.created || 0) + 1,
        }));
      }
    } catch (err) {
      console.error('Error resolving review:', err);
      setError('Failed to resolve: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReviewResolutionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Bulk resolve all review items
  const resolveAllReviews = async (action) => {
    if (!alumniImportResult?.review_needed?.length) return;

    const unresolved = alumniImportResult.review_needed.filter(r => {
      const key = `${r.row}-${r.potential_match.id}`;
      return !resolvedReviews[key];
    });

    if (!unresolved.length) return;

    const confirmMsg = action === 'accept'
      ? `Accept all ${unresolved.length} matches and add emails to existing records?`
      : `Skip all ${unresolved.length} matches without making changes?`;

    if (!window.confirm(confirmMsg)) return;

    for (const item of unresolved) {
      await resolveReviewItem(item, action);
    }
  };

  // Legacy contact import (keep for backwards compatibility)
  const [contactImportLoading, setContactImportLoading] = useState(false);
  const [contactImportResult, setContactImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState(null); // {progress, total, job_type}

  const handleContactCsvImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError(null);
    setContactImportLoading(true);
    setContactImportResult(null);
    setImportProgress(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('auto_match', 'true');
      const token = getValidToken();
      const res = await axios.post(`${API_URL}/api/exec/contacts/import/csv`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data.job_id) {
        // Background processing — poll for results
        setImportProgress({ progress: 0, total: 0, job_type: 'contact_csv' });
        const result = await pollImportJob(res.data.job_id, (job) => {
          setImportProgress({ progress: job.progress, total: job.total, job_type: 'contact_csv' });
        });
        setContactImportResult(result);
      } else {
        // Synchronous response (shouldn't happen with new backend, but handle gracefully)
        setContactImportResult(res.data);
      }
    } catch (err) {
      console.error('Error importing contact CSV:', err);
      setError('Contact CSV import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setContactImportLoading(false);
      setImportProgress(null);
      event.target.value = '';
    }
  };

  // ============================================
  // DE-DUPLICATION TOOL
  // ============================================
  const [dedupStats, setDedupStats] = useState(null);
  const [dedupStatsLoading, setDedupStatsLoading] = useState(false);
  const [dedupGroups, setDedupGroups] = useState([]);
  const [dedupGroupsLoading, setDedupGroupsLoading] = useState(false);
  const [dedupSearchType, setDedupSearchType] = useState('name'); // 'name', 'email', or 'all'
  const [dedupPage, setDedupPage] = useState(1);
  const [dedupTotal, setDedupTotal] = useState(0);
  const [dedupMergeLoading, setDedupMergeLoading] = useState({});
  const [showDedupSection, setShowDedupSection] = useState(false);
  const [autoMergeLoading, setAutoMergeLoading] = useState(false);
  const [autoMergePreview, setAutoMergePreview] = useState(null);
  const [autoMergeResult, setAutoMergeResult] = useState(null);
  const [showDedupInfoModal, setShowDedupInfoModal] = useState(false);

  // ============================================
  // UNPROVISIONED CONTACTS
  // ============================================
  const [showUnprovisionedSection, setShowUnprovisionedSection] = useState(false);
  const [unprovisionedData, setUnprovisionedData] = useState(null);
  const [unprovisionedLoading, setUnprovisionedLoading] = useState(false);
  const [unprovisionedPage, setUnprovisionedPage] = useState(1);
  const [unprovisionedPageSize, setUnprovisionedPageSize] = useState(50);
  const [unprovisionedJumpPage, setUnprovisionedJumpPage] = useState('');
  const [unprovisionedSort, setUnprovisionedSort] = useState({ field: 'full_name', dir: 'asc' });
  const [unprovisionedFilter, setUnprovisionedFilter] = useState('all');
  const [unprovisionedSearch, setUnprovisionedSearch] = useState('');
  const [unprovisionedUrlEdits, setUnprovisionedUrlEdits] = useState({});
  const [unprovisionedSaving, setUnprovisionedSaving] = useState({});
  const [unprovisionedProcessing, setUnprovisionedProcessing] = useState(false);
  const [unprovisionedBatchStatus, setUnprovisionedBatchStatus] = useState(null);
  const [unprovisionedCopied, setUnprovisionedCopied] = useState({});
  // Assignment state
  const [unprovisionedSelected, setUnprovisionedSelected] = useState(new Set());
  const [unprovisionedAssignModalOpen, setUnprovisionedAssignModalOpen] = useState(false);
  const [unprovisionedAssignableAdmins, setUnprovisionedAssignableAdmins] = useState([]);
  const [unprovisionedAssignTarget, setUnprovisionedAssignTarget] = useState('');
  const [unprovisionedAssignDueDate, setUnprovisionedAssignDueDate] = useState('');
  const [unprovisionedAssigning, setUnprovisionedAssigning] = useState(false);
  const [unprovisionedAssignedFilter, setUnprovisionedAssignedFilter] = useState('');
  const [unprovisionedResearchFilter, setUnprovisionedResearchFilter] = useState('');
  const [unprovisionedAssignmentStats, setUnprovisionedAssignmentStats] = useState(null);
  const [unprovisionedShowStats, setUnprovisionedShowStats] = useState(false);
  const [unprovisionedNotFoundModal, setUnprovisionedNotFoundModal] = useState(null);
  const [unprovisionedNotFoundNote, setUnprovisionedNotFoundNote] = useState('');

  const fetchUnprovisioned = async (page = 1, pageSizeOverride, sortOverride) => {
    setUnprovisionedLoading(true);
    try {
      const ps = pageSizeOverride !== undefined ? pageSizeOverride : unprovisionedPageSize;
      const sort = sortOverride !== undefined ? sortOverride : unprovisionedSort;
      const params = new URLSearchParams({ page, page_size: ps, sort_by: sort.field, sort_dir: sort.dir });
      if (unprovisionedFilter === 'with_url') params.set('has_linkedin_url', 'true');
      if (unprovisionedFilter === 'without_url') params.set('has_linkedin_url', 'false');
      if (unprovisionedSearch) params.set('search', unprovisionedSearch);
      if (unprovisionedAssignedFilter) params.set('assigned_to', unprovisionedAssignedFilter);
      if (unprovisionedResearchFilter) params.set('research_status', unprovisionedResearchFilter);
      const res = await axios.get(`${API_URL}/api/exec/contacts/unprovisioned?${params}`, authHeaders());
      setUnprovisionedData(res.data);
      setUnprovisionedPage(page);
      setUnprovisionedSelected(new Set());
    } catch (err) {
      console.error('Error fetching unprovisioned contacts:', err);
      setError('Failed to load unprovisioned contacts');
    } finally {
      setUnprovisionedLoading(false);
    }
  };

  const handleSaveLinkedinUrl = async (contactId) => {
    const url = unprovisionedUrlEdits[contactId];
    if (!url?.trim()) return;
    setUnprovisionedSaving(prev => ({ ...prev, [contactId]: true }));
    try {
      await axios.patch(`${API_URL}/api/exec/contacts/${contactId}/linkedin-url`, { linkedin_url: url }, authHeaders());
      setUnprovisionedUrlEdits(prev => { const next = { ...prev }; delete next[contactId]; return next; });
      fetchUnprovisioned(unprovisionedPage);
    } catch (err) {
      setError(`Failed to save URL: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUnprovisionedSaving(prev => ({ ...prev, [contactId]: false }));
    }
  };

  const handleCopySearchText = (contact) => {
    const text = `${contact.first_name || ''} ${contact.last_name || ''} ${contact.university || ''}`.trim();
    navigator.clipboard.writeText(text);
    setUnprovisionedCopied(prev => ({ ...prev, [contact.id]: true }));
    setTimeout(() => setUnprovisionedCopied(prev => ({ ...prev, [contact.id]: false })), 2000);
  };

  const handleProcessUnprovisioned = async (mode, singleContactId = null) => {
    setUnprovisionedProcessing(true);
    setUnprovisionedBatchStatus(null);
    try {
      const body = mode === 'all_with_linkedin'
        ? { all_with_linkedin: true, auto_score: true }
        : { contact_ids: singleContactId ? [singleContactId] : [], auto_score: true };
      const res = await axios.post(`${API_URL}/api/exec/contacts/unprovisioned/process`, body, authHeaders());
      setUnprovisionedBatchStatus(res.data);
      pollUnprovisionedBatch(res.data.batch_id);
    } catch (err) {
      setError(`Processing failed: ${err.response?.data?.detail || err.message}`);
      setUnprovisionedProcessing(false);
    }
  };

  const pollUnprovisionedBatch = (batchId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/exec/import-batches/${batchId}`, authHeaders());
        setUnprovisionedBatchStatus(prev => ({ ...prev, ...res.data }));
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(interval);
          setUnprovisionedProcessing(false);
          fetchUnprovisioned(1);
        }
      } catch (err) {
        console.error('Error polling batch:', err);
        clearInterval(interval);
        setUnprovisionedProcessing(false);
      }
    }, 5000);
  };

  const fetchAssignableAdmins = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/contacts/assignable-admins`, authHeaders());
      setUnprovisionedAssignableAdmins(res.data);
    } catch (err) {
      console.error('Error fetching assignable admins:', err);
    }
  };

  const handleOpenAssignModal = () => {
    if (unprovisionedSelected.size === 0) return;
    fetchAssignableAdmins();
    setUnprovisionedAssignTarget('');
    setUnprovisionedAssignDueDate('');
    setUnprovisionedAssignModalOpen(true);
  };

  const handleAssignContacts = async () => {
    if (!unprovisionedAssignTarget) return;
    setUnprovisionedAssigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/exec/contacts/assign`, {
        contact_ids: Array.from(unprovisionedSelected),
        assigned_to_id: parseInt(unprovisionedAssignTarget),
        due_date: unprovisionedAssignDueDate || null,
      }, authHeaders());
      setUnprovisionedAssignModalOpen(false);
      setUnprovisionedSelected(new Set());
      fetchUnprovisioned(unprovisionedPage);
    } catch (err) {
      setError(`Assignment failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUnprovisionedAssigning(false);
    }
  };

  // --- Search outreach assignment ---
  const handleOpenSearchAssignModal = () => {
    if (searchSelected.size === 0) return;
    fetchAssignableAdmins();
    setSearchAssignForm({ assigned_to_id: '', goal: 'engagement', priority: 'normal', due_date: '', purpose: '' });
    setShowSearchAssignModal(true);
  };

  const handleSearchAssignOutreach = async () => {
    if (!searchAssignForm.assigned_to_id) return;
    setSearchAssigning(true);
    try {
      const assignments = Array.from(searchSelected).map(contactId => ({
        exec_contact_id: contactId,
        assigned_to_id: parseInt(searchAssignForm.assigned_to_id),
        goal: searchAssignForm.goal || 'engagement',
        priority: searchAssignForm.priority || 'normal',
        due_date: searchAssignForm.due_date || null,
        purpose: searchAssignForm.purpose || null,
      }));
      await axios.post(`${API_URL}/api/exec/outreach/assign`, { assignments }, authHeaders());
      setShowSearchAssignModal(false);
      setSearchSelected(new Set());
      setSuccessMsg(`Outreach assigned for ${assignments.length} contact(s)`);
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      setError(`Assignment failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSearchAssigning(false);
    }
  };

  const handleMarkNotFound = async (contactId, note) => {
    try {
      await axios.post(`${API_URL}/api/exec/contacts/${contactId}/mark-not-found`, { note }, authHeaders());
      setUnprovisionedNotFoundModal(null);
      setUnprovisionedNotFoundNote('');
      fetchUnprovisioned(unprovisionedPage);
    } catch (err) {
      setError(`Failed to mark as not found: ${err.response?.data?.detail || err.message}`);
    }
  };

  const fetchAssignmentStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/contacts/assignment-stats`, authHeaders());
      setUnprovisionedAssignmentStats(res.data);
    } catch (err) {
      console.error('Error fetching assignment stats:', err);
    }
  };

  const toggleUnprovisionedSelect = (id) => {
    setUnprovisionedSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllUnprovisioned = () => {
    if (!unprovisionedData?.items) return;
    const allIds = unprovisionedData.items.map(c => c.id);
    const allSelected = allIds.every(id => unprovisionedSelected.has(id));
    if (allSelected) {
      setUnprovisionedSelected(new Set());
    } else {
      setUnprovisionedSelected(new Set(allIds));
    }
  };

  const fetchDedupStats = async () => {
    setDedupStatsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/contacts/duplicates/stats`, authHeaders());
      setDedupStats(res.data);
    } catch (err) {
      console.error('Error fetching dedup stats:', err);
    } finally {
      setDedupStatsLoading(false);
    }
  };

  const fetchDedupGroups = async (page = 1, type = dedupSearchType) => {
    setDedupGroupsLoading(true);
    try {
      const params = new URLSearchParams({
        match_type: type,
        page: page.toString(),
        per_page: '20',
      }).toString();
      const res = await axios.get(`${API_URL}/api/exec/contacts/duplicates?${params}`, authHeaders());
      setDedupGroups(res.data.groups || []);
      setDedupTotal(res.data.total_groups || 0);
      setDedupPage(page);
    } catch (err) {
      console.error('Error fetching duplicates:', err);
      setError('Failed to fetch duplicates: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDedupGroupsLoading(false);
    }
  };

  const handleDedupSearchTypeChange = (type) => {
    setDedupSearchType(type);
    fetchDedupGroups(1, type);
  };

  const handleMergeDuplicates = async (primaryId, duplicateIds) => {
    const key = `${primaryId}-${duplicateIds.join(',')}`;
    setDedupMergeLoading(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API_URL}/api/exec/contacts/duplicates/merge`, {
        primary_id: primaryId,
        duplicate_ids: duplicateIds,
      }, authHeaders());

      // Remove merged group from list
      setDedupGroups(prev => {
        const updated = prev.filter(g =>
          !(g.records.some(c => c.id === primaryId) &&
            g.records.some(c => duplicateIds.includes(c.id)))
        );
        // If page is now empty but there are more pages, auto-fetch
        if (updated.length === 0 && dedupTotal > 20) {
          const nextPage = dedupPage > 1 ? dedupPage : dedupPage + 1;
          setTimeout(() => fetchDedupGroups(nextPage > Math.ceil((dedupTotal - 1) / 20) ? Math.max(1, nextPage - 1) : nextPage, dedupSearchType), 100);
        }
        return updated;
      });
      setDedupTotal(prev => Math.max(0, prev - 1));

      // Refresh stats
      fetchDedupStats();
    } catch (err) {
      console.error('Error merging duplicates:', err);
      setError('Merge failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDedupMergeLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleNotDuplicate = async (ids) => {
    const key = ids.join(',');
    setDedupMergeLoading(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API_URL}/api/exec/contacts/duplicates/not-duplicate`, {
        contact_ids: ids,
      }, authHeaders());

      // Remove group from list
      setDedupGroups(prev => {
        const updated = prev.filter(g =>
          !g.records.every(c => ids.includes(c.id))
        );
        // If page is now empty but there are more pages, auto-fetch
        if (updated.length === 0 && dedupTotal > 20) {
          const nextPage = dedupPage > 1 ? dedupPage : dedupPage + 1;
          setTimeout(() => fetchDedupGroups(nextPage > Math.ceil((dedupTotal - 1) / 20) ? Math.max(1, nextPage - 1) : nextPage, dedupSearchType), 100);
        }
        return updated;
      });
      setDedupTotal(prev => Math.max(0, prev - 1));

      // Refresh stats
      fetchDedupStats();
    } catch (err) {
      console.error('Error marking not duplicate:', err);
      setError('Failed to mark as not duplicate: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDedupMergeLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAutoMergePreview = async () => {
    setAutoMergeLoading(true);
    setAutoMergeResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/exec/contacts/duplicates/auto-merge`, {
        match_type: dedupSearchType,
        dry_run: true,
        max_groups: 10000,
      }, authHeaders());
      setAutoMergePreview(res.data);
    } catch (err) {
      console.error('Error previewing auto-merge:', err);
      setError('Auto-merge preview failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAutoMergeLoading(false);
    }
  };

  const handleAutoMergeExecute = async () => {
    setAutoMergeLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/exec/contacts/duplicates/auto-merge`, {
        match_type: dedupSearchType,
        dry_run: false,
        max_groups: 10000,
      }, authHeaders());
      setAutoMergeResult(res.data);
      setAutoMergePreview(null);
      // Refresh everything
      fetchDedupStats();
      fetchDedupGroups(1, dedupSearchType);
    } catch (err) {
      console.error('Error executing auto-merge:', err);
      setError('Auto-merge failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAutoMergeLoading(false);
    }
  };

  // ============================================
  // CONTACT RE-MATCHING
  // ============================================
  const [rematchLoading, setRematchLoading] = useState(false);
  const [rematchResult, setRematchResult] = useState(null);

  const handleRematchContacts = async () => {
    setError(null);
    setRematchLoading(true);
    setRematchResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/exec/contacts/match/rematch`, {}, { ...authHeaders(), timeout: 300000 });
      setRematchResult(res.data);
    } catch (err) {
      console.error('Error re-matching contacts:', err);
      setError('Re-match failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRematchLoading(false);
    }
  };

  // ============================================
  // REVIEWS TAB
  // ============================================
  const fetchReviews = async (page = 1) => {
    setReviewsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page, page_size: 20,
        status: reviewStatusFilter,
      });
      const res = await axios.get(`${API_URL}/api/exec/reviews?${params}`, authHeaders());
      setReviews(res.data.reviews || []);
      setReviewTotal(res.data.total || 0);
      setReviewPage(page);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load review queue');
    } finally {
      setReviewsLoading(false);
    }
  };

  const resolveReview = async (reviewId, action, selectedProfileId = null) => {
    try {
      await axios.post(`${API_URL}/api/exec/reviews/${reviewId}/resolve`, {
        action,
        selected_profile_id: selectedProfileId,
      }, authHeaders());
      fetchReviews(reviewPage);
    } catch (err) {
      console.error('Error resolving review:', err);
      setError('Failed to resolve review');
    }
  };

  // ============================================
  // SCORING TAB
  // ============================================
  const fetchScoringSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/admin/settings`, authHeaders());
      setScoringEnabled(res.data.executive_scoring_enabled || false);
    } catch (err) {
      console.error('Error fetching scoring settings:', err);
    }
  };

  const toggleScoringEnabled = async () => {
    setScoringEnabledLoading(true);
    try {
      await axios.put(`${API_URL}/api/exec/admin/settings`, {
        executive_scoring_enabled: !scoringEnabled,
      }, authHeaders());
      setScoringEnabled(!scoringEnabled);
    } catch (err) {
      console.error('Error toggling scoring:', err);
      setError('Failed to update scoring setting');
    } finally {
      setScoringEnabledLoading(false);
    }
  };

  const fetchScoringQueue = async () => {
    setScoringQueueLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: 1, page_size: 20 });
      const res = await axios.get(`${API_URL}/api/exec/scoring/queue?${params}`, authHeaders());
      setScoringQueue(res.data.queue || []);
      setScoringQueueTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching scoring queue:', err);
      setError('Failed to load scoring queue');
    } finally {
      setScoringQueueLoading(false);
    }
  };

  const triggerScoring = async (forceRescore = false) => {
    setScoringRunning(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/exec/scoring/trigger`, {
        force_rescore: forceRescore,
      }, authHeaders());
      setLastRun(res.data);
      alert(`Scoring run started: ${res.data.model_run_id?.substring(0, 8)}...`);
      fetchScoringQueue();
      // Refresh runs list after a short delay to show the new run
      setTimeout(() => fetchScoringRuns(), 2000);
    } catch (err) {
      console.error('Error triggering scoring:', err);
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      setError(`Failed to start scoring run: ${detail}`);
    } finally {
      setScoringRunning(false);
    }
  };

  const fetchScoringRuns = async () => {
    setScoringRunsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/scoring/runs?page=1&page_size=10`, authHeaders());
      setScoringRuns(res.data.items || []);
    } catch (err) {
      console.error('Error fetching scoring runs:', err);
    } finally {
      setScoringRunsLoading(false);
    }
  };

  // Poll scoring runs while any run is in progress
  useEffect(() => {
    if (activeTab !== 'scoring') return;
    const hasRunning = scoringRuns.some(run => run.status === 'running');
    if (!hasRunning) return;

    const interval = setInterval(() => {
      fetchScoringRuns();
      fetchScoringQueue();
    }, 5000);
    return () => clearInterval(interval);
  }, [scoringRuns, activeTab]);

  // ============================================
  // CLASSIFICATION (Role Level/Function Inference)
  // ============================================
  const fetchClassificationStats = async () => {
    setClassificationStatsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/classification/stats`, authHeaders());
      setClassificationStats(res.data);
    } catch (err) {
      console.error('Error fetching classification stats:', err);
    } finally {
      setClassificationStatsLoading(false);
    }
  };

  const runBatchClassification = async () => {
    setClassificationRunning(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/exec/classification/run`, {}, authHeaders());
      alert(`Classification complete!\n\nTotal Roles: ${res.data.total_roles}\nNewly Classified: ${res.data.newly_classified}\nAlready Classified: ${res.data.already_classified}\nUnclassified: ${res.data.unclassified}`);
      // Refresh stats after classification
      fetchClassificationStats();
      // Refresh dashboard data to show updated counts
      fetchDashboard();
    } catch (err) {
      console.error('Error running classification:', err);
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      setError(`Failed to run classification: ${detail}`);
    } finally {
      setClassificationRunning(false);
    }
  };

  // Fetch classification stats when dashboard tab is active
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchClassificationStats();
    }
  }, [activeTab]);

  // ============================================
  // REPORTS TAB
  // ============================================
  const REPORT_LIST = [
    { key: 'data-quality', name: 'Data Quality Dashboard', category: 'Day-1' },
    { key: 'talent-map', name: 'Executive Talent Map', category: 'Day-1' },
    { key: 'board-ready', name: 'Board-Ready Pool', category: 'Day-1' },
    { key: 'depth-chart', name: 'Functional Leadership Depth', category: 'Day-1' },
    { key: 'company-footprint', name: 'Company Footprint', category: 'Day-1' },
    { key: 'industry-concentration', name: 'Industry Concentration', category: 'Day-1' },
    { key: 'stale-profiles', name: 'Stale Profile Report', category: 'Day-1' },
    { key: 'on-the-move', name: '"On the Move" Alerts', category: 'Early Signal' },
    { key: 'visibility-spikes', name: 'Visibility Spike Detection', category: 'Early Signal' },
    { key: 'open-to-work', name: '"Open to Work" Tracking', category: 'Early Signal' },
    { key: 'velocity-leaderboard', name: 'Velocity Leaderboard', category: 'Medium-Term' },
    { key: 'trajectory-patterns', name: 'Trajectory Patterns', category: 'Medium-Term' },
    { key: 'succession-timing', name: 'Succession Timing', category: 'Medium-Term' },
    { key: 'career-outcomes', name: 'Alumni Career Outcomes', category: 'Analytics' },
    { key: 'function-migration', name: 'Function Migration', category: 'Analytics' },
    { key: 'geographic-flow', name: 'Geographic Flow', category: 'Analytics' },
    { key: 'warm-intros', name: 'Warm Introductions', category: 'Exec Search' },
    { key: 'comparable-pool', name: 'Comparable Candidate Pool', category: 'Exec Search' },
  ];

  const fetchReport = async (reportKey, params = {}) => {
    setReportLoading(true);
    setReportData(null);
    setError(null);
    try {
      const queryParams = new URLSearchParams(params);
      const url = `${API_URL}/api/exec/reports/${reportKey}${queryParams.toString() ? '?' + queryParams : ''}`;
      const res = await axios.get(url, authHeaders());
      setReportData(res.data);
      setSelectedReport(reportKey);
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  // ============================================
  // COMPANY ENRICHMENT (Apify Company Pages)
  // ============================================
  const fetchEnrichmentStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/companies/enrichment-stats`, authHeaders());
      setEnrichmentStats(res.data);
    } catch (err) {
      console.error('Error fetching enrichment stats:', err);
    }
  };

  const startEnrichment = async (limit = 50) => {
    setEnrichmentLoading(true);
    setEnrichmentResult(null);
    setEnrichmentProgress(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/exec/companies/enrich?limit=${limit}`,
        {},
        authHeaders()
      );
      if (res.data.job_id) {
        setEnrichmentJobId(res.data.job_id);
        // Poll for completion
        const result = await pollImportJob(res.data.job_id, (job) => {
          setEnrichmentProgress(job);
        });
        setEnrichmentResult(result);
        setEnrichmentJobId(null);
        setEnrichmentProgress(null);
        // Refresh stats
        fetchEnrichmentStats();
      } else {
        // No job created (all already enriched)
        setEnrichmentResult(res.data);
      }
    } catch (err) {
      console.error('Error starting enrichment:', err);
      setError('Company enrichment failed: ' + (err.response?.data?.detail || err.message));
      setEnrichmentJobId(null);
      setEnrichmentProgress(null);
    } finally {
      setEnrichmentLoading(false);
    }
  };

  const triggerBackfill = async () => {
    setEnrichmentLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/exec/companies/backfill-company-context`,
        {},
        authHeaders()
      );
      setEnrichmentResult(res.data);
      fetchEnrichmentStats();
    } catch (err) {
      console.error('Error running backfill:', err);
      setError('Backfill failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEnrichmentLoading(false);
    }
  };

  // ============================================
  // LINKEDIN COMMENTS IMPORT
  // ============================================

  const fetchCommentCostSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/comments/cost-summary`, authHeaders());
      setCommentCostSummary(res.data);
    } catch (err) {
      console.error('Error fetching comment cost summary:', err);
    }
  };

  const handleOpenCommentImportModal = () => {
    fetchCommentCostSummary();
    setCommentImportMaxPages(1);
    setCommentImportResult(null);
    setShowCommentImportModal(true);
  };

  const handleBulkCommentImport = async () => {
    if (searchSelected.size === 0) return;
    setCommentImporting(true);
    setCommentImportResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/exec/comments/import/bulk`,
        {
          exec_person_ids: Array.from(searchSelected),
          max_pages: commentImportMaxPages,
        },
        authHeaders()
      );
      if (res.data.job_id) {
        const result = await pollImportJob(res.data.job_id, () => {});
        setCommentImportResult({
          ...result,
          cost_usd: res.data.estimated_cost_usd,
          profiles_skipped_no_linkedin: res.data.profiles_skipped_no_linkedin,
        });
        fetchCommentCostSummary();
      }
    } catch (err) {
      console.error('Error in bulk comment import:', err);
      setError('Comment import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCommentImporting(false);
    }
  };

  const handleSingleCommentImport = async (execPersonId) => {
    setCommentSingleImporting(prev => ({ ...prev, [execPersonId]: true }));
    try {
      const res = await axios.post(
        `${API_URL}/api/exec/comments/import/single`,
        { exec_person_id: execPersonId, max_pages: 1 },
        authHeaders()
      );
      if (res.data.success) {
        setError(null);
        // Brief success indicator
        setCommentSingleImporting(prev => ({ ...prev, [execPersonId]: 'done' }));
        setTimeout(() => {
          setCommentSingleImporting(prev => ({ ...prev, [execPersonId]: false }));
        }, 2000);
      }
    } catch (err) {
      console.error('Error importing comments:', err);
      setError('Comment import failed: ' + (err.response?.data?.detail || err.message));
      setCommentSingleImporting(prev => ({ ...prev, [execPersonId]: false }));
    }
  };

  // ============================================
  // COMPANY INDUSTRIES
  // ============================================
  const fetchCompaniesMissingIndustries = async () => {
    setCompaniesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/companies/missing-industries?limit=200`, authHeaders());
      setCompaniesMissingIndustries(res.data.companies || []);
      setCompaniesDiagnostics(res.data.diagnostics || null);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to load companies');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchCompaniesWithIndustries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/companies/industries?limit=200`, authHeaders());
      setCompaniesWithIndustries(res.data.companies || []);
    } catch (err) {
      console.error('Error fetching companies with industries:', err);
    }
  };

  const clearCompanyIndustry = async (companyName) => {
    setIndustryUpdateLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/exec/companies/set-industry?company_name=${encodeURIComponent(companyName)}&industry=`,
        {},
        authHeaders()
      );
      // Refresh both lists
      fetchCompaniesMissingIndustries();
      fetchCompaniesWithIndustries();
      return true;
    } catch (err) {
      console.error('Error clearing industry:', err);
      setError('Failed to clear industry');
      return false;
    } finally {
      setIndustryUpdateLoading(false);
    }
  };

  const setCompanyIndustry = async (companyName, industry) => {
    setIndustryUpdateLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/exec/companies/set-industry?company_name=${encodeURIComponent(companyName)}&industry=${encodeURIComponent(industry)}`,
        {},
        authHeaders()
      );
      // Refresh both lists
      fetchCompaniesMissingIndustries();
      if (showCompaniesWithIndustries) {
        fetchCompaniesWithIndustries();
      }
      return true;
    } catch (err) {
      console.error('Error setting industry:', err);
      setError('Failed to set industry');
      return false;
    } finally {
      setIndustryUpdateLoading(false);
    }
  };

  // ============================================
  // PUBLIC COMPANY CLASSIFICATION
  // ============================================
  const fetchPublicCompanyStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/companies/public-company-stats`, authHeaders());
      setPublicCompanyStats(res.data);
    } catch (err) {
      console.error('Error fetching public company stats:', err);
    }
  };

  const importPublicCompanies = async (files) => {
    if (!files || files.length === 0) return;
    setPublicCompanyLoading(true);
    setPublicCompanyResult(null);
    try {
      const formData = new FormData();
      for (const file of files) {
        const name = file.name.toLowerCase();
        if (name.includes('nasdaqlisted') || name === 'nasdaqlisted.csv') {
          formData.append('nasdaq_file', file);
        } else if (name.includes('otherlisted') || name === 'otherlisted.csv') {
          formData.append('other_file', file);
        }
      }
      if (!formData.has('nasdaq_file') && !formData.has('other_file')) {
        setPublicCompanyResult({ error: 'No recognized CSV files. Please select nasdaqlisted.csv and/or otherlisted.csv.' });
        setPublicCompanyLoading(false);
        return;
      }
      const token = getValidToken();
      const res = await axios.post(`${API_URL}/api/exec/companies/upload-public-company-csvs`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPublicCompanyResult(res.data);
      fetchPublicCompanyStats();
    } catch (err) {
      console.error('Error uploading public company CSVs:', err);
      setPublicCompanyResult({ error: err.response?.data?.detail || err.message });
    } finally {
      setPublicCompanyLoading(false);
      if (publicCompanyFileRef.current) publicCompanyFileRef.current.value = '';
    }
  };

  const classifyPublicCompanies = async (dryRun = true) => {
    setPublicCompanyLoading(true);
    setPublicCompanyResult(null);
    if (!dryRun) setTier2ReviewList([]);
    try {
      const res = await axios.post(
        `${API_URL}/api/exec/companies/classify-public-companies?dry_run=${dryRun}&force=false`,
        {},
        authHeaders()
      );
      setPublicCompanyResult(res.data);
      // When classifying (not dry run), populate review list from tier2 matches
      if (!dryRun && res.data.all_tier2?.length > 0) {
        setTier2ReviewList(res.data.all_tier2.map(m => ({ ...m, approved: true })));
      }
      if (!dryRun) fetchPublicCompanyStats();
    } catch (err) {
      console.error('Error classifying public companies:', err);
      setPublicCompanyResult({ error: err.response?.data?.detail || err.message });
    } finally {
      setPublicCompanyLoading(false);
    }
  };

  const applyApprovedContainment = async () => {
    const approved = tier2ReviewList.filter(m => m.approved).map(m => m.role_name);
    if (approved.length === 0) return;
    setPublicCompanyLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/exec/companies/apply-approved-public`,
        { company_names: approved },
        authHeaders()
      );
      setPublicCompanyResult(prev => ({
        ...prev,
        containment_applied: res.data,
      }));
      setTier2ReviewList([]);
      fetchPublicCompanyStats();
    } catch (err) {
      console.error('Error applying approved matches:', err);
      setPublicCompanyResult(prev => ({
        ...prev,
        containment_error: err.response?.data?.detail || err.message,
      }));
    } finally {
      setPublicCompanyLoading(false);
    }
  };

  const COMMON_INDUSTRIES = [
    'Technology',
    'Financial Services',
    'Healthcare',
    'Manufacturing',
    'Semiconductor Manufacturing',
    'Chemicals',
    'Medical Manufacturing',
    'Beauty & Cosmetics',
    'Biotechnology',
    'Food Services',
    'Retail',
    'Energy',
    'Telecommunications',
    'Media & Entertainment',
    'Consulting',
    'Aerospace & Defense',
    'Automotive',
    'Consumer Goods',
    'Education',
    'Government',
    'Pharmaceuticals',
    'Real Estate',
    'Construction / Real Estate',
    'Transportation',
    'Utilities',
    'Non-Profit',
    'Hospitality',
    'Agriculture',
    'Other',
  ];

  const handleCsvImport = async () => {
    if (!csvImportFile) return;
    setCsvImportLoading(true);
    setCsvImportResults(null);
    try {
      const token = getValidToken();
      const formData = new FormData();
      formData.append('file', csvImportFile);
      const params = new URLSearchParams();
      params.set('skip_unknowns', 'true');
      if (csvForceOverwrite) params.set('force_overwrite', 'true');
      const resp = await fetch(`${API_URL}/api/exec/companies/import-industries-csv?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Import failed');
      }
      const data = await resp.json();
      setCsvImportResults(data);
      // Refresh diagnostics
      fetchCompaniesMissingIndustries();
      fetchCompaniesWithIndustries();
    } catch (err) {
      console.error('CSV import error:', err);
      setError(err.message || 'Failed to import CSV');
    } finally {
      setCsvImportLoading(false);
    }
  };

  // ============================================
  // ALUMNI SEARCH
  // ============================================
  const LEVEL_OPTIONS = [
    { value: 'c_suite', label: 'C-Suite' },
    { value: 'board', label: 'Board' },
    { value: 'evp', label: 'EVP' },
    { value: 'svp', label: 'SVP' },
    { value: 'vp', label: 'VP' },
    { value: 'senior_director', label: 'Senior Director' },
    { value: 'director', label: 'Director' },
    { value: 'senior_manager', label: 'Senior Manager' },
    { value: 'manager', label: 'Manager' },
  ];

  const fetchAlumniSearch = async (page = 1) => {
    setAlumniSearchLoading(true);
    setError(null);
    try {
      const body = { page, page_size: 50 };
      if (alumniSearchFilters.company.trim()) body.company = alumniSearchFilters.company.trim();
      if (alumniSearchFilters.industries.length) body.industries = alumniSearchFilters.industries;
      if (alumniSearchFilters.levels.length) body.levels = alumniSearchFilters.levels;
      body.current_roles_only = alumniSearchFilters.current_roles_only;
      if (alumniSearchFilters.search.trim()) body.search = alumniSearchFilters.search.trim();
      if (alumniSearchFilters.university.trim()) body.university = alumniSearchFilters.university.trim();
      if (alumniSearchFilters.university.trim()) body.university_search_all = alumniSearchFilters.university_search_all;
      body.sort_by = alumniSearchSort.key;
      body.sort_dir = alumniSearchSort.dir;
      const res = await axios.post(`${API_URL}/api/exec/reports/alumni-search`, body, authHeaders());
      setAlumniSearchData(res.data);
      setAlumniSearchPage(page);
    } catch (err) {
      console.error('Error in alumni search:', err);
      setError('Alumni search failed');
    } finally {
      setAlumniSearchLoading(false);
    }
  };

  const clearAlumniSearch = () => {
    setAlumniSearchFilters({ company: '', industries: [], levels: [], current_roles_only: false, search: '', university: '', university_search_all: false });
    setAlumniSearchData(null);
    setAlumniSearchPage(1);
  };

  const toggleAlumniIndustry = (ind) => {
    setAlumniSearchFilters(prev => ({
      ...prev,
      industries: prev.industries.includes(ind)
        ? prev.industries.filter(i => i !== ind)
        : [...prev.industries, ind],
    }));
  };

  const toggleAlumniLevel = (lv) => {
    setAlumniSearchFilters(prev => ({
      ...prev,
      levels: prev.levels.includes(lv)
        ? prev.levels.filter(l => l !== lv)
        : [...prev.levels, lv],
    }));
  };

  // ============================================
  // ANALYTICS TAB
  // ============================================
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ days: trendDays });
      const res = await axios.get(`${API_URL}/api/exec/analytics/trends?${params}`, authHeaders());
      setAnalyticsData(res.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const triggerAnalyticsCompute = async () => {
    setError(null);
    try {
      await axios.post(`${API_URL}/api/exec/analytics/compute`, {}, authHeaders());
      alert('Analytics computation triggered successfully');
      fetchAnalytics();
    } catch (err) {
      console.error('Error computing analytics:', err);
      setError('Failed to trigger analytics computation');
    }
  };

  // ============================================
  // CALIBRATION TAB
  // ============================================
  const fetchCalibrationRuns = async () => {
    setCalibrationRunsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/calibration/history?limit=20`, authHeaders());
      setCalibrationRuns(res.data.runs || []);
    } catch (err) {
      console.error('Error fetching calibration runs:', err);
    } finally {
      setCalibrationRunsLoading(false);
    }
  };

  const fetchCalibrationAlerts = async () => {
    setCalibrationAlertsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/calibration/alerts?status=open&limit=50`, authHeaders());
      setCalibrationAlerts(res.data.alerts || []);
    } catch (err) {
      console.error('Error fetching calibration alerts:', err);
    } finally {
      setCalibrationAlertsLoading(false);
    }
  };

  const fetchCalibrationAlertSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/calibration/alerts/summary`, authHeaders());
      setCalibrationAlertSummary(res.data);
    } catch (err) {
      console.error('Error fetching alert summary:', err);
    }
  };

  const triggerCalibration = async (testType = 'full') => {
    setCalibrationTriggerLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/exec/calibration/trigger`, { test_type: testType }, authHeaders());
      alert(`Calibration ${testType} completed. ${res.data.warnings?.length || 0} warnings.`);
      fetchCalibrationRuns();
      fetchCalibrationAlerts();
      fetchCalibrationAlertSummary();
    } catch (err) {
      console.error('Error triggering calibration:', err);
      setError('Failed to trigger calibration');
    } finally {
      setCalibrationTriggerLoading(false);
    }
  };

  const resolveAlert = async (alertId, notes = '') => {
    try {
      await axios.put(`${API_URL}/api/exec/calibration/alerts/${alertId}/resolve`, { resolution_notes: notes }, authHeaders());
      fetchCalibrationAlerts();
      fetchCalibrationAlertSummary();
    } catch (err) {
      console.error('Error resolving alert:', err);
      alert('Failed to resolve alert');
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/exec/calibration/alerts/${alertId}/acknowledge`, {}, authHeaders());
      fetchCalibrationAlerts();
      fetchCalibrationAlertSummary();
    } catch (err) {
      console.error('Error acknowledging alert:', err);
      alert('Failed to acknowledge alert');
    }
  };

  // ============================================
  // REFERENCE SCORES
  // ============================================
  const fetchReferenceScores = async () => {
    setReferenceScoresLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/calibration/reference-scores`, authHeaders());
      setReferenceScores(res.data.reference_scores || []);
    } catch (err) {
      console.error('Error fetching reference scores:', err);
    } finally {
      setReferenceScoresLoading(false);
    }
  };

  const fetchReferenceStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exec/calibration/reference-scores/stats`, authHeaders());
      setReferenceStats(res.data);
    } catch (err) {
      console.error('Error fetching reference stats:', err);
    }
  };

  const searchProfilesForReference = async (query) => {
    if (!query || query.length < 2) {
      setProfileSearchResults([]);
      return;
    }
    setProfileSearchLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/exec/profiles/search`, { query, limit: 10 }, authHeaders());
      setProfileSearchResults(res.data.items || []);
    } catch (err) {
      console.error('Error searching profiles:', err);
    } finally {
      setProfileSearchLoading(false);
    }
  };

  const addReferenceScore = async () => {
    if (!referenceFormData.profile_id || !referenceFormData.human_score) {
      alert('Please select a profile and enter a score');
      return;
    }
    setReferenceFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/exec/calibration/reference-scores`, {
        profile_id: referenceFormData.profile_id,
        human_score: parseFloat(referenceFormData.human_score),
        rationale: referenceFormData.rationale || null,
        trajectory_score: referenceFormData.trajectory_score ? parseFloat(referenceFormData.trajectory_score) : null,
        velocity_score: referenceFormData.velocity_score ? parseFloat(referenceFormData.velocity_score) : null,
        scope_score: referenceFormData.scope_score ? parseFloat(referenceFormData.scope_score) : null,
        recency_score: referenceFormData.recency_score ? parseFloat(referenceFormData.recency_score) : null,
      }, authHeaders());
      setShowAddReferenceModal(false);
      setReferenceFormData({ profile_id: '', human_score: '', rationale: '', trajectory_score: '', velocity_score: '', scope_score: '', recency_score: '' });
      setProfileSearchQuery('');
      setProfileSearchResults([]);
      fetchReferenceScores();
      fetchReferenceStats();
    } catch (err) {
      console.error('Error adding reference score:', err);
      alert(err.response?.data?.detail || 'Failed to add reference score');
    } finally {
      setReferenceFormLoading(false);
    }
  };

  const deleteReferenceScore = async (refId) => {
    if (!window.confirm('Remove this reference score?')) return;
    try {
      await axios.delete(`${API_URL}/api/exec/calibration/reference-scores/${refId}`, authHeaders());
      fetchReferenceScores();
      fetchReferenceStats();
    } catch (err) {
      console.error('Error deleting reference score:', err);
      alert('Failed to remove reference score');
    }
  };

  const runReferenceComparison = async () => {
    setCalibrationTriggerLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/exec/calibration/reference-comparison`, {}, authHeaders());
      alert(`Reference comparison complete. MAE: ${res.data.results?.mae?.toFixed(2) || 'N/A'}, Band Agreement: ${res.data.results?.band_agreement_percent?.toFixed(1) || 'N/A'}%`);
      fetchCalibrationRuns();
      fetchCalibrationAlerts();
      fetchCalibrationAlertSummary();
    } catch (err) {
      console.error('Error running reference comparison:', err);
      alert('Failed to run reference comparison');
    } finally {
      setCalibrationTriggerLoading(false);
    }
  };

  // ============================================
  // BROKERAGE QUEUE FUNCTIONS
  // ============================================
  const fetchBrokerageQueue = async () => {
    setBrokerageQueueLoading(true);
    try {
      const params = new URLSearchParams();
      if (queueFilters.status.length) {
        queueFilters.status.forEach(s => params.append('status', s));
      }
      if (queueFilters.request_type.length) {
        queueFilters.request_type.forEach(t => params.append('request_type', t));
      }
      if (queueFilters.assigned_to !== null) {
        params.append('assigned_to', queueFilters.assigned_to);
      }
      params.append('sort_by', queueFilters.sort_by);
      params.append('sort_dir', queueFilters.sort_dir);
      params.append('page_size', '200');

      const res = await axios.get(
        `${API_URL}/api/staff/brokerage/queue?${params.toString()}`,
        authHeaders()
      );
      setBrokerageQueue(res.data.items || []);
    } catch (err) {
      console.error('Error fetching brokerage queue:', err);
      // Don't show error if it's just that the feature is disabled
      if (err.response?.status !== 503) {
        setError('Failed to load brokerage queue: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setBrokerageQueueLoading(false);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/admin-users`, authHeaders());
      const active = (res.data || []).filter(u => u.is_active);
      setStaffMembers(active);
    } catch (err) {
      console.error('Failed to fetch staff members:', err);
    }
  };

  // Fetch tiers for quick upgrade dropdown
  const fetchQueueTiers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/membership/tiers`, authHeaders());
      setQueueTiers((res.data || []).filter(t => t.is_active && t.tier_category === 'individual'));
    } catch (err) {
      console.error('Failed to fetch tiers for queue:', err);
    }
  };

  // Quick tier upgrade from queue row
  const handleQuickUpgrade = async (alumniUserId, tierSlug, requesterName) => {
    if (!alumniUserId || !tierSlug) return;
    setUpgradeLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/membership/grant`,
        { alumni_user_id: alumniUserId, tier_slug: tierSlug, status: 'comped' },
        authHeaders()
      );
      setUpgradingRequestId(null);
      setUpgradeTierSlug('');
      // Refresh queue to show updated tier badge
      fetchBrokerageQueue();
    } catch (err) {
      console.error('Error upgrading tier:', err);
      alert('Failed to upgrade tier: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Refresh display name for an ExecPerson (fixes "Test User" bug)
  const refreshRequesterDisplayName = async (execPersonId) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/staff/brokerage/executives/${execPersonId}/refresh-display-name`,
        {},
        authHeaders()
      );
      if (res.data.changed) {
        // Refresh the queue to show updated name
        fetchBrokerageQueue();
        alert(`Display name updated: "${res.data.old_display_name}" → "${res.data.new_display_name}"`);
      } else {
        alert(`Display name is already correct: "${res.data.new_display_name}"`);
      }
    } catch (err) {
      console.error('Error refreshing display name:', err);
      alert('Failed to refresh display name: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Open the request detail modal
  const openRequestDetail = (requestId) => {
    setSelectedRequestId(requestId);
    setShowRequestDetail(true);
  };

  // Fetch brokerage queue, staff members, and tiers when tab becomes active
  useEffect(() => {
    if (activeTab === 'brokerage') {
      fetchStaffMembers();
      fetchQueueTiers();
      if (brokerageView === 'queue') {
        fetchBrokerageQueue();
      }
    }
  }, [activeTab, brokerageView]);

  // Re-fetch queue when filters change
  useEffect(() => {
    if (activeTab === 'brokerage' && brokerageView === 'queue') {
      fetchBrokerageQueue();
    }
  }, [queueFilters]);

  // ============================================
  // TAB DEFINITIONS
  // ============================================
  const TABS = [
    { key: 'dashboard', label: 'Dashboard', icon: '◈', permKey: 'hide_exec_dashboard' },
    { key: 'search', label: 'Search', icon: '⌕', permKey: 'hide_exec_search' },
    { key: 'map', label: 'Map', icon: '◎', permKey: 'hide_exec_map' },
    { key: 'favorites', label: 'Favorites', icon: '♥', permKey: 'hide_exec_favorites' },
    { key: 'imports', label: 'Imports', icon: '↓', permKey: 'hide_exec_imports' },
    { key: 'reviews', label: 'Reviews', icon: '✓', permKey: 'hide_exec_reviews' },
    { key: 'scoring', label: 'Scoring', icon: '★', permKey: 'hide_exec_scoring' },
    { key: 'calibration', label: 'Calibration', icon: '⚖', permKey: 'hide_exec_calibration' },
    { key: 'reports', label: 'Reports', icon: '◰', permKey: 'hide_exec_reports' },
    { key: 'analytics', label: 'Analytics', icon: '◳', permKey: 'hide_exec_analytics' },
    { key: 'brokerage', label: 'Brokerage', icon: '⇆', permKey: 'hide_exec_brokerage' },
  ];

  // Filter tabs based on admin permissions (super admins see all)
  const visibleTabs = TABS.filter(tab => {
    if (isSuperAdmin) return true;
    if (!adminPermissions) return true;
    return !adminPermissions[tab.permKey];
  });

  // ============================================
  // RENDER HELPERS
  // ============================================

  const StatCard = ({ label, value, sublabel, color = 'blue' }) => (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm`}>
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className={`text-2xl font-bold text-${color}-600 mt-1`}>{value ?? '—'}</div>
      {sublabel && <div className="text-xs text-gray-400 mt-1">{sublabel}</div>}
    </div>
  );

  const LoadingSpinner = ({ message = 'Loading...' }) => (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3"></div>
      <span className="text-gray-500">{message}</span>
    </div>
  );

  const ImportProgressBar = () => {
    if (!importProgress) return null;
    const pct = importProgress.total > 0
      ? Math.round((importProgress.progress / importProgress.total) * 100)
      : 0;
    const label = {
      contact_csv: 'Contact CSV',
      alumni_csv: 'Alumni Database',
      linkedin_followers: 'LinkedIn Followers',
    }[importProgress.job_type] || 'Import';
    return (
      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-blue-700">
            Processing {label}...
          </span>
          <span className="text-sm text-blue-600">
            {importProgress.total > 0
              ? `${importProgress.progress} / ${importProgress.total} rows (${pct}%)`
              : 'Starting...'}
          </span>
        </div>
        <div className="w-full bg-blue-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.max(pct, 2)}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const ScoreBadge = ({ band }) => {
    const colors = {
      emerging: 'bg-gray-100 text-gray-700',
      developing: 'bg-blue-100 text-blue-700',
      established: 'bg-green-100 text-green-700',
      senior: 'bg-purple-100 text-purple-700',
      executive: 'bg-amber-100 text-amber-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[band] || 'bg-gray-100 text-gray-600'}`}>
        {band || 'unscored'}
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
        {status}
      </span>
    );
  };

  // ============================================
  // DASHBOARD TAB RENDER
  // ============================================
  const renderDashboard = () => {
    if (dashboardLoading) return <LoadingSpinner message="Loading dashboard..." />;
    if (!dashboardData) return <div className="text-center text-gray-500 py-8">No dashboard data available</div>;

    const d = dashboardData;
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard label="Total Profiles" value={d.total_profiles} color="indigo" />
          <StatCard label="Total Persons" value={d.total_persons} color="blue" />
          <StatCard label="Scored" value={d.scored_profiles} color="green" />
          <StatCard label="Pending Reviews" value={d.pending_reviews} color="yellow" />
          <StatCard label="Total Contacts" value={d.total_contacts} color="purple" />
          <StatCard label="Matched Contacts" value={d.matched_contacts} color="teal" />
        </div>

        {/* Unmatched Profiles */}
        {d.total_profiles > 0 && d.matched_contacts < d.total_profiles && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">
                Unmatched Profiles
                <span className="ml-2 text-xs font-normal text-red-500">
                  {d.total_profiles - d.matched_contacts} of {d.total_profiles} profiles have no matched contact
                </span>
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={bulkLinkProfilesToContacts}
                  disabled={bulkLinkLoading}
                  className={`px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1 ${bulkLinkLoading ? 'bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'}`}
                  title="Match profiles to contacts by LinkedIn URL"
                >
                  {bulkLinkLoading ? (
                    <>
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Linking...
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Link to Contacts
                    </>
                  )}
                </button>
                {!unmatchedProfiles && (
                  <button
                    onClick={fetchUnmatchedProfiles}
                    disabled={unmatchedLoading}
                    className={`px-3 py-1.5 text-xs text-white rounded-lg ${unmatchedLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
                  >
                    {unmatchedLoading ? 'Loading...' : 'View Details'}
                  </button>
                )}
                {unmatchedProfiles && (
                  <button
                    onClick={() => setUnmatchedProfiles(null)}
                    className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Hide
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-3">
              These profiles were imported from LinkedIn JSON but have no matching contact in the CSV data.
              This usually means the person was not in the CSV, or their name/LinkedIn URL differs between the two sources.
              Click <strong>"Link to Contacts"</strong> to automatically match profiles to contacts by LinkedIn URL.
            </p>

            {/* Bulk Link Result */}
            {bulkLinkResult && (
              <div className={`mb-3 p-3 rounded-lg border ${
                bulkLinkResult.stats?.contacts_linked > 0
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="text-sm font-medium mb-2">
                  {bulkLinkResult.stats?.contacts_linked > 0 ? (
                    <span className="text-green-700">
                      ✓ Linked {bulkLinkResult.stats.contacts_linked} contacts to {bulkLinkResult.stats.profiles_with_new_links} profiles
                    </span>
                  ) : (
                    <span className="text-gray-600">No new links found — profiles and contacts may already be linked</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-gray-600">
                    <span className="font-medium">Profiles Checked:</span> {bulkLinkResult.stats?.profiles_checked || 0}
                  </div>
                  <div className="text-purple-600">
                    <span className="font-medium">Profiles Linked:</span> {bulkLinkResult.stats?.profiles_with_new_links || 0}
                  </div>
                  <div className="text-green-600">
                    <span className="font-medium">Contacts Linked:</span> {bulkLinkResult.stats?.contacts_linked || 0}
                  </div>
                </div>
                <button
                  onClick={() => setBulkLinkResult(null)}
                  className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  Dismiss
                </button>
              </div>
            )}

            {unmatchedLoading && <LoadingSpinner message="Analyzing unmatched profiles..." />}
            {unmatchedProfiles && !unmatchedLoading && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {unmatchedProfiles.profiles.map((p, idx) => (
                  <div key={p.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-800">{p.full_name}</div>
                        {p.current_title && p.current_company && (
                          <div className="text-gray-500 mt-0.5">{p.current_title} at {p.current_company}</div>
                        )}
                        {p.linkedin_url && (
                          <div className="text-indigo-500 mt-0.5 truncate">
                            <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              {p.linkedin_url}
                            </a>
                          </div>
                        )}
                      </div>
                      <span className="text-gray-400 font-mono shrink-0">#{idx + 1}</span>
                    </div>
                    <div className="mt-2 p-2 bg-white rounded border border-gray-100">
                      <span className="font-medium text-gray-600">Diagnosis: </span>
                      <span className="text-gray-700">{p.diagnosis}</span>
                      {p.closest_contact && (
                        <div className="mt-1 text-gray-500">
                          Closest contact: <span className="font-medium">{p.closest_contact.name}</span>
                          {p.closest_contact.linkedin_url && (
                            <span className="ml-1">({p.closest_contact.linkedin_url})</span>
                          )}
                          <span className="ml-1 text-gray-400">— status: {p.closest_contact.status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {unmatchedProfiles.profiles.length === 0 && (
                  <div className="text-center text-gray-400 py-4">All profiles have matched contacts.</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recent Batches */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Recent Import Batches</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Batch ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Items</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Successful</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.recent_batches || []).map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{b.batch_id?.substring(0, 8)}...</td>
                    <td className="px-4 py-2"><StatusBadge status={b.status} /></td>
                    <td className="px-4 py-2">{b.total_items}</td>
                    <td className="px-4 py-2">{b.successful_count}</td>
                    <td className="px-4 py-2 text-gray-500">{new Date(b.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {(!d.recent_batches || d.recent_batches.length === 0) && (
                  <tr><td colSpan="5" className="px-4 py-4 text-center text-gray-400">No recent batches</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => changeTab('imports')}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
            >
              Import Profiles
            </button>
            <button
              onClick={() => triggerScoring()}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
              disabled={scoringRunning}
            >
              {scoringRunning ? 'Starting...' : 'Run Scoring'}
            </button>
            <button
              onClick={() => changeTab('reports')}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
            >
              View Reports
            </button>
            <button
              onClick={() => triggerAnalyticsCompute()}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
            >
              Compute Analytics
            </button>
          </div>
        </div>

        {/* Role Classification Section */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Role Classification</h3>
              <p className="text-xs text-gray-500 mt-1">
                Automatically infer seniority level, function, and category from job titles
              </p>
            </div>
            <button
              onClick={runBatchClassification}
              disabled={classificationRunning}
              className={`px-4 py-2 text-sm rounded-lg ${
                classificationRunning
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {classificationRunning ? 'Running...' : 'Run Classification'}
            </button>
          </div>

          {classificationStatsLoading ? (
            <div className="text-center text-gray-400 py-4">Loading classification stats...</div>
          ) : classificationStats ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-800">{classificationStats.total_roles}</div>
                  <div className="text-xs text-gray-500">Total Roles</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{classificationStats.classified_roles}</div>
                  <div className="text-xs text-green-600">Classified</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-700">{classificationStats.unclassified_roles}</div>
                  <div className="text-xs text-yellow-600">Unclassified</div>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-indigo-700">
                    {classificationStats.classification_rate || 0}%
                  </div>
                  <div className="text-xs text-indigo-600">Coverage</div>
                </div>
              </div>

              {/* Level Distribution */}
              {classificationStats.level_distribution && Object.keys(classificationStats.level_distribution).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">Level Distribution <span className="font-normal text-gray-400">(count of titles, unscored)</span></h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(classificationStats.level_distribution)
                      .sort((a, b) => b[1] - a[1])
                      .map(([level, count]) => (
                        <span
                          key={level}
                          className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs"
                        >
                          {level}: {count}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Function Distribution */}
              {classificationStats.function_distribution && Object.keys(classificationStats.function_distribution).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">Function Distribution</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(classificationStats.function_distribution)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 12)
                      .map(([func, count]) => (
                        <span
                          key={func}
                          className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs"
                        >
                          {func}: {count}
                        </span>
                      ))}
                    {Object.keys(classificationStats.function_distribution).length > 12 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        +{Object.keys(classificationStats.function_distribution).length - 12} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Category Distribution */}
              {classificationStats.category_distribution && Object.keys(classificationStats.category_distribution).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">Category Distribution</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(classificationStats.category_distribution)
                      .sort((a, b) => b[1] - a[1])
                      .map(([cat, count]) => (
                        <span
                          key={cat}
                          className="px-2 py-1 bg-teal-100 text-teal-800 rounded text-xs"
                        >
                          {cat}: {count}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-4">No classification data available</div>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // SEARCH TAB RENDER
  // ============================================
  const renderSearch = () => {
    const levelOptions = ['c_suite', 'board', 'vp', 'svp', 'evp', 'director', 'senior_director', 'manager', 'senior_manager', 'lead', 'senior', 'mid', 'junior', 'entry'];
    const bandOptions = ['emerging', 'developing', 'established', 'senior', 'executive'];

    return (
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex gap-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && executeSearch()}
              placeholder="Search by name, company, title, skills..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            />
            <button
              onClick={() => executeSearch()}
              disabled={searchLoading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
            {(searchQuery || searchResults.length > 0) && (
              <button
                onClick={clearSearch}
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Clear
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Levels</label>
              <select
                multiple
                value={searchFilters.levels}
                onChange={(e) => setSearchFilters({ ...searchFilters, levels: Array.from(e.target.selectedOptions, o => o.value) })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs h-20"
              >
                {levelOptions.map(l => <option key={l} value={l}>{l.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                Score Bands
                <button
                  type="button"
                  onClick={() => setShowScoreBandsModal(true)}
                  className="text-indigo-500 hover:text-indigo-700"
                  title="What do score bands mean?"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </label>
              <select
                multiple
                value={searchFilters.score_bands}
                onChange={(e) => setSearchFilters({ ...searchFilters, score_bands: Array.from(e.target.selectedOptions, o => o.value) })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs h-20"
              >
                {bandOptions.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Min Leadership Score</label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.5"
                value={searchFilters.min_leadership_score || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, min_leadership_score: e.target.value ? parseFloat(e.target.value) : null })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                placeholder="0-10"
              />
              <div className="mt-2">
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={searchFilters.open_to_work === true}
                    onChange={(e) => setSearchFilters({ ...searchFilters, open_to_work: e.target.checked ? true : null })}
                  />
                  Open to Work only
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={searchFilters.linkedin_follower === true}
                    onChange={(e) => setSearchFilters({ ...searchFilters, linkedin_follower: e.target.checked ? true : null })}
                    className="text-emerald-600"
                  />
                  <span className="flex items-center gap-1">
                    <svg className="h-3 w-3 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                    LinkedIn Followers only
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Education Filters */}
          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 14l9-5-9-5-9 5 9 5z" />
                <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
              </svg>
              <span className="text-xs font-semibold text-purple-800">Education Filters</span>
              <span className="text-xs text-purple-600">(Search by where alumni graduated)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-purple-700 mb-1">University / Institution</label>
                <input
                  type="text"
                  value={searchFilters.universities.join(', ')}
                  onChange={(e) => {
                    const value = e.target.value;
                    const unis = value ? value.split(',').map(u => u.trim()).filter(u => u) : [];
                    setSearchFilters({ ...searchFilters, universities: unis });
                  }}
                  className="w-full px-2 py-1.5 border border-purple-300 rounded text-xs focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., Cornell, MIT, Morgan State (comma-separated)"
                />
                <p className="text-xs text-purple-600 mt-1">Partial matches work (e.g., "Howard" matches "Howard University")</p>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 px-3 py-2 bg-purple-100 border border-purple-300 rounded-lg cursor-pointer hover:bg-purple-200 transition-colors">
                  <input
                    type="checkbox"
                    checked={searchFilters.hbcu_only}
                    onChange={(e) => setSearchFilters({ ...searchFilters, hbcu_only: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-purple-300 rounded focus:ring-purple-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-purple-800">HBCU Alumni</span>
                    <p className="text-xs text-purple-600">Filter to alumni from Historically Black Colleges & Universities</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Company Filters */}
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="text-xs font-semibold text-emerald-800">Company Filters</span>
              <span className="text-xs text-emerald-600">(Filter by employer characteristics)</span>
            </div>

            {/* Exclude Self-employed */}
            <div className="mb-3">
              <label className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 border border-emerald-300 rounded-lg cursor-pointer hover:bg-emerald-200 transition-colors w-fit">
                <input
                  type="checkbox"
                  checked={searchFilters.exclude_self_employed}
                  onChange={(e) => setSearchFilters({ ...searchFilters, exclude_self_employed: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 border-emerald-300 rounded focus:ring-emerald-500"
                />
                <span className="text-xs font-medium text-emerald-800">Exclude Self-employed / Freelance</span>
              </label>
            </div>

            {/* Company Type */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-emerald-700 mb-1">Company Type</label>
              <div className="flex flex-wrap gap-1.5">
                {['Public', 'Educational', 'Government', 'Nonprofit'].map(ct => (
                  <button
                    key={ct}
                    onClick={() => {
                      setSearchFilters(prev => ({
                        ...prev,
                        company_types: prev.company_types.includes(ct)
                          ? prev.company_types.filter(t => t !== ct)
                          : [...prev.company_types, ct],
                      }));
                    }}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      searchFilters.company_types.includes(ct)
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    {ct}
                  </button>
                ))}
              </div>
            </div>

            {/* Industry */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-emerald-700 mb-1">Industry</label>
              <div className="flex flex-wrap gap-1.5">
                {COMMON_INDUSTRIES.filter(i => i !== 'Other').map(ind => (
                  <button
                    key={ind}
                    onClick={() => {
                      setSearchFilters(prev => ({
                        ...prev,
                        industries: prev.industries.includes(ind)
                          ? prev.industries.filter(i => i !== ind)
                          : [...prev.industries, ind],
                      }));
                    }}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      searchFilters.industries.includes(ind)
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            </div>

            {/* Company Size */}
            <div>
              <label className="block text-xs font-medium text-emerald-700 mb-1">Company Size (Employees)</label>
              <div className="flex flex-wrap gap-1.5">
                {(() => {
                  const SIZE_500_PLUS = ['501-1K', '1K-5K', '5K-10K', '10K-50K', '50K+'];
                  const ALL_BANDS = ['50K+', '10K-50K', '5K-10K', '1K-5K', '501-1K', '201-500', '51-200', '11-50', '1-10'];
                  const is500Active = SIZE_500_PLUS.every(b => searchFilters.company_size_bands.includes(b));

                  const toggleBand = (band) => {
                    setSearchFilters(prev => ({
                      ...prev,
                      company_size_bands: prev.company_size_bands.includes(band)
                        ? prev.company_size_bands.filter(b => b !== band)
                        : [...prev.company_size_bands, band],
                    }));
                  };

                  const toggle500Plus = () => {
                    if (is500Active) {
                      setSearchFilters(prev => ({
                        ...prev,
                        company_size_bands: prev.company_size_bands.filter(b => !SIZE_500_PLUS.includes(b)),
                      }));
                    } else {
                      setSearchFilters(prev => ({
                        ...prev,
                        company_size_bands: [...new Set([...prev.company_size_bands, ...SIZE_500_PLUS])],
                      }));
                    }
                  };

                  return (
                    <>
                      <button
                        onClick={toggle500Plus}
                        className={`px-2.5 py-1 text-xs rounded-full border-2 font-semibold transition-colors ${
                          is500Active
                            ? 'bg-emerald-700 text-white border-emerald-700'
                            : 'border-emerald-400 text-emerald-700 hover:bg-emerald-100'
                        }`}
                      >
                        500+
                      </button>
                      <span className="text-emerald-300 self-center">|</span>
                      {ALL_BANDS.map(band => (
                        <button
                          key={band}
                          onClick={() => toggleBand(band)}
                          className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                            searchFilters.company_size_bands.includes(band)
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                          }`}
                        >
                          {band}
                        </button>
                      ))}
                    </>
                  );
                })()}
              </div>
              <p className="text-[10px] text-emerald-500 mt-1">
                Sourced from LinkedIn company data.
                {enrichmentStats && ` ${enrichmentStats.roles_with_employee_band} / ${enrichmentStats.total_roles} roles enriched.`}
              </p>
            </div>

            {/* Company Enrichment Panel */}
            <div className="mt-3 pt-3 border-t border-emerald-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-emerald-800">LinkedIn Company Enrichment</span>
                {enrichmentStats && (
                  <span className="text-[10px] text-emerald-600">
                    {enrichmentStats.enriched} enriched / {enrichmentStats.not_yet_cached} pending / {enrichmentStats.total_unique_companies} total
                  </span>
                )}
              </div>
              <p className="text-[10px] text-emerald-600 mb-2">Scrapes LinkedIn company pages to populate employee count, size band, and industry. Enrich to scrape, then Backfill to apply.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  onClick={() => startEnrichment(50)}
                  disabled={enrichmentLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {enrichmentLoading && enrichmentJobId ? (
                    <span className="flex items-center gap-1.5">
                      <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      {enrichmentProgress ? `Enriching... ${enrichmentProgress.processed || 0}/${enrichmentProgress.total || '?'}` : 'Starting...'}
                    </span>
                  ) : 'Enrich Companies (50)'}
                </button>
                <button
                  onClick={() => startEnrichment(200)}
                  disabled={enrichmentLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Enrich 200
                </button>
                <button
                  onClick={triggerBackfill}
                  disabled={enrichmentLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Apply cached enrichment data to exec_roles"
                >
                  {enrichmentLoading && !enrichmentJobId ? (
                    <span className="flex items-center gap-1.5">
                      <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      Backfilling...
                    </span>
                  ) : 'Backfill Roles'}
                </button>
              </div>
              {enrichmentResult && !enrichmentLoading && (
                <div className="mt-2 text-[10px] text-emerald-700 bg-emerald-100 rounded px-2 py-1">
                  {enrichmentResult.message || (
                    enrichmentResult.roles_enriched != null
                      ? `Backfill complete: ${enrichmentResult.roles_enriched} roles enriched, ${enrichmentResult.roles_no_data} no data`
                      : enrichmentResult.enriched != null
                        ? `Enrichment complete: ${enrichmentResult.enriched} companies scraped, ${enrichmentResult.failed} failed`
                        : JSON.stringify(enrichmentResult)
                  )}
                </div>
              )}
            </div>

            {/* Public Company Classification Panel */}
            <div className="mt-3 pt-3 border-t border-emerald-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-emerald-800">Public Company Classification</span>
                {publicCompanyStats && (
                  <span className="text-[10px] text-emerald-600">
                    {publicCompanyStats.public_companies_loaded} companies loaded / {publicCompanyStats.roles_classified_public} roles classified
                  </span>
                )}
              </div>
              <p className="text-[10px] text-emerald-600 mb-2">Matches company names against NASDAQ/NYSE exchange data to identify publicly traded companies. Upload nasdaqlisted.csv and otherlisted.csv (from NASDAQ), then Classify Exact for automatic matches. Containment matches require manual review.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  ref={publicCompanyFileRef}
                  type="file"
                  accept=".csv"
                  multiple
                  className="hidden"
                  onChange={(e) => importPublicCompanies(e.target.files)}
                />
                <button
                  onClick={() => publicCompanyFileRef.current?.click()}
                  disabled={publicCompanyLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Upload nasdaqlisted.csv and/or otherlisted.csv from NASDAQ"
                >
                  {publicCompanyLoading ? 'Uploading...' : 'Upload CSVs'}
                </button>
                <button
                  onClick={() => classifyPublicCompanies(true)}
                  disabled={publicCompanyLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-400 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Preview which roles would be classified as Public (no changes made)"
                >
                  Dry Run
                </button>
                <button
                  onClick={() => classifyPublicCompanies(false)}
                  disabled={publicCompanyLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Apply exact matches and show containment matches for review"
                >
                  Classify Exact
                </button>
              </div>
              {/* Classification results */}
              {publicCompanyResult && !publicCompanyLoading && (
                <div className="mt-2 text-[10px] text-emerald-700 bg-emerald-100 rounded px-2 py-1.5">
                  {publicCompanyResult.error ? (
                    <span className="text-red-600">{publicCompanyResult.error}</span>
                  ) : publicCompanyResult.inserted != null ? (
                    <span>
                      Upload & import complete: {publicCompanyResult.unique_company_names} unique companies from {publicCompanyResult.total_parsed} securities ({publicCompanyResult.nasdaq_parsed} NASDAQ + {publicCompanyResult.other_parsed} other)
                      {publicCompanyResult.files_saved?.length > 0 && <> — Files: {publicCompanyResult.files_saved.join(', ')}</>}
                    </span>
                  ) : (
                    <div>
                      <div className="font-medium mb-1">
                        {publicCompanyResult.dry_run ? 'Dry Run Preview' : 'Exact Matches Applied'}:
                        {' '}{publicCompanyResult.tier1_exact_matches} exact + {publicCompanyResult.tier2_containment_matches} containment = {publicCompanyResult.total_matched_companies} matched companies
                        {publicCompanyResult.updated_roles != null && ` — ${publicCompanyResult.updated_roles} exact-match roles updated`}
                        {publicCompanyResult.tier2_containment_matches > 0 && !publicCompanyResult.dry_run && ' (containment matches below for review)'}
                      </div>
                      {publicCompanyResult.sample_tier1?.length > 0 && (
                        <div className="mt-1">
                          <span className="font-medium">Exact matches ({publicCompanyResult.tier1_exact_matches} total): </span>
                          {publicCompanyResult.sample_tier1.map(m => m.role_name).join(', ')}
                          {publicCompanyResult.tier1_exact_matches > publicCompanyResult.sample_tier1.length && '...'}
                        </div>
                      )}
                      {/* Show containment list in dry-run mode only (in classify mode, they go to review panel below) */}
                      {publicCompanyResult.dry_run && publicCompanyResult.all_tier2?.length > 0 && (
                        <div className="mt-1">
                          <span className="font-medium">Containment matches ({publicCompanyResult.tier2_containment_matches} total):</span>
                          <ul className="mt-0.5 ml-3 list-disc">
                            {publicCompanyResult.all_tier2.map((m, i) => (
                              <li key={i}>{m.role_name} <span className="text-emerald-500">→</span> {m.matched}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {publicCompanyResult.containment_applied && (
                        <div className="mt-1 font-medium text-emerald-800">
                          Containment review applied: {publicCompanyResult.containment_applied.approved_count} approved, {publicCompanyResult.containment_applied.updated_roles} roles updated
                        </div>
                      )}
                      {publicCompanyResult.containment_error && (
                        <div className="mt-1 text-red-600">Error applying containment: {publicCompanyResult.containment_error}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* Tier 2 Containment Review Checklist */}
              {tier2ReviewList.length > 0 && (
                <div className="mt-3 border border-blue-200 rounded-lg bg-blue-50 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-blue-800">
                      Review Containment Matches ({tier2ReviewList.filter(m => m.approved).length}/{tier2ReviewList.length} approved)
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setTier2ReviewList(prev => prev.map(m => ({ ...m, approved: true })))}
                        className="px-2 py-0.5 text-[10px] font-medium rounded border border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setTier2ReviewList(prev => prev.map(m => ({ ...m, approved: false })))}
                        className="px-2 py-0.5 text-[10px] font-medium rounded border border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {tier2ReviewList.map((match, i) => (
                      <label key={i} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs transition-colors ${match.approved ? 'bg-blue-100' : 'bg-gray-100 text-gray-400'}`}>
                        <input
                          type="checkbox"
                          checked={match.approved}
                          onChange={() => setTier2ReviewList(prev => prev.map((m, j) => j === i ? { ...m, approved: !m.approved } : m))}
                          className="w-3.5 h-3.5 text-blue-600 border-blue-300 rounded focus:ring-blue-500"
                        />
                        <span className={match.approved ? 'text-blue-900' : 'text-gray-500'}>{match.role_name}</span>
                        <span className="text-blue-400">→</span>
                        <span className={match.approved ? 'text-blue-600' : 'text-gray-400'}>{match.matched}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={applyApprovedContainment}
                      disabled={publicCompanyLoading || tier2ReviewList.filter(m => m.approved).length === 0}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Apply {tier2ReviewList.filter(m => m.approved).length} Approved
                    </button>
                    <button
                      onClick={() => setTier2ReviewList([])}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Profile Picture Backfill */}
            <div className="mt-3 pt-3 border-t border-purple-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-purple-800">Profile Picture Backfill</span>
              </div>
              <p className="text-[10px] text-purple-600 mb-2">Download profile pictures from LinkedIn data and upload to S3 for profiles that don't have one yet.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  onClick={async () => {
                    setPublicCompanyLoading(true);
                    setPublicCompanyResult(null);
                    try {
                      const res = await axios.post(`${API_URL}/api/exec/profiles/backfill-profile-pictures`, {}, authHeaders());
                      setPublicCompanyResult(res.data);
                    } catch (err) {
                      setPublicCompanyResult({ error: err.response?.data?.detail || err.message });
                    } finally {
                      setPublicCompanyLoading(false);
                    }
                  }}
                  disabled={publicCompanyLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-400 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Download and upload profile pictures to S3 for profiles missing them"
                >
                  {publicCompanyLoading ? 'Processing all profiles...' : 'Backfill All Pictures'}
                </button>
              </div>
              {publicCompanyResult && !publicCompanyLoading && publicCompanyResult.uploaded != null && (
                <div className="mt-2 text-[10px] text-purple-700 bg-purple-100 rounded px-2 py-1.5">
                  <div>Processed: {publicCompanyResult.processed} | Uploaded: {publicCompanyResult.uploaded} | No pic in data: {publicCompanyResult.no_pic_in_raw} | Failed (expired): {publicCompanyResult.failed} | Remaining: {publicCompanyResult.remaining_without_picture}</div>
                  {publicCompanyResult.skipped_total > 0 && (
                    <div className="mt-1 text-purple-500">Previously marked (skipped): {publicCompanyResult.skipped_total}</div>
                  )}
                  {publicCompanyResult.failed > 0 && (
                    <div className="mt-1 text-purple-500 italic">Expired URLs are marked and won't be retried. Re-ingest profiles to get fresh picture URLs.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-700">
              Results {searchTotal > 0 && <span className="text-gray-400 font-normal">({searchTotal} total)</span>}
            </h3>
            <button
              onClick={() => {
                const newState = !showSearchMapPanel;
                setShowSearchMapPanel(newState);
                if (newState) {
                  if (searchMapProfiles.length === 0) {
                    fetchSearchMapProfiles();
                  }
                  if (mapLocationOptions.countries.length === 0) {
                    fetchMapLocationOptions();
                  }
                }
              }}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                showSearchMapPanel
                  ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>◎</span>
              {showSearchMapPanel ? 'Hide Map' : 'Show Map'}
              <span className="text-[10px]">{showSearchMapPanel ? '▲' : '▼'}</span>
            </button>
          </div>
          {searchLoading ? (
            <LoadingSpinner message="Searching..." />
          ) : (
            <>
            {/* Floating selection bar */}
            {isSuperAdmin && searchSelected.size > 0 && (
              <div className="mb-2 px-4 py-2 bg-violet-50 border border-violet-200 rounded-lg flex items-center justify-between">
                <span className="text-sm font-medium text-violet-700">
                  {searchSelected.size} selected
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleOpenSearchAssignModal}
                    className="px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700"
                  >
                    Assign Outreach
                  </button>
                  <button
                    onClick={handleOpenCommentImportModal}
                    disabled={commentImporting}
                    className="px-3 py-1.5 bg-sky-600 text-white text-xs font-medium rounded-lg hover:bg-sky-700 disabled:opacity-50"
                  >
                    {commentImporting ? 'Importing...' : 'Import Comments'}
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {isSuperAdmin && (
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 bg-gray-50 w-10">
                        <input
                          type="checkbox"
                          checked={searchResults.filter(r => r.exec_contact_id).length > 0 && searchResults.filter(r => r.exec_contact_id).every(r => searchSelected.has(r.exec_contact_id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newSet = new Set(searchSelected);
                              searchResults.forEach(r => { if (r.exec_contact_id) newSet.add(r.exec_contact_id); });
                              setSearchSelected(newSet);
                            } else {
                              const newSet = new Set(searchSelected);
                              searchResults.forEach(r => { if (r.exec_contact_id) newSet.delete(r.exec_contact_id); });
                              setSearchSelected(newSet);
                            }
                          }}
                          className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                        />
                      </th>
                    )}
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 sticky left-0 z-20 bg-gray-50 min-w-[150px]">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 sticky left-[150px] z-20 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] min-w-[200px]">Headline</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Company</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Location</th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:text-indigo-600 select-none"
                      onClick={() => {
                        const next = searchSort.field === 'followers' && searchSort.dir === 'desc' ? 'asc'
                          : searchSort.field === 'followers' && searchSort.dir === 'asc' ? null : 'desc';
                        const newSort = next ? { field: 'followers', dir: next } : { field: null, dir: null };
                        setSearchSort(newSort);
                        if (searchResults.length > 0) executeSearch(1, newSort);
                      }}
                    >
                      Followers {searchSort.field === 'followers' ? (searchSort.dir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:text-indigo-600 select-none"
                      onClick={() => {
                        const next = searchSort.field === 'connections' && searchSort.dir === 'desc' ? 'asc'
                          : searchSort.field === 'connections' && searchSort.dir === 'asc' ? null : 'desc';
                        const newSort = next ? { field: 'connections', dir: next } : { field: null, dir: null };
                        setSearchSort(newSort);
                        if (searchResults.length > 0) executeSearch(1, newSort);
                      }}
                    >
                      Connections {searchSort.field === 'connections' ? (searchSort.dir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer hover:text-indigo-600 select-none"
                      onClick={() => {
                        const next = searchSort.field === 'score' && searchSort.dir === 'desc' ? 'asc'
                          : searchSort.field === 'score' && searchSort.dir === 'asc' ? null : 'desc';
                        const newSort = next ? { field: 'score', dir: next } : { field: null, dir: null };
                        setSearchSort(newSort);
                        if (searchResults.length > 0) executeSearch(1, newSort);
                      }}
                    >
                      Score {searchSort.field === 'score' ? (searchSort.dir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Band</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 w-10" title="Address on file (improves Versium score accuracy)">Addr</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {searchResults.map((r) => (
                    <tr key={r.id} className={`hover:bg-gray-50 group ${searchSelected.has(r.exec_contact_id) ? 'bg-violet-50/50' : ''}`}>
                      {isSuperAdmin && (
                        <td className="px-2 py-2 text-center w-10">
                          {r.exec_contact_id ? (
                            <input
                              type="checkbox"
                              checked={searchSelected.has(r.exec_contact_id)}
                              onChange={(e) => {
                                const newSet = new Set(searchSelected);
                                if (e.target.checked) newSet.add(r.exec_contact_id);
                                else newSet.delete(r.exec_contact_id);
                                setSearchSelected(newSet);
                              }}
                              className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                            />
                          ) : (
                            <span className="text-gray-300" title="No contact record">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-2 font-medium text-indigo-600 sticky left-0 z-10 bg-white group-hover:bg-gray-50 min-w-[150px]">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(`/admin/executive-intelligence/profile/${r.id}`)}
                            className="hover:underline text-left"
                          >
                            {r.full_name || 'Unknown'}
                          </button>
                          {r.is_linkedin_follower && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-blue-600" title={r.linkedin_connected_on ? `Connected ${new Date(r.linkedin_connected_on).toLocaleDateString()}` : 'LinkedIn Follower'}>
                              <svg className="h-2.5 w-2.5 mr-0.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                              </svg>
                            </span>
                          )}
                          {r.match_type && r.match_type !== 'name' && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              r.match_type === 'title/company' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {r.match_type === 'title/company' ? 'title/co.' : 'keyword'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-600 max-w-md sticky left-[150px] z-10 bg-white group-hover:bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] min-w-[200px]">{r.headline || '—'}</td>
                      <td className="px-4 py-2">{r.current_company || '—'}</td>
                      <td className="px-4 py-2 text-gray-500">{r.location_raw || '—'}</td>
                      <td className="px-4 py-2 text-gray-500">{r.follower_count != null ? r.follower_count.toLocaleString() : '—'}</td>
                      <td className="px-4 py-2 text-gray-500">{r.connection_count != null ? r.connection_count.toLocaleString() : '—'}</td>
                      <td className="px-4 py-2 font-mono">{r.leadership_score != null ? r.leadership_score.toFixed(1) : '—'}</td>
                      <td className="px-4 py-2"><ScoreBadge band={r.score_band} /></td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleBookmark(r.id, r.is_bookmarked)}
                            disabled={bookmarkActionLoading[r.id]}
                            className={`text-lg transition-colors ${
                              r.is_bookmarked
                                ? 'text-yellow-500 hover:text-yellow-600'
                                : 'text-gray-300 hover:text-yellow-400'
                            } ${bookmarkActionLoading[r.id] ? 'opacity-50' : ''}`}
                            title={r.is_bookmarked ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            {r.is_bookmarked ? '★' : '☆'}
                          </button>
                          <button
                            onClick={() => navigate(`/admin/executive-intelligence/profile/${r.id}`)}
                            className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                          >
                            View
                          </button>
                          {r.profile_url && (
                            <button
                              onClick={() => handleSingleCommentImport(r.exec_person_id || r.id)}
                              disabled={!!commentSingleImporting[r.exec_person_id || r.id]}
                              className={`text-xs font-medium transition-colors ${
                                commentSingleImporting[r.exec_person_id || r.id] === 'done'
                                  ? 'text-green-600'
                                  : commentSingleImporting[r.exec_person_id || r.id]
                                    ? 'text-gray-400'
                                    : 'text-sky-600 hover:text-sky-800'
                              }`}
                              title="Import LinkedIn comments"
                            >
                              {commentSingleImporting[r.exec_person_id || r.id] === 'done' ? '..done' : commentSingleImporting[r.exec_person_id || r.id] ? '...' : 'Cmts'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {r.email ? (
                          <a href={`mailto:${r.email}`} className="text-indigo-600 hover:text-indigo-800 hover:underline">
                            {r.email}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {r.has_address ? (
                          <svg className="w-4 h-4 mx-auto text-green-500" fill="currentColor" viewBox="0 0 20 20" title="Address available — better Versium accuracy">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 mx-auto text-gray-300" fill="currentColor" viewBox="0 0 20 20" title="No address on file">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </td>
                    </tr>
                  ))}
                  {searchResults.length === 0 && !searchLoading && (
                    <tr><td colSpan={isSuperAdmin ? 12 : 11} className="px-4 py-8 text-center text-gray-400">
                      {searchQuery ? 'No results found' : 'Enter a search term to begin'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            </>
          )}
          {/* Pagination */}
          {searchTotal > 25 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Page {searchPage} of {Math.ceil(searchTotal / 25)}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => executeSearch(searchPage - 1)}
                  disabled={searchPage <= 1}
                  className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => executeSearch(searchPage + 1)}
                  disabled={searchPage >= Math.ceil(searchTotal / 25)}
                  className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Collapsible Map Panel */}
        {showSearchMapPanel && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">
                  Map View
                  {searchMapProfiles.length > 0 && (
                    <span className="text-gray-400 font-normal ml-2">
                      ({searchMapProfiles.length} with coordinates)
                    </span>
                  )}
                </span>
                {/* Mini Legend */}
                <div className="hidden md:flex items-center gap-3 text-xs text-gray-500 ml-4">
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                    <span>Bookmarked</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-purple-600"></div>
                    <span>Executive</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>
                    <span>Senior</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-600"></div>
                    <span>Established</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-500"></div>
                    <span>Other</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchSearchMapProfiles}
                  disabled={searchMapLoading}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded disabled:opacity-50"
                >
                  {searchMapLoading ? 'Loading...' : 'Refresh'}
                </button>
                <button
                  onClick={() => setShowSearchMapPanel(false)}
                  className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Location Filters - Cascading: Country → State → City */}
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-medium text-gray-500">Filter by:</span>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-500">Country:</label>
                  <select
                    value={mapLocationFilters.countries[0] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Reset state and city when country changes
                      const updated = { countries: val ? [val] : [], states: [], cities: [] };
                      setMapLocationFilters(updated);
                      // Refresh state/city options based on new country
                      fetchMapLocationOptions(val || null, null);
                      fetchSearchMapProfiles();
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                  >
                    <option value="">All Countries</option>
                    {mapLocationOptions.countries.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-500">State:</label>
                  <select
                    value={mapLocationFilters.states[0] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const country = mapLocationFilters.countries[0] || null;
                      // Reset city when state changes
                      const updated = { ...mapLocationFilters, states: val ? [val] : [], cities: [] };
                      setMapLocationFilters(updated);
                      // Refresh city options based on country + state
                      fetchMapLocationOptions(country, val || null);
                      fetchSearchMapProfiles();
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                    disabled={mapLocationOptionsLoading}
                  >
                    <option value="">All States</option>
                    {mapLocationOptions.states.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-500">City:</label>
                  <select
                    value={mapLocationFilters.cities[0] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const updated = { ...mapLocationFilters, cities: val ? [val] : [] };
                      setMapLocationFilters(updated);
                      fetchSearchMapProfiles();
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                    disabled={mapLocationOptionsLoading}
                  >
                    <option value="">All Cities</option>
                    {mapLocationOptions.cities.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                {(mapLocationFilters.countries.length > 0 || mapLocationFilters.states.length > 0 || mapLocationFilters.cities.length > 0) && (
                  <button
                    onClick={() => {
                      setMapLocationFilters({ cities: [], states: [], countries: [] });
                      fetchMapLocationOptions(); // Reset to show all options
                      fetchSearchMapProfiles();
                    }}
                    className="px-2 py-1 text-xs text-red-600 hover:text-red-700"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {searchMapLoading ? (
              <div className="h-[350px] flex items-center justify-center">
                <LoadingSpinner message="Loading map..." />
              </div>
            ) : searchMapProfiles.length === 0 ? (
              <div className="h-[200px] flex flex-col items-center justify-center text-gray-400">
                <div className="text-3xl mb-2">◎</div>
                <p className="text-sm">No geocoded profiles match your filters</p>
                <p className="text-xs mt-1">Try adjusting filters or geocode more profiles in the Map tab</p>
              </div>
            ) : (
              <div style={{ height: '350px' }}>
                <MapContainer
                  center={[39.8283, -98.5795]}
                  zoom={4}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {searchMapProfiles.map((profile) => (
                    <Marker
                      key={profile.id}
                      position={[profile.latitude, profile.longitude]}
                      icon={createExecMarkerIcon(profile)}
                    >
                      <Popup>
                        <div className="min-w-[200px]">
                          <div className="flex items-start gap-3">
                            {profile.profile_picture_url ? (
                              <img
                                src={profile.profile_picture_url}
                                alt={profile.full_name}
                                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                <span className="text-gray-500 text-lg font-medium">
                                  {profile.full_name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 mb-0.5 truncate">{profile.full_name}</h4>
                              {profile.current_title && (
                                <p className="text-sm text-gray-600 truncate">{profile.current_title}</p>
                              )}
                              {profile.current_company && (
                                <p className="text-sm text-gray-500 truncate">{profile.current_company}</p>
                              )}
                            </div>
                          </div>
                          {profile.location_raw && (
                            <p className="text-xs text-gray-400 mt-2">{profile.location_raw}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {profile.score_band && (
                              <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                profile.score_band === 'executive' ? 'bg-purple-100 text-purple-700' :
                                profile.score_band === 'senior' ? 'bg-blue-100 text-blue-700' :
                                profile.score_band === 'established' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {profile.score_band}
                              </span>
                            )}
                            {profile.leadership_score != null && (
                              <span className="text-xs text-gray-500">
                                Score: {profile.leadership_score.toFixed(1)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <button
                              onClick={() => toggleBookmark(profile.id, profile.is_bookmarked)}
                              disabled={bookmarkActionLoading[profile.id]}
                              className={`text-lg transition-colors ${
                                profile.is_bookmarked
                                  ? 'text-yellow-500 hover:text-yellow-600'
                                  : 'text-gray-300 hover:text-yellow-400'
                              } ${bookmarkActionLoading[profile.id] ? 'opacity-50' : ''}`}
                              title={profile.is_bookmarked ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              {profile.is_bookmarked ? '★' : '☆'}
                            </button>
                            <button
                              onClick={() => navigate(`/admin/executive-intelligence/profile/${profile.id}`)}
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              View Profile →
                            </button>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // IMPORTS TAB RENDER
  // ============================================
  const renderImports = () => (
    <div className="space-y-4">

      {/* Unprovisioned Contacts */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <svg className="h-4 w-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            Unprovisioned Contacts
          </h3>
          <button
            onClick={() => {
              setShowUnprovisionedSection(!showUnprovisionedSection);
              if (!showUnprovisionedSection && !unprovisionedData) fetchUnprovisioned(1);
            }}
            className="text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1"
          >
            {showUnprovisionedSection ? 'Hide' : 'Show'}
            <svg className={`h-4 w-4 transition-transform ${showUnprovisionedSection ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Summary bar - always visible */}
        {unprovisionedData?.summary && (
          <div className="text-sm text-amber-700 mt-2">
            <strong>{unprovisionedData.summary.total_unprovisioned.toLocaleString()}</strong> contacts without profiles
            {' '}(<span className="text-green-700 font-medium">{unprovisionedData.summary.with_linkedin_url.toLocaleString()}</span> have LinkedIn URLs,
            {' '}<span className="text-red-700 font-medium">{unprovisionedData.summary.without_linkedin_url.toLocaleString()}</span> need URLs)
          </div>
        )}

        {showUnprovisionedSection && (
          <div className="mt-4 space-y-3">
            {/* Assignment Stats Dashboard (superadmin only) */}
            {isSuperAdmin && (
              <div className="mb-2">
                <button
                  onClick={() => { setUnprovisionedShowStats(!unprovisionedShowStats); if (!unprovisionedShowStats) fetchAssignmentStats(); }}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  {unprovisionedShowStats ? 'Hide' : 'Show'} Assignment Dashboard
                </button>
                {unprovisionedShowStats && unprovisionedAssignmentStats && (
                  <div className="mt-2 bg-white border border-indigo-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-indigo-800 mb-3">Research Assignment Progress</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                      <div className="bg-indigo-50 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-indigo-700">{unprovisionedAssignmentStats.total_assigned}</div>
                        <div className="text-xs text-indigo-600">Total Assigned</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-green-700">{unprovisionedAssignmentStats.total_found}</div>
                        <div className="text-xs text-green-600">Found</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-red-700">{unprovisionedAssignmentStats.total_not_found}</div>
                        <div className="text-xs text-red-600">Not Found</div>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-yellow-700">{unprovisionedAssignmentStats.total_pending}</div>
                        <div className="text-xs text-yellow-600">Pending</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-gray-700">{unprovisionedAssignmentStats.unassigned}</div>
                        <div className="text-xs text-gray-600">Unassigned</div>
                      </div>
                    </div>
                    {unprovisionedAssignmentStats.by_assignee.length > 0 && (
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="bg-indigo-50 text-indigo-800">
                            <th className="px-3 py-2 text-left font-medium">Team Member</th>
                            <th className="px-3 py-2 text-center font-medium">Assigned</th>
                            <th className="px-3 py-2 text-center font-medium">Found</th>
                            <th className="px-3 py-2 text-center font-medium">Not Found</th>
                            <th className="px-3 py-2 text-center font-medium">Pending</th>
                            <th className="px-3 py-2 text-center font-medium">Overdue</th>
                            <th className="px-3 py-2 text-center font-medium">Completion</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-indigo-100">
                          {unprovisionedAssignmentStats.by_assignee.map(s => (
                            <tr key={s.admin_id} className="hover:bg-indigo-50/50">
                              <td className="px-3 py-2 font-medium text-gray-800">{s.admin_name}</td>
                              <td className="px-3 py-2 text-center">{s.assigned}</td>
                              <td className="px-3 py-2 text-center text-green-700 font-medium">{s.found}</td>
                              <td className="px-3 py-2 text-center text-red-700">{s.not_found}</td>
                              <td className="px-3 py-2 text-center text-yellow-700">{s.pending}</td>
                              <td className="px-3 py-2 text-center">{s.overdue > 0 ? <span className="text-red-600 font-bold">{s.overdue}</span> : '0'}</td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center gap-1">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${s.completion_pct}%` }} />
                                  </div>
                                  <span className="text-xs font-medium w-10 text-right">{s.completion_pct}%</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Filters + Actions */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Filter toggles */}
              {['all', 'with_url', 'without_url'].map(f => (
                <button
                  key={f}
                  onClick={() => { setUnprovisionedFilter(f); setTimeout(() => fetchUnprovisioned(1), 0); }}
                  className={`px-3 py-1 text-xs rounded-full border ${unprovisionedFilter === f ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100'}`}
                >
                  {f === 'all' ? 'All' : f === 'with_url' ? 'Ready to Process' : 'Needs LinkedIn URL'}
                </button>
              ))}
              {/* Assignee filter */}
              <select
                value={unprovisionedAssignedFilter}
                onChange={(e) => { setUnprovisionedAssignedFilter(e.target.value); setTimeout(() => fetchUnprovisioned(1), 0); }}
                onFocus={() => { if (unprovisionedAssignableAdmins.length === 0) fetchAssignableAdmins(); }}
                className="px-2 py-1 text-xs border border-amber-300 rounded-lg focus:ring-1 focus:ring-amber-500"
              >
                <option value="">All Assignees</option>
                <option value="0">Unassigned</option>
                {unprovisionedAssignableAdmins.map(a => (
                  <option key={a.id} value={a.id}>{a.full_name}</option>
                ))}
              </select>
              {/* Research status filter */}
              <select
                value={unprovisionedResearchFilter}
                onChange={(e) => { setUnprovisionedResearchFilter(e.target.value); setTimeout(() => fetchUnprovisioned(1), 0); }}
                className="px-2 py-1 text-xs border border-amber-300 rounded-lg focus:ring-1 focus:ring-amber-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="found">Found</option>
                <option value="not_found">Not Found</option>
                <option value="overdue">Overdue</option>
              </select>
              {/* Search */}
              <input
                type="text"
                placeholder="Search by name..."
                value={unprovisionedSearch}
                onChange={(e) => setUnprovisionedSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchUnprovisioned(1)}
                className="px-3 py-1 text-xs border border-amber-300 rounded-lg w-48 focus:ring-1 focus:ring-amber-500"
              />
              <button onClick={() => fetchUnprovisioned(1)} className="px-2 py-1 text-xs bg-amber-200 text-amber-800 rounded hover:bg-amber-300">Search</button>
              {/* Assign Selected button */}
              {unprovisionedSelected.size > 0 && (
                <button
                  onClick={handleOpenAssignModal}
                  className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Assign Selected ({unprovisionedSelected.size})
                </button>
              )}
              {/* Process All Ready button */}
              {unprovisionedData?.summary?.with_linkedin_url > 0 && (
                <button
                  onClick={() => {
                    const count = unprovisionedData.summary.with_linkedin_url;
                    const cost = (count * 0.08).toFixed(2);
                    if (window.confirm(`Process ${count.toLocaleString()} contacts with LinkedIn URLs?\n\nEstimated cost: $${cost} (Apify + scoring)\n\nThis will:\n1. Scrape each LinkedIn profile via Apify\n2. Create executive profiles\n3. Link contacts to profiles\n4. Score all new profiles\n\nContacts scoring 7+ automatically receive DataFinder predictive scores.\n\nContinue?`)) {
                      handleProcessUnprovisioned('all_with_linkedin');
                    }
                  }}
                  disabled={unprovisionedProcessing}
                  className="ml-auto px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  {unprovisionedProcessing ? 'Processing...' : `Process All Ready (${unprovisionedData.summary.with_linkedin_url})`}
                </button>
              )}
            </div>

            {/* Processing status */}
            {unprovisionedBatchStatus && (
              <div className={`p-3 rounded-lg border ${unprovisionedBatchStatus.status === 'completed' ? 'bg-emerald-50 border-emerald-200' : unprovisionedBatchStatus.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">
                    {unprovisionedBatchStatus.status === 'completed' ? 'Processing Complete' : unprovisionedBatchStatus.status === 'failed' ? 'Processing Failed' : 'Processing...'}
                  </span>
                  {unprovisionedBatchStatus.status === 'completed' && (
                    <button onClick={() => setUnprovisionedBatchStatus(null)} className="text-xs text-gray-500 hover:text-gray-700">Dismiss</button>
                  )}
                </div>
                {unprovisionedBatchStatus.total_items > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className={`h-2 rounded-full transition-all ${unprovisionedBatchStatus.status === 'completed' ? 'bg-emerald-500' : unprovisionedBatchStatus.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.round(((unprovisionedBatchStatus.processed_count || 0) / unprovisionedBatchStatus.total_items) * 100)}%` }}
                    />
                  </div>
                )}
                <div className="flex gap-4 text-xs text-gray-600">
                  <span>Total: {unprovisionedBatchStatus.total_items || unprovisionedBatchStatus.total_contacts}</span>
                  {unprovisionedBatchStatus.processed_count != null && <span>Processed: {unprovisionedBatchStatus.processed_count}</span>}
                  {unprovisionedBatchStatus.successful_count != null && <span className="text-green-600">Succeeded: {unprovisionedBatchStatus.successful_count}</span>}
                  {unprovisionedBatchStatus.failed_count > 0 && <span className="text-red-600">Failed: {unprovisionedBatchStatus.failed_count}</span>}
                </div>
              </div>
            )}

            {/* Contact table */}
            {unprovisionedLoading ? (
              <div className="text-center py-8 text-sm text-gray-500">Loading contacts...</div>
            ) : unprovisionedData?.items?.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-amber-100 text-amber-800">
                        <th className="px-2 py-2 text-center font-medium sticky left-0 z-20 bg-amber-100" style={{ width: '40px', minWidth: '40px' }}>
                          <input
                            type="checkbox"
                            checked={unprovisionedData.items.length > 0 && unprovisionedData.items.every(c => unprovisionedSelected.has(c.id))}
                            onChange={toggleSelectAllUnprovisioned}
                            className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                          />
                        </th>
                        {[
                          { key: 'full_name', label: 'Name', style: { left: '40px', minWidth: '160px' }, sticky: true },
                          { key: 'university', label: 'University', style: { left: '200px', minWidth: '130px' }, sticky: true },
                          { key: 'graduation_year', label: 'Grad', style: { left: '330px', minWidth: '60px' }, sticky: true },
                        ].map(col => (
                          <th
                            key={col.key}
                            className={`px-3 py-2 text-left font-medium cursor-pointer select-none hover:text-amber-800 ${col.sticky ? 'sticky z-20 bg-amber-100' : ''}`}
                            style={col.style}
                            onClick={() => {
                              const newDir = unprovisionedSort.field === col.key && unprovisionedSort.dir === 'asc' ? 'desc' : 'asc';
                              const newSort = { field: col.key, dir: newDir };
                              setUnprovisionedSort(newSort);
                              fetchUnprovisioned(1, undefined, newSort);
                            }}
                          >
                            {col.label} {unprovisionedSort.field === col.key ? (unprovisionedSort.dir === 'asc' ? '▲' : '▼') : ''}
                          </th>
                        ))}
                        <th
                          className="px-3 py-2 text-left font-medium sticky z-20 bg-amber-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] cursor-pointer select-none hover:text-amber-800"
                          style={{ left: '390px', minWidth: '190px' }}
                          onClick={() => {
                            const newDir = unprovisionedSort.field === 'ethnicity' && unprovisionedSort.dir === 'asc' ? 'desc' : 'asc';
                            const newSort = { field: 'ethnicity', dir: newDir };
                            setUnprovisionedSort(newSort);
                            fetchUnprovisioned(1, undefined, newSort);
                          }}
                          title="Sort by ethnicity"
                        >
                          Demographics {unprovisionedSort.field === 'ethnicity' ? (unprovisionedSort.dir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th
                          className="px-3 py-2 text-left font-medium cursor-pointer select-none hover:text-amber-800"
                          onClick={() => {
                            const newDir = unprovisionedSort.field === 'current_company' && unprovisionedSort.dir === 'asc' ? 'desc' : 'asc';
                            const newSort = { field: 'current_company', dir: newDir };
                            setUnprovisionedSort(newSort);
                            fetchUnprovisioned(1, undefined, newSort);
                          }}
                        >
                          Company {unprovisionedSort.field === 'current_company' ? (unprovisionedSort.dir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th
                          className="px-3 py-2 text-left font-medium cursor-pointer select-none hover:text-amber-800"
                          style={{minWidth: '260px'}}
                          onClick={() => {
                            const newDir = unprovisionedSort.field === 'linkedin_url' && unprovisionedSort.dir === 'asc' ? 'desc' : 'asc';
                            const newSort = { field: 'linkedin_url', dir: newDir };
                            setUnprovisionedSort(newSort);
                            fetchUnprovisioned(1, undefined, newSort);
                          }}
                        >
                          LinkedIn URL {unprovisionedSort.field === 'linkedin_url' ? (unprovisionedSort.dir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th
                          className="px-3 py-2 text-left font-medium cursor-pointer select-none hover:text-amber-800"
                          style={{minWidth: '170px'}}
                          onClick={() => {
                            const newDir = unprovisionedSort.field === 'research_status' && unprovisionedSort.dir === 'asc' ? 'desc' : 'asc';
                            const newSort = { field: 'research_status', dir: newDir };
                            setUnprovisionedSort(newSort);
                            fetchUnprovisioned(1, undefined, newSort);
                          }}
                          title="Sort by research status"
                        >
                          Assignment {unprovisionedSort.field === 'research_status' ? (unprovisionedSort.dir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th className="px-3 py-2 text-left font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {unprovisionedData.items.map(contact => {
                        const isOverdue = contact.assignment_due_date && contact.research_status === 'pending' && new Date(contact.assignment_due_date) < new Date();
                        return (
                        <tr key={contact.id} className={`hover:bg-amber-50/50 ${unprovisionedSelected.has(contact.id) ? 'bg-indigo-50/50' : ''}`}>
                          <td className="px-2 py-2 text-center sticky left-0 z-10 bg-amber-50" style={{ width: '40px', minWidth: '40px' }}>
                            <input
                              type="checkbox"
                              checked={unprovisionedSelected.has(contact.id)}
                              onChange={() => toggleUnprovisionedSelect(contact.id)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap sticky z-10 bg-amber-50" style={{ left: '40px', minWidth: '160px' }} title={contact.email || contact.personal_email || ''}>
                            {contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`}
                            {(contact.email || contact.personal_email) && (
                              <div className="text-[10px] text-gray-400 font-normal truncate max-w-[140px]">{contact.email || contact.personal_email}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600 sticky z-10 bg-amber-50" style={{ left: '200px', minWidth: '130px' }}>{contact.university || '-'}</td>
                          <td className="px-3 py-2 text-gray-600 sticky z-10 bg-amber-50" style={{ left: '330px', minWidth: '60px' }}>{contact.graduation_year || '-'}</td>
                          <td className="px-3 py-2 sticky z-10 bg-amber-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: '390px', minWidth: '190px' }}>
                            {(contact.major || contact.gender || contact.ethnicity) ? (
                              <div className="relative group">
                                <div className="text-[11px] text-gray-500 cursor-default leading-tight">
                                  <div>{[contact.gender, contact.ethnicity].filter(Boolean).join(' / ') || ''}</div>
                                  {contact.major && <div className="text-gray-400 truncate max-w-[170px]">{contact.major}</div>}
                                </div>
                                <div className="absolute left-0 top-full mt-1 z-30 hidden group-hover:block bg-white border border-gray-200 rounded-lg shadow-lg p-2.5 min-w-[180px]">
                                  {contact.gender && <div className="text-xs text-gray-700"><span className="font-medium text-gray-500">Gender:</span> {contact.gender}</div>}
                                  {contact.ethnicity && <div className="text-xs text-gray-700 mt-0.5"><span className="font-medium text-gray-500">Ethnicity:</span> {contact.ethnicity}</div>}
                                  {contact.major && <div className="text-xs text-gray-700 mt-0.5"><span className="font-medium text-gray-500">Major:</span> {contact.major}</div>}
                                </div>
                              </div>
                            ) : <span className="text-gray-400 text-[11px]">-</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{contact.current_company || '-'}</td>
                          <td className="px-3 py-2">
                            {contact.has_linkedin_url ? (
                              <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[260px]" title={contact.linkedin_url}>
                                {contact.linkedin_url.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//i, '').replace(/\/$/, '')}
                              </a>
                            ) : (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={unprovisionedUrlEdits[contact.id] || ''}
                                  onChange={(e) => setUnprovisionedUrlEdits(prev => ({ ...prev, [contact.id]: e.target.value }))}
                                  placeholder="Paste LinkedIn URL..."
                                  className="border border-gray-300 rounded px-2 py-1 w-44 text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                                  onKeyDown={(e) => e.key === 'Enter' && handleSaveLinkedinUrl(contact.id)}
                                />
                                <button
                                  onClick={() => handleSaveLinkedinUrl(contact.id)}
                                  disabled={unprovisionedSaving[contact.id] || !unprovisionedUrlEdits[contact.id]?.trim()}
                                  className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                                >
                                  {unprovisionedSaving[contact.id] ? '...' : 'Save'}
                                </button>
                              </div>
                            )}
                          </td>
                          {/* Assignment tag */}
                          <td className="px-3 py-2">
                            {contact.assigned_to_name ? (
                              <div className={`inline-flex flex-col px-2 py-1 rounded-lg text-xs border ${
                                contact.research_status === 'found' ? 'bg-green-50 border-green-200 text-green-800' :
                                contact.research_status === 'not_found' ? 'bg-red-50 border-red-200 text-red-800' :
                                isOverdue ? 'bg-amber-50 border-amber-300 text-amber-800' :
                                'bg-indigo-50 border-indigo-200 text-indigo-800'
                              }`}>
                                <span className="font-medium">{contact.assigned_to_name}</span>
                                <span className="text-[10px] opacity-75">
                                  Assigned {new Date(contact.assigned_at).toLocaleDateString()}
                                  {contact.assignment_due_date && (
                                    <>{' '}· Due {new Date(contact.assignment_due_date + 'T00:00:00').toLocaleDateString()}</>
                                  )}
                                </span>
                                {contact.research_status === 'not_found' && (
                                  <span className="text-[10px] italic mt-0.5" title={contact.research_note}>Not found{contact.research_note ? `: ${contact.research_note}` : ''}</span>
                                )}
                                {contact.research_status === 'found' && (
                                  <span className="text-[10px] mt-0.5">URL found</span>
                                )}
                                {isOverdue && (
                                  <span className="text-[10px] font-bold text-amber-700 mt-0.5">OVERDUE</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">Unassigned</span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleCopySearchText(contact)}
                                className={`px-2 py-1 text-xs rounded ${unprovisionedCopied[contact.id] ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
                                title="Copy name + university for LinkedIn search"
                              >
                                {unprovisionedCopied[contact.id] ? 'Copied!' : 'Copy'}
                              </button>
                              {contact.has_linkedin_url && (
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Process ${contact.full_name || contact.first_name}?\n\nThis will scrape their LinkedIn profile, create an executive profile, and score them.\n\nEstimated cost: ~$0.08`)) {
                                      handleProcessUnprovisioned('single', contact.id);
                                    }
                                  }}
                                  disabled={unprovisionedProcessing}
                                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                >
                                  Process
                                </button>
                              )}
                              {!contact.has_linkedin_url && contact.research_status !== 'not_found' && (
                                <button
                                  onClick={() => { setUnprovisionedNotFoundModal(contact.id); setUnprovisionedNotFoundNote(''); }}
                                  className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                  title="Mark as unable to find on LinkedIn"
                                >
                                  Not Found
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {unprovisionedData.total_pages > 1 && (() => {
                  const tp = unprovisionedData.total_pages;
                  const cp = unprovisionedData.page;
                  // Build smart page numbers: 1 ... (cp-2) (cp-1) [cp] (cp+1) (cp+2) ... last
                  const pages = [];
                  const addPage = (n) => { if (n >= 1 && n <= tp && !pages.includes(n)) pages.push(n); };
                  addPage(1);
                  for (let i = cp - 2; i <= cp + 2; i++) addPage(i);
                  addPage(tp);
                  pages.sort((a, b) => a - b);
                  // Insert ellipsis markers
                  const display = [];
                  for (let i = 0; i < pages.length; i++) {
                    if (i > 0 && pages[i] - pages[i - 1] > 1) display.push('...');
                    display.push(pages[i]);
                  }
                  return (
                    <div className="flex flex-col gap-2 mt-3">
                      {/* Top row: info + page size */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          Page {cp} of {tp} ({unprovisionedData.total.toLocaleString()} contacts)
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-500">Per page:</span>
                          {[50, 100, 200].map(size => (
                            <button
                              key={size}
                              onClick={() => { setUnprovisionedPageSize(size); fetchUnprovisioned(1, size); }}
                              className={`px-2 py-0.5 text-xs rounded ${
                                unprovisionedPageSize === size
                                  ? 'bg-amber-600 text-white'
                                  : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* Bottom row: navigation */}
                      <div className="flex items-center justify-between">
                        {/* Prev / page numbers / Next */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => fetchUnprovisioned(1)}
                            disabled={cp <= 1}
                            className="px-1.5 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                            title="First page"
                          >
                            &laquo;
                          </button>
                          <button
                            onClick={() => fetchUnprovisioned(cp - 1)}
                            disabled={cp <= 1}
                            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                          >
                            Prev
                          </button>
                          {display.map((item, idx) =>
                            item === '...' ? (
                              <span key={`ellipsis-${idx}`} className="px-1 text-xs text-gray-400">...</span>
                            ) : (
                              <button
                                key={item}
                                onClick={() => fetchUnprovisioned(item)}
                                className={`px-2 py-1 text-xs rounded ${
                                  item === cp
                                    ? 'bg-amber-600 text-white font-semibold'
                                    : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                {item}
                              </button>
                            )
                          )}
                          <button
                            onClick={() => fetchUnprovisioned(cp + 1)}
                            disabled={cp >= tp}
                            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                          >
                            Next
                          </button>
                          <button
                            onClick={() => fetchUnprovisioned(tp)}
                            disabled={cp >= tp}
                            className="px-1.5 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                            title="Last page"
                          >
                            &raquo;
                          </button>
                        </div>
                        {/* Jump to page */}
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">Go to:</span>
                          <input
                            type="number"
                            min={1}
                            max={tp}
                            value={unprovisionedJumpPage}
                            onChange={(e) => setUnprovisionedJumpPage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const p = parseInt(unprovisionedJumpPage);
                                if (p >= 1 && p <= tp) { fetchUnprovisioned(p); setUnprovisionedJumpPage(''); }
                              }
                            }}
                            className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder={`${cp}`}
                          />
                          <button
                            onClick={() => {
                              const p = parseInt(unprovisionedJumpPage);
                              if (p >= 1 && p <= tp) { fetchUnprovisioned(p); setUnprovisionedJumpPage(''); }
                            }}
                            className="px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded hover:bg-amber-200"
                          >
                            Go
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : unprovisionedData ? (
              <div className="text-center py-6 text-sm text-gray-500">No unprovisioned contacts found for this filter.</div>
            ) : null}

            {/* Assign Modal */}
            {unprovisionedAssignModalOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Assign {unprovisionedSelected.size} Contact{unprovisionedSelected.size !== 1 ? 's' : ''} for Research
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                      <select
                        value={unprovisionedAssignTarget}
                        onChange={(e) => setUnprovisionedAssignTarget(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Select team member...</option>
                        {unprovisionedAssignableAdmins.map(a => (
                          <option key={a.id} value={a.id}>{a.full_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date (optional)</label>
                      <input
                        type="date"
                        value={unprovisionedAssignDueDate}
                        onChange={(e) => setUnprovisionedAssignDueDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setUnprovisionedAssignModalOpen(false)}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAssignContacts}
                      disabled={!unprovisionedAssignTarget || unprovisionedAssigning}
                      className="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:bg-gray-400"
                    >
                      {unprovisionedAssigning ? 'Assigning...' : 'Assign'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Not Found Modal */}
            {unprovisionedNotFoundModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Mark as Not Found</h3>
                  <p className="text-sm text-gray-500 mb-4">This contact could not be found on LinkedIn. Optionally add a note explaining why.</p>
                  <textarea
                    value={unprovisionedNotFoundNote}
                    onChange={(e) => setUnprovisionedNotFoundNote(e.target.value)}
                    placeholder="e.g., No LinkedIn presence, common name - couldn't confirm identity..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 resize-none"
                  />
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={() => { setUnprovisionedNotFoundModal(null); setUnprovisionedNotFoundNote(''); }}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleMarkNotFound(unprovisionedNotFoundModal, unprovisionedNotFoundNote)}
                      className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg"
                    >
                      Mark Not Found
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Import Actions */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Import LinkedIn Profiles (JSON)</h3>
        <div className="flex flex-wrap gap-3 items-center">
          <label className={`px-4 py-2 text-white text-sm rounded-lg cursor-pointer ${jsonImportLoading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
            {jsonImportLoading ? 'Importing...' : 'Upload JSON File'}
            <input type="file" accept=".json" onChange={handleJsonImport} className="hidden" disabled={jsonImportLoading} />
          </label>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Upload a JSON file containing an array of LinkedIn profile objects.
        </p>
        {jsonImportLoading && (
          <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center gap-2 text-sm text-indigo-700">
              <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Processing <strong>{jsonImportProfileCount}</strong> profiles from <strong>{jsonImportFileName}</strong>...</span>
            </div>
            <div className="mt-2 w-full bg-indigo-200 rounded-full h-2">
              <div className="bg-indigo-600 h-2 rounded-full animate-pulse" style={{width: '100%'}}></div>
            </div>
            <p className="text-xs text-indigo-500 mt-1">This may take a while for large files. Please don't navigate away.</p>
          </div>
        )}
        {jsonImportResult && !jsonImportLoading && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
            <div className="flex items-center gap-2 mb-2">
              <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
              <span className="font-medium text-gray-700">Import Complete</span>
            </div>
            <div className="flex gap-4 flex-wrap">
              <span className="text-gray-700">Total: <strong>{jsonImportResult.total_items}</strong></span>
              <span className="text-gray-700">Successful: <strong className="text-green-600">{jsonImportResult.successful_count}</strong></span>
              <span className="text-gray-700">Failed: <strong className={jsonImportResult.failed_count > 0 ? 'text-red-600' : 'text-gray-500'}>{jsonImportResult.failed_count}</strong></span>
              {jsonImportResult.skipped_count > 0 && (
                <span className="text-gray-700">Unchanged: <strong className="text-gray-500">{jsonImportResult.skipped_count}</strong></span>
              )}
            </div>
            {jsonImportResult.failed_items && jsonImportResult.failed_items.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800 font-medium">
                  View {jsonImportResult.failed_items.length} failed item{jsonImportResult.failed_items.length !== 1 ? 's' : ''}
                </summary>
                <div className="mt-2 max-h-48 overflow-y-auto bg-red-50 rounded border border-red-200 p-2">
                  {jsonImportResult.failed_items.map((item, idx) => (
                    <div key={idx} className="text-xs text-red-700 py-1 border-b border-red-100 last:border-0">
                      <span className="font-mono font-medium">Item #{item.index}:</span> {item.error}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Original: Import Contacts CSV (exec_person_contacts schema) */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Import Contacts CSV (Original Schema)</h3>
        <p className="text-xs text-gray-500 mb-3">
          Upload a CSV with the original exec_person_contacts schema (School, First Name, Last Name, LinkedIn, Email, Address, etc.).
          Records will be auto-matched against existing LinkedIn profiles.
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <label className={`px-4 py-2 text-white text-sm rounded-lg cursor-pointer ${contactImportLoading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {contactImportLoading ? 'Importing...' : 'Upload Contact CSV'}
            <input type="file" accept=".csv" onChange={handleContactCsvImport} className="hidden" disabled={contactImportLoading} />
          </label>
          {contactImportLoading && !importProgress && <LoadingSpinner />}
        </div>
        {contactImportLoading && importProgress && importProgress.job_type === 'contact_csv' && <ImportProgressBar />}
        {contactImportResult && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
            <div className="flex gap-4 flex-wrap">
              <span className="text-gray-700">Imported: <strong className="text-green-600">{contactImportResult.imported}</strong></span>
              <span className="text-gray-700">Errors: <strong className={contactImportResult.errors > 0 ? 'text-red-600' : 'text-gray-500'}>{contactImportResult.errors}</strong></span>
            </div>
            {contactImportResult.matching && (
              <div className="flex gap-4 flex-wrap mt-2">
                <span className="text-gray-600">Auto-matched: <strong className="text-indigo-600">{contactImportResult.matching.auto_matched}</strong></span>
                <span className="text-gray-600">Review needed: <strong className="text-amber-600">{contactImportResult.matching.review_needed}</strong></span>
                <span className="text-gray-600">No match: <strong className="text-gray-500">{contactImportResult.matching.no_match}</strong></span>
              </div>
            )}
            {contactImportResult.error_details && contactImportResult.error_details.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-red-600 cursor-pointer">View error details</summary>
                <ul className="mt-1 text-xs text-red-500 space-y-1 max-h-40 overflow-y-auto">
                  {contactImportResult.error_details.map((e, i) => (
                    <li key={i}>Row {e.row}: {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Import Alumni Contacts CSV - Multi-Source */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Import Alumni Database (CSV)</h3>
        <p className="text-xs text-gray-500 mb-4">
          Import alumni contact data with automatic matching to <strong>existing exec_person_contacts</strong>. Supports multiple CSV formats.
        </p>

        {/* Step 1: Select Source Type */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-2">1. Select Database Type</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {SOURCE_TYPES.map(st => (
              <button
                key={st.key}
                onClick={() => handleAlumniSourceTypeChange(st.key)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  alumniImportSourceType === st.key
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="font-medium text-sm text-gray-800">{st.name}</div>
                <div className="text-xs text-gray-500 mt-1">{st.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Upload File */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-2">2. Upload CSV File</label>
          <div className="flex flex-wrap gap-3 items-center">
            <label className={`px-4 py-2 text-white text-sm rounded-lg cursor-pointer ${
              alumniImportLoading ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}>
              {alumniImportFileName ? 'Change File' : 'Select CSV File'}
              <input
                type="file"
                accept=".csv"
                onChange={handleAlumniFileSelect}
                className="hidden"
                disabled={alumniImportLoading}
              />
            </label>
            {alumniImportFileName && (
              <span className="text-sm text-gray-600">
                Selected: <strong>{alumniImportFileName}</strong>
              </span>
            )}
            {alumniImportLoading && !importProgress && <LoadingSpinner />}
          </div>
          {alumniImportLoading && importProgress && importProgress.job_type === 'alumni_csv' && <ImportProgressBar />}
          {/* Status message - what's needed next */}
          {alumniImportFileName && !alumniImportSourceType && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
              ⚠️ Please select a database type above (Step 1) to continue.
            </div>
          )}
          {alumniImportFileName && alumniImportSourceType && !alumniImportPreview && !alumniImportLoading && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              Preview failed to load. Check the error message above or try selecting the source type again.
            </div>
          )}
        </div>

        {/* Preview Results */}
        {alumniImportPreview && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Column Mapping Preview</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
              {Object.entries(alumniImportPreview.mapped_columns || {}).slice(0, 8).map(([csv, std]) => (
                <div key={csv} className="bg-white p-2 rounded border">
                  <div className="text-gray-500 truncate">{csv}</div>
                  <div className="text-emerald-600 font-medium">→ {std}</div>
                </div>
              ))}
            </div>
            {alumniImportPreview.unmapped_columns?.length > 0 && (
              <details className="text-xs">
                <summary className="text-gray-500 cursor-pointer">
                  {alumniImportPreview.unmapped_columns.length} unmapped columns (ignored)
                </summary>
                <div className="mt-1 text-gray-400">
                  {alumniImportPreview.unmapped_columns.join(', ')}
                </div>
              </details>
            )}
            {alumniImportPreview.sample_rows?.length > 0 && (
              <details className="text-xs mt-2">
                <summary className="text-gray-600 cursor-pointer font-medium">
                  Preview first {alumniImportPreview.sample_rows.length} rows
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-2 py-1 text-left">Name</th>
                        <th className="px-2 py-1 text-left">Email</th>
                        <th className="px-2 py-1 text-left">University</th>
                        <th className="px-2 py-1 text-left">Grad Year</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alumniImportPreview.sample_rows.map((row, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-1">{row.normalized.full_name || `${row.normalized.first_name} ${row.normalized.last_name}`}</td>
                          <td className="px-2 py-1 text-blue-600">{row.normalized.email || '—'}</td>
                          <td className="px-2 py-1">{row.normalized.university || '—'}</td>
                          <td className="px-2 py-1">{row.normalized.graduation_year || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}

        {/* Step 3: Import Options - show when file and source type are selected */}
        {alumniImportFile && alumniImportSourceType && !alumniImportLoading && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-2">3. Import Options</label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={alumniImportOptions.updateExisting}
                  onChange={(e) => setAlumniImportOptions(prev => ({ ...prev, updateExisting: e.target.checked }))}
                  className="rounded text-emerald-600"
                />
                <span>Update existing records with email</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={alumniImportOptions.createNew}
                  onChange={(e) => setAlumniImportOptions(prev => ({ ...prev, createNew: e.target.checked }))}
                  className="rounded text-emerald-600"
                />
                <span>Create new records for unmatched</span>
              </label>
            </div>

            {/* Low-confidence matching option */}
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={alumniImportOptions.includeLowConfidence}
                  onChange={(e) => setAlumniImportOptions(prev => ({ ...prev, includeLowConfidence: e.target.checked }))}
                  className="rounded text-amber-600 mt-0.5"
                />
                <div>
                  <span className="font-medium text-amber-800">Include name-only matches (55% confidence)</span>
                  <p className="text-xs text-amber-700 mt-1">
                    By default, only high-confidence matches (≥80%) are auto-applied. Enable this to include
                    <strong> all matches</strong> including name-only (first + last name). Use this if you trust that
                    names in your CSV are unique enough to match correctly.
                  </p>
                  <div className="text-xs text-amber-600 mt-2">
                    <strong>Confidence tiers:</strong> Name+Uni+Year (95%) → Name+Uni (85%) → Name+Location (75%) →
                    Name+Year (70%) → <span className="bg-amber-200 px-1 rounded">Name-only (55%)</span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Step 4: Run Import */}
        {alumniImportFile && alumniImportSourceType && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-2">4. Run Import</label>
            {!alumniImportPreview && !alumniImportLoading && (
              <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                ⚠️ Column preview not available. You can still run the import, but consider checking if the backend is running.
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => runAlumniImport(true)}
                disabled={alumniImportLoading}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {alumniImportLoading ? 'Processing...' : 'Preview (Dry Run)'}
              </button>
              <button
                onClick={() => runAlumniImport(false)}
                disabled={alumniImportLoading}
                className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {alumniImportLoading ? 'Importing...' : 'Run Import'}
              </button>
            </div>
          </div>
        )}

        {/* Import Results */}
        {alumniImportResult && (
          <div className={`mt-4 p-4 rounded-lg border ${alumniImportResult.dry_run ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center gap-2 mb-3">
              {alumniImportResult.dry_run ? (
                <>
                  <span className="text-amber-600 text-lg">⚡</span>
                  <span className="font-medium text-amber-800">Dry Run Results (No Changes Made)</span>
                </>
              ) : (
                <>
                  <span className="text-green-600 text-lg">✓</span>
                  <span className="font-medium text-green-800">Import Complete</span>
                </>
              )}
            </div>

            {/* Show effective threshold info */}
            {alumniImportResult.include_low_confidence !== undefined && (
              <div className={`text-xs mb-3 p-2 rounded ${alumniImportResult.include_low_confidence ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-700'}`}>
                {alumniImportResult.include_low_confidence ? (
                  <>⚠️ <strong>Low-confidence matching enabled</strong> — all matches including name-only were included (threshold: {Math.round((alumniImportResult.effective_threshold || 0) * 100)}%)</>
                ) : (
                  <>ℹ️ Standard matching — only high-confidence matches (≥80%) were auto-applied</>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
              <div className="bg-white p-2 rounded border">
                <div className="text-gray-500 text-xs">Total Rows</div>
                <div className="font-bold text-lg">{alumniImportResult.total_rows}</div>
              </div>
              <div className="bg-white p-2 rounded border">
                <div className="text-gray-500 text-xs">Matched</div>
                <div className="font-bold text-lg text-blue-600">{alumniImportResult.matched}</div>
              </div>
              <div className="bg-white p-2 rounded border">
                <div className="text-gray-500 text-xs">Updated</div>
                <div className="font-bold text-lg text-green-600">{alumniImportResult.updated}</div>
              </div>
              <div className="bg-white p-2 rounded border">
                <div className="text-gray-500 text-xs">Created</div>
                <div className="font-bold text-lg text-purple-600">{alumniImportResult.created}</div>
              </div>
            </div>

            {/* Match breakdown by tier */}
            {alumniImportResult.matches_by_tier && Object.keys(alumniImportResult.matches_by_tier).length > 0 && (
              <details className="text-xs mb-2" open>
                <summary className="text-gray-600 cursor-pointer font-medium">Match breakdown by confidence tier</summary>
                <div className="mt-2 space-y-1">
                  {Object.entries(alumniImportResult.matches_by_tier)
                    .sort((a, b) => {
                      const tierOrder = ['name_university_year', 'name_university', 'name_location', 'name_year', 'name_only'];
                      return tierOrder.indexOf(a[0]) - tierOrder.indexOf(b[0]);
                    })
                    .map(([tier, count]) => {
                      const tierConfidence = {
                        'name_university_year': 95,
                        'name_university': 85,
                        'name_location': 75,
                        'name_year': 70,
                        'name_only': 55,
                      };
                      const confidence = tierConfidence[tier] || 50;
                      const threshold = alumniImportResult.include_low_confidence ? 0 : 80;
                      const isAutoApplied = confidence >= threshold;

                      return (
                        <div key={tier} className={`flex items-center gap-2 px-2 py-1 rounded ${isAutoApplied ? 'bg-green-50 border border-green-200' : 'bg-gray-100 border border-gray-200'}`}>
                          <span className={`font-mono text-xs ${isAutoApplied ? 'text-green-700' : 'text-gray-500'}`}>
                            {confidence}%
                          </span>
                          <span className="text-gray-700">{tier.replace(/_/g, ' ')}:</span>
                          <span className="font-bold">{count}</span>
                          <span className={`text-xs ${isAutoApplied ? 'text-green-600' : 'text-amber-600'}`}>
                            {isAutoApplied ? '✓ auto-applied' : '→ review needed'}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </details>
            )}

            {/* Sample matches */}
            {alumniImportResult.sample_matches?.length > 0 && (
              <details className="text-xs mb-2">
                <summary className="text-blue-600 cursor-pointer font-medium">
                  View sample matches ({alumniImportResult.sample_matches.length}) — emails added to personal_email columns
                </summary>
                <p className="mt-1 text-gray-500 text-xs">
                  Emails are added to first available slot: personal_email → personal_email_2 → personal_email_3 → personal_email_4.
                  Duplicates are ignored.
                </p>
                <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                  {alumniImportResult.sample_matches.map((m, i) => (
                    <div key={i} className="bg-white p-2 rounded border text-xs">
                      <div>
                        <span className="text-gray-600">Row {m.row}:</span>{' '}
                        <span className="font-medium">{m.csv_name}</span>
                        <span className="text-gray-400 mx-2">→</span>
                        <span className="text-green-600">{m.matched_name}</span>
                        <span className="text-gray-400 ml-2">({(m.confidence * 100).toFixed(0)}%)</span>
                      </div>
                      {(m.csv_emails?.length > 0 || m.csv_email) && (
                        <div className="mt-1 text-gray-500 ml-4">
                          <span className="text-purple-600">
                            ← {(m.csv_emails || [m.csv_email]).filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Review needed */}
            {alumniImportResult.review_needed?.length > 0 && (
              <details className="text-xs mb-2" open>
                <summary className="text-amber-600 cursor-pointer font-medium">
                  Low-confidence matches needing review ({alumniImportResult.review_needed.length})
                </summary>

                {/* Explanation */}
                <div className="mt-2 mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-700">
                  <strong>Review these potential matches:</strong> These rows matched existing records by name but with lower confidence.
                  Click <span className="bg-green-100 px-1 rounded">Accept Match</span> to add the email to the first available
                  <code className="bg-purple-100 px-1 rounded mx-1">personal_email</code> slot (duplicates ignored), or
                  <span className="bg-gray-100 px-1 rounded ml-1">Not a Match</span> to skip.
                </div>

                {/* Bulk actions */}
                <div className="mb-3 flex gap-2 items-center flex-wrap">
                  <span className="text-gray-500">Bulk actions:</span>
                  <button
                    onClick={() => resolveAllReviews('accept')}
                    className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    ✓ Accept All Matches
                  </button>
                  <button
                    onClick={() => resolveAllReviews('reject')}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  >
                    ✗ Skip All
                  </button>
                  <span className="text-gray-400 ml-2">
                    ({Object.keys(resolvedReviews).length} of {alumniImportResult.review_needed.length} resolved)
                  </span>
                </div>

                <div className="mt-2 space-y-2 max-h-80 overflow-y-auto">
                  {alumniImportResult.review_needed.map((r, i) => {
                    const key = `${r.row}-${r.potential_match.id}`;
                    const isResolved = resolvedReviews[key];
                    const isLoading = reviewResolutionLoading[key];

                    return (
                      <div
                        key={i}
                        className={`p-3 rounded border text-xs ${
                          isResolved === 'accept' ? 'bg-green-50 border-green-200' :
                          isResolved === 'reject' ? 'bg-gray-100 border-gray-200 opacity-50' :
                          'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            <div className="mb-1">
                              <span className="text-gray-500">Row {r.row}:</span>{' '}
                              <span className="font-medium">{r.csv_data.name}</span>
                              {(r.csv_data.emails?.length > 0 || r.csv_data.email) && (
                                <span className="text-blue-600 ml-1">
                                  ({(r.csv_data.emails || [r.csv_data.email]).filter(Boolean).join(', ')})
                                </span>
                              )}
                            </div>
                            <div className="text-gray-500">
                              <span className="text-gray-400">↳ Potential match:</span>{' '}
                              <span className="text-amber-700 font-medium">{r.potential_match.name}</span>
                              {r.potential_match.email && (
                                <span className="text-gray-500 ml-1">({r.potential_match.email})</span>
                              )}
                              <span className="text-gray-400 ml-2">
                                • {r.potential_match.university || 'No university'}
                                {r.potential_match.graduation_year && ` '${String(r.potential_match.graduation_year).slice(-2)}`}
                              </span>
                            </div>
                            <div className="text-gray-400 mt-1">
                              {(r.confidence * 100).toFixed(0)}% confidence via {r.method}
                              {r.match_count > 1 && <span className="text-amber-600 ml-1">({r.match_count} similar records)</span>}
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div className="flex gap-1 flex-shrink-0">
                            {isResolved ? (
                              <span className={`px-2 py-1 rounded ${
                                isResolved === 'accept' ? 'bg-green-200 text-green-800' :
                                'bg-gray-200 text-gray-600'
                              }`}>
                                {isResolved === 'accept' ? '✓ Matched' : '✗ Skipped'}
                              </span>
                            ) : isLoading ? (
                              <span className="px-2 py-1 text-gray-400">Saving...</span>
                            ) : (
                              <>
                                <button
                                  onClick={() => resolveReviewItem(r, 'accept')}
                                  className="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                  title="Add email to this existing record"
                                >
                                  ✓ Accept Match
                                </button>
                                <button
                                  onClick={() => resolveReviewItem(r, 'reject')}
                                  className="px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                                  title="Not a match - skip this row"
                                >
                                  ✗ Not a Match
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            )}

            {/* Errors */}
            {alumniImportResult.errors?.length > 0 && (
              <details className="text-xs">
                <summary className="text-red-600 cursor-pointer font-medium">
                  Errors ({alumniImportResult.errors.length})
                </summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {alumniImportResult.errors.map((e, i) => (
                    <div key={i} className="text-red-600">Row {e.row}: {e.error}</div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Re-match Contacts */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Re-match Unmatched Contacts</h3>
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={handleRematchContacts}
            disabled={rematchLoading}
            className={`px-4 py-2 text-white text-sm rounded-lg ${rematchLoading ? 'bg-gray-400' : 'bg-amber-600 hover:bg-amber-700'}`}
          >
            {rematchLoading ? 'Matching...' : 'Run Re-match'}
          </button>
          {rematchLoading && <LoadingSpinner />}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Re-runs matching for all unmatched contacts against current LinkedIn profiles.
          Use this after importing new profiles to link previously unmatched contacts.
        </p>
        {rematchLoading && (
          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <svg className="animate-spin h-4 w-4 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Matching contacts against profiles... This may take a while.</span>
            </div>
          </div>
        )}
        {rematchResult && !rematchLoading && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
            <div className="flex items-center gap-2 mb-2">
              <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
              <span className="font-medium text-gray-700">Re-match Complete</span>
            </div>
            <div className="flex gap-4 flex-wrap">
              <span className="text-gray-700">Processed: <strong>{rematchResult.total_processed}</strong></span>
              <span className="text-gray-700">Newly matched: <strong className="text-green-600">{rematchResult.newly_matched}</strong></span>
              <span className="text-gray-700">Review needed: <strong className="text-amber-600">{rematchResult.review_needed}</strong></span>
              <span className="text-gray-700">Still unmatched: <strong className="text-gray-500">{rematchResult.still_unmatched}</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* LinkedIn Followers Import */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
            LinkedIn Followers Import
          </h3>
          <button
            onClick={() => {
              setShowLinkedinFollowersSection(!showLinkedinFollowersSection);
              if (!showLinkedinFollowersSection && !linkedinFollowersStats) {
                fetchLinkedinFollowersStats();
              }
            }}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            {showLinkedinFollowersSection ? 'Hide' : 'Show'}
            <svg className={`h-4 w-4 transition-transform ${showLinkedinFollowersSection ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Import LinkedIn connections export to identify alumni in your network. Matches against <strong>exec_person_contacts</strong> by LinkedIn URL, email, or name+company.
        </p>

        {showLinkedinFollowersSection && (
          <div className="space-y-4">
            {/* Stats */}
            {linkedinFollowersStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{linkedinFollowersStats.total_followers || 0}</div>
                  <div className="text-xs text-gray-500">Total Followers</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{linkedinFollowersStats.matched_followers || 0}</div>
                  <div className="text-xs text-gray-500">Matched</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-600">{linkedinFollowersStats.unmatched_followers || 0}</div>
                  <div className="text-xs text-gray-500">Unmatched</div>
                </div>
                <div className="bg-indigo-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-indigo-600">{linkedinFollowersStats.contacts_marked_as_followers || 0}</div>
                  <div className="text-xs text-gray-500">Contacts Tagged</div>
                </div>
              </div>
            )}

            {/* NEW: Profile Linkage Stats */}
            {linkedinFollowersStats && (linkedinFollowersStats.contacts_with_profiles > 0 || linkedinFollowersStats.contacts_without_profiles > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Profile Linkage Analysis
                  <button
                    onClick={() => setShowLinkageWorkflowModal(true)}
                    className="ml-1 p-1 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-full transition-colors"
                    title="How does this work?"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="bg-white p-3 rounded-lg border border-green-200 text-center">
                    <div className="text-2xl font-bold text-green-600">{linkedinFollowersStats.contacts_with_profiles || 0}</div>
                    <div className="text-xs text-gray-600">With Profiles</div>
                    <div className="text-[10px] text-gray-400 mt-1">Searchable in Executive Search</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-orange-200 text-center">
                    <div className="text-2xl font-bold text-orange-600">{linkedinFollowersStats.contacts_without_profiles || 0}</div>
                    <div className="text-xs text-gray-600">Without Profiles</div>
                    <div className="text-[10px] text-gray-400 mt-1">Need Apify ingestion</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-200 text-center">
                    <div className="text-2xl font-bold text-blue-600">{linkedinFollowersStats.contacts_ready_for_ingestion || 0}</div>
                    <div className="text-xs text-gray-600">Ready for Ingestion</div>
                    <div className="text-[10px] text-gray-400 mt-1">Have LinkedIn URLs</div>
                  </div>
                </div>

                {/* Export Buttons */}
                {linkedinFollowersStats.contacts_ready_for_ingestion > 0 && (
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-amber-200">
                    <span className="text-xs text-amber-700 self-center mr-2">Export LinkedIn URLs:</span>
                    <button
                      onClick={() => downloadUnlinkedLinkedInUrls('txt')}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 flex items-center gap-1"
                    >
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      .txt (URLs only)
                    </button>
                    <button
                      onClick={() => downloadUnlinkedLinkedInUrls('csv')}
                      className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 flex items-center gap-1"
                    >
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      .csv (with names)
                    </button>
                    <span className="text-[10px] text-amber-600 self-center ml-2">
                      Use these URLs with Apify to fetch full profiles
                    </span>
                  </div>
                )}

                {/* Re-link Button - show when there are unlinked followers */}
                {linkedinFollowersStats.contacts_without_profiles > 0 && (
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-amber-200 mt-3">
                    <span className="text-xs text-amber-700 self-center mr-2">After importing new profiles:</span>
                    <button
                      onClick={relinkFollowersToProfiles}
                      disabled={relinkLoading}
                      className={`px-3 py-1.5 text-white text-xs font-medium rounded flex items-center gap-1 ${
                        relinkLoading ? 'bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                    >
                      {relinkLoading ? (
                        <>
                          <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Re-linking...
                        </>
                      ) : (
                        <>
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Re-link to Profiles
                        </>
                      )}
                    </button>
                    <span className="text-[10px] text-amber-600 self-center ml-2">
                      Match unlinked followers to newly imported profiles by LinkedIn URL
                    </span>
                  </div>
                )}

                {/* Relink Result */}
                {relinkResult && (
                  <div className={`mt-3 p-3 rounded-lg border ${
                    relinkResult.stats?.newly_linked > 0
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="text-sm font-medium mb-2">
                      {relinkResult.stats?.newly_linked > 0 ? (
                        <span className="text-green-700">✓ Linked {relinkResult.stats.newly_linked} followers to profiles</span>
                      ) : (
                        <span className="text-gray-600">No new links found</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="text-gray-600">
                        <span className="font-medium">Checked:</span> {relinkResult.stats?.total_unlinked_checked || 0}
                      </div>
                      <div className="text-green-600">
                        <span className="font-medium">Newly Linked:</span> {relinkResult.stats?.newly_linked || 0}
                      </div>
                      <div className="text-orange-600">
                        <span className="font-medium">No Match:</span> {relinkResult.stats?.no_profile_match || 0}
                      </div>
                      <div className="text-gray-500">
                        <span className="font-medium">Missing URL:</span> {relinkResult.stats?.no_linkedin_url || 0}
                      </div>
                    </div>
                    {relinkResult.linked_contacts && relinkResult.linked_contacts.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <div className="text-xs text-gray-600 mb-1">Recently linked:</div>
                        <div className="flex flex-wrap gap-1">
                          {relinkResult.linked_contacts.slice(0, 5).map((c, i) => (
                            <span key={i} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                              {c.contact_name}
                            </span>
                          ))}
                          {relinkResult.linked_contacts.length > 5 && (
                            <span className="text-xs text-gray-500">
                              +{relinkResult.linked_contacts.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Linkage Workflow Modal */}
            {showLinkageWorkflowModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Profile Linkage Workflow
                      </h3>
                      <button
                        onClick={() => setShowLinkageWorkflowModal(false)}
                        className="text-white hover:text-amber-200 transition-colors"
                      >
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">
                      Follow these steps to link your LinkedIn followers to searchable executive profiles:
                    </p>
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                        <div>
                          <div className="font-medium text-gray-800">Import LinkedIn Followers CSV</div>
                          <div className="text-sm text-gray-500">Creates contact records with <code className="bg-gray-100 px-1 rounded">is_linkedin_follower=true</code></div>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                        <div>
                          <div className="font-medium text-gray-800">Import New Profiles via JSON</div>
                          <div className="text-sm text-gray-500">Use the Ingestion tab to import executive profiles from Apify</div>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                        <div>
                          <div className="font-medium text-gray-800">Click "Re-link to Profiles"</div>
                          <div className="text-sm text-gray-500">Matches unlinked follower contacts to newly imported profiles by LinkedIn URL</div>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-sm">4</div>
                        <div>
                          <div className="font-medium text-gray-800">Stats Update Automatically</div>
                          <div className="text-sm text-gray-500">"With Profiles" count increases — these are now searchable in Executive Search</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="text-sm text-amber-800">
                        <strong>Tip:</strong> You don't need to re-import the LinkedIn Followers CSV after adding new profiles. Just click "Re-link to Profiles" to match any previously imported followers to newly imported profiles.
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-6 py-4 flex justify-end">
                    <button
                      onClick={() => setShowLinkageWorkflowModal(false)}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium"
                    >
                      Got it
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* CSV Format Info */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs">
              <div className="font-medium text-blue-800 mb-1">Expected CSV Columns:</div>
              <div className="text-blue-700">
                First Name, Last Name, URL, Email Address, Company, Position, Connected On
              </div>
              <div className="text-blue-600 mt-1">
                Date format: <code className="bg-blue-100 px-1 rounded">28-Jan-26</code>
              </div>
            </div>

            {/* File Upload */}
            <div className="flex flex-wrap gap-3 items-center">
              <label className={`px-4 py-2 text-white text-sm rounded-lg cursor-pointer ${
                linkedinFollowersLoading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
              }`}>
                {linkedinFollowersFileName ? 'Change File' : 'Select CSV File'}
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleLinkedinFollowersFileSelect}
                  className="hidden"
                  disabled={linkedinFollowersLoading}
                />
              </label>
              {linkedinFollowersFileName && (
                <span className="text-sm text-gray-600">
                  Selected: <strong>{linkedinFollowersFileName}</strong>
                </span>
              )}
            </div>

            {/* Import Button */}
            {linkedinFollowersFile && (
              <div className="flex gap-3">
                <button
                  onClick={runLinkedinFollowersImport}
                  disabled={linkedinFollowersLoading}
                  className={`px-4 py-2 text-white text-sm font-medium rounded-lg ${
                    linkedinFollowersLoading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {linkedinFollowersLoading ? 'Importing...' : 'Import & Match'}
                </button>
                {linkedinFollowersLoading && !importProgress && <LoadingSpinner />}
              </div>
            )}
            {linkedinFollowersLoading && importProgress && importProgress.job_type === 'linkedin_followers' && <ImportProgressBar />}

            {/* Results */}
            {linkedinFollowersResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium text-green-800">Import Complete</span>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <div className="text-center">
                    <div className="font-bold text-lg text-gray-800">{linkedinFollowersResult.total_rows}</div>
                    <div className="text-xs text-gray-500">Total Rows</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg text-blue-600">{linkedinFollowersResult.new_followers}</div>
                    <div className="text-xs text-gray-500">New Followers</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg text-indigo-600">{linkedinFollowersResult.updated_followers}</div>
                    <div className="text-xs text-gray-500">Updated</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg text-green-600">{linkedinFollowersResult.matched_contacts}</div>
                    <div className="text-xs text-gray-500">Matched</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg text-gray-500">{linkedinFollowersResult.unmatched}</div>
                    <div className="text-xs text-gray-500">Unmatched</div>
                  </div>
                </div>

                {/* Match Method Breakdown */}
                {linkedinFollowersResult.matches_by_method && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-600 mb-2">Matches by Method:</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(linkedinFollowersResult.matches_by_method)
                        .filter(([method, count]) => method !== 'unmatched' && count > 0)
                        .map(([method, count]) => (
                          <span key={method} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            {method.replace('_', ' ')}: {count}
                          </span>
                        ))
                      }
                    </div>
                  </div>
                )}

                {/* Download Export Button */}
                {linkedinFollowersResult.export_csv && (
                  <button
                    onClick={downloadLinkedinFollowersExport}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Results CSV (with Match Status)
                  </button>
                )}

                {/* Errors */}
                {linkedinFollowersResult.error_count > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs text-red-600 cursor-pointer">
                      {linkedinFollowersResult.error_count} error{linkedinFollowersResult.error_count !== 1 ? 's' : ''} occurred
                    </summary>
                    <div className="mt-2 text-xs text-red-600 space-y-1">
                      {linkedinFollowersResult.errors?.map((e, i) => (
                        <div key={i}>Row {e.row}: {e.error}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* De-duplication Info Modal */}
      {showDedupInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowDedupInfoModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-bold text-gray-800">How the De-duplication Tool Works</h2>
              <button onClick={() => setShowDedupInfoModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-6 text-sm text-gray-700">

              {/* Overview */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">Overview</h3>
                <p className="text-gray-600 leading-relaxed">
                  The dedup tool finds contacts that appear more than once in the database and merges them into a single, consolidated record. It preserves all unique data (emails, phone, address, executive links) and maintains a full audit trail of every merge.
                </p>
              </div>

              {/* Detection Methods */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Duplicate Detection Methods</h3>
                <div className="space-y-2">
                  <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
                    <div className="font-medium text-rose-700 text-xs uppercase tracking-wide mb-1">Name Match</div>
                    <p className="text-gray-600 text-xs">Exact match on first name + last name (case-insensitive, trimmed). Two contacts named "John Smith" and "john smith" are grouped together.</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <div className="font-medium text-amber-700 text-xs uppercase tracking-wide mb-1">Email Match</div>
                    <p className="text-gray-600 text-xs">Checks all 5 email fields (primary email, personal_email, personal_email_2, _3, _4) across every contact. If any email appears on two different contacts, they are grouped as potential duplicates.</p>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                    <div className="font-medium text-indigo-700 text-xs uppercase tracking-wide mb-1">All</div>
                    <p className="text-gray-600 text-xs">Runs both Name and Email detection, then removes overlapping groups (same set of contacts found by both methods).</p>
                  </div>
                </div>
              </div>

              {/* Fullness Scoring */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Fullness Scoring Protocol</h3>
                <p className="text-gray-600 text-xs mb-2">
                  Each contact is scored based on how many fields are populated. The fullest record is recommended as the primary (surviving) record during merge. Points are awarded as follows:
                </p>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left px-3 py-2 font-medium text-gray-600">Field</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-600">Points</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr><td className="px-3 py-1.5">First name + Last name (both present)</td><td className="text-right px-3 py-1.5 font-medium">+2</td></tr>
                      <tr><td className="px-3 py-1.5">Each email field populated (up to 5)</td><td className="text-right px-3 py-1.5 font-medium">+1 each</td></tr>
                      <tr><td className="px-3 py-1.5">Phone number</td><td className="text-right px-3 py-1.5 font-medium">+1</td></tr>
                      <tr><td className="px-3 py-1.5">Street address</td><td className="text-right px-3 py-1.5 font-medium">+1</td></tr>
                      <tr><td className="px-3 py-1.5">City + State (both present)</td><td className="text-right px-3 py-1.5 font-medium">+1</td></tr>
                      <tr><td className="px-3 py-1.5">University</td><td className="text-right px-3 py-1.5 font-medium">+1</td></tr>
                      <tr><td className="px-3 py-1.5">Graduation year</td><td className="text-right px-3 py-1.5 font-medium">+1</td></tr>
                      <tr><td className="px-3 py-1.5">Current company</td><td className="text-right px-3 py-1.5 font-medium">+1</td></tr>
                      <tr><td className="px-3 py-1.5">LinkedIn URL</td><td className="text-right px-3 py-1.5 font-medium">+1</td></tr>
                      <tr><td className="px-3 py-1.5">Is LinkedIn follower</td><td className="text-right px-3 py-1.5 font-medium">+1</td></tr>
                      <tr><td className="px-3 py-1.5">Linked to Executive Person record</td><td className="text-right px-3 py-1.5 font-medium text-indigo-600">+2</td></tr>
                      <tr><td className="px-3 py-1.5">Linked to Executive Profile</td><td className="text-right px-3 py-1.5 font-medium text-indigo-600">+2</td></tr>
                      <tr className="bg-gray-50 font-medium"><td className="px-3 py-2">Maximum possible score</td><td className="text-right px-3 py-2">18</td></tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-gray-500">Score badges:</span>
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs">10+ pts</span>
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">5-9 pts</span>
                  <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-xs">&lt;5 pts</span>
                </div>
              </div>

              {/* Merge Behavior */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">What Happens During a Merge</h3>
                <p className="text-gray-600 text-xs mb-2">
                  The primary record is kept. All unique data from duplicates is consolidated onto it, then the duplicates are deleted.
                </p>
                <ul className="text-xs text-gray-600 space-y-1.5 list-none">
                  <li className="flex items-start gap-2">
                    <span className="text-rose-500 mt-0.5">&#9679;</span>
                    <span><strong>Emails</strong> &mdash; All unique emails from duplicates are added to available personal_email slots on the primary. Already-present emails are skipped.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-rose-500 mt-0.5">&#9679;</span>
                    <span><strong>Phone, address, company, university, LinkedIn URL</strong> &mdash; Filled into the primary only if that field is currently empty ("fill empty" strategy). Existing values are never overwritten.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-rose-500 mt-0.5">&#9679;</span>
                    <span><strong>Executive Person / Profile links</strong> &mdash; If the primary has no link and the duplicate does, the link is transferred. If both have different links and they share the same LinkedIn URL, the system picks the Executive Person with more associated profiles. Otherwise the conflict is logged for manual review.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-rose-500 mt-0.5">&#9679;</span>
                    <span><strong>LinkedIn Followers</strong> &mdash; Any follower records pointing to the duplicate are relinked to the primary before deletion, so no follower matches are lost.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-rose-500 mt-0.5">&#9679;</span>
                    <span><strong>Audit trail</strong> &mdash; Every merge is recorded in the primary's <code className="bg-gray-100 px-1 rounded">additional_data.merge_history</code> with: who merged, when, which IDs were merged, which emails/fields were added, and any conflicts.</span>
                  </li>
                </ul>
              </div>

              {/* Auto-Merge */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Auto-Merge All</h3>
                <p className="text-gray-600 text-xs leading-relaxed">
                  The "Auto-Merge All" button processes all duplicate groups for the selected match type in a single batch. For each group, the system automatically selects the highest-scoring record as primary, merges all others into it, and deletes the duplicates. A dry-run preview is always shown first so you can review the impact before confirming. Groups previously marked as "Not Duplicates" are excluded.
                </p>
              </div>

              {/* Not Duplicates */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">"Not Duplicates" Feature</h3>
                <p className="text-gray-600 text-xs leading-relaxed">
                  If two contacts share a name or email but are genuinely different people, click "Not Duplicates" to exclude them from future scans. This marking is stored on each contact and persists across sessions. Groups where all pairs have been dismissed will no longer appear in results.
                </p>
              </div>

            </div>
            <div className="border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowDedupInfoModal(false)}
                className="px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* De-duplication Tool */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <svg className="h-4 w-4 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            De-duplication Tool
            <button
              onClick={() => setShowDedupInfoModal(true)}
              className="text-gray-400 hover:text-rose-500 transition-colors"
              title="How does dedup work?"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </h3>
          <button
            onClick={() => {
              setShowDedupSection(!showDedupSection);
              if (!showDedupSection && !dedupStats) {
                fetchDedupStats();
              }
            }}
            className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1"
          >
            {showDedupSection ? 'Hide' : 'Show'}
            <svg className={`h-4 w-4 transition-transform ${showDedupSection ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Find and merge duplicate contacts. Records are sorted by completeness — the fullest record is recommended as primary.
        </p>

        {showDedupSection && (
          <div className="space-y-4">
            {/* Stats */}
            {dedupStatsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <LoadingSpinner /> Loading statistics...
              </div>
            ) : dedupStats && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-800">{dedupStats.total_contacts?.toLocaleString() || 0}</div>
                  <div className="text-xs text-gray-500">Total Contacts</div>
                </div>
                <div className="bg-rose-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-rose-600">{dedupStats.name_duplicate_groups || 0}</div>
                  <div className="text-xs text-gray-500">Name Match Groups</div>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-600">{dedupStats.email_duplicate_groups || 0}</div>
                  <div className="text-xs text-gray-500">Email Match Groups</div>
                </div>
              </div>
            )}

            {/* Search Type Toggle + Actions */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-4">
                <span className="text-xs font-medium text-gray-600">Search by:</span>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  {['name', 'email', 'all'].map(type => (
                    <button
                      key={type}
                      onClick={() => handleDedupSearchTypeChange(type)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors capitalize ${
                        dedupSearchType === type ? 'bg-white text-rose-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => fetchDedupGroups(1, dedupSearchType)}
                  disabled={dedupGroupsLoading}
                  className="px-3 py-1 text-xs bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:bg-gray-400"
                >
                  {dedupGroupsLoading ? 'Loading...' : 'Find Duplicates'}
                </button>
              </div>
              <button
                onClick={handleAutoMergePreview}
                disabled={autoMergeLoading}
                className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 flex items-center gap-1"
              >
                {autoMergeLoading ? 'Processing...' : 'Auto-Merge All'}
              </button>
            </div>

            {/* Auto-Merge Preview */}
            {autoMergePreview && (
              <div className="border border-indigo-200 rounded-lg bg-indigo-50 p-4">
                <h4 className="text-sm font-semibold text-indigo-800 mb-2">Auto-Merge Preview (Dry Run)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <div className="text-center">
                    <div className="text-xl font-bold text-indigo-700">{autoMergePreview.groups_found}</div>
                    <div className="text-xs text-gray-500">Groups Found</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-indigo-700">{autoMergePreview.records_merged}</div>
                    <div className="text-xs text-gray-500">Records to Merge</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-indigo-700">{autoMergePreview.emails_consolidated}</div>
                    <div className="text-xs text-gray-500">Emails to Consolidate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-indigo-700">{autoMergePreview.linkedin_followers_relinked}</div>
                    <div className="text-xs text-gray-500">Followers to Relink</div>
                  </div>
                </div>
                {autoMergePreview.merge_conflicts > 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded mb-3">
                    {autoMergePreview.merge_conflicts} exec link conflict(s) will be skipped (require manual review)
                  </div>
                )}
                {autoMergePreview.details && autoMergePreview.details.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs font-medium text-gray-600 mb-1">Sample merges:</div>
                    <div className="space-y-1">
                      {autoMergePreview.details.slice(0, 5).map((d, i) => (
                        <div key={i} className="text-xs text-gray-600 bg-white p-1.5 rounded">
                          <span className="font-medium">{d.primary_name}</span>
                          <span className="text-gray-400"> (score {d.primary_fullness})</span>
                          <span className="text-gray-400"> — merging {d.duplicates_merged} duplicate{d.duplicates_merged !== 1 ? 's' : ''}</span>
                          {d.emails_added > 0 && <span className="text-indigo-600"> +{d.emails_added} emails</span>}
                          {d.fields_filled > 0 && <span className="text-emerald-600"> +{d.fields_filled} fields</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleAutoMergeExecute}
                    disabled={autoMergeLoading}
                    className="px-4 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 font-medium"
                  >
                    {autoMergeLoading ? 'Merging...' : `Confirm: Merge ${autoMergePreview.groups_found} Groups`}
                  </button>
                  <button
                    onClick={() => setAutoMergePreview(null)}
                    className="px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Auto-Merge Result */}
            {autoMergeResult && (
              <div className="border border-emerald-200 rounded-lg bg-emerald-50 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-emerald-800">Auto-Merge Complete</h4>
                  <button onClick={() => setAutoMergeResult(null)} className="text-xs text-gray-500 hover:text-gray-700">Dismiss</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center">
                    <div className="text-xl font-bold text-emerald-700">{autoMergeResult.groups_processed}</div>
                    <div className="text-xs text-gray-500">Groups Merged</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-emerald-700">{autoMergeResult.records_merged}</div>
                    <div className="text-xs text-gray-500">Records Removed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-emerald-700">{autoMergeResult.emails_consolidated}</div>
                    <div className="text-xs text-gray-500">Emails Consolidated</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-emerald-700">{autoMergeResult.linkedin_followers_relinked}</div>
                    <div className="text-xs text-gray-500">Followers Relinked</div>
                  </div>
                </div>
                {autoMergeResult.groups_remaining > 0 && (
                  <div className="text-xs text-amber-700 mt-2">
                    {autoMergeResult.groups_remaining} group(s) remaining. Run again to process more.
                  </div>
                )}
              </div>
            )}

            {/* Duplicate Groups List */}
            {dedupGroupsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
                <LoadingSpinner /> Searching for duplicates...
              </div>
            ) : dedupGroups.length > 0 ? (
              <div className="space-y-3">
                <div className="text-xs text-gray-500">
                  Found {dedupTotal} potential duplicate group{dedupTotal !== 1 ? 's' : ''} (showing page {dedupPage})
                </div>
                {dedupGroups.map((group, groupIdx) => {
                  const groupIds = group.records.map(c => c.id);
                  const mergeKey = groupIds.join(',');
                  const isLoading = dedupMergeLoading[mergeKey];
                  const maxFullness = Math.max(...group.records.map(r => r.fullness_score || 0));

                  return (
                    <div key={groupIdx} className="border border-rose-200 rounded-lg bg-rose-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-rose-700">
                          {group.match_type === 'name' ? '👤 Name Match' : '📧 Email Match'}: {group.match_key}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleNotDuplicate(groupIds)}
                            disabled={isLoading}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            Not Duplicates
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {group.records.map((record, idx) => {
                          const otherIds = groupIds.filter(id => id !== record.id);
                          const recordMergeKey = `${record.id}-${otherIds.join(',')}`;
                          const isMerging = dedupMergeLoading[recordMergeKey];
                          const displayName = record.full_name || `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'No name';
                          const isFullest = record.fullness_score != null && record.fullness_score === maxFullness && idx === 0;

                          return (
                            <div key={record.id} className={`bg-white p-2 rounded border ${isFullest ? 'border-emerald-300' : 'border-gray-200'} flex items-start justify-between`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-gray-800 truncate">{displayName}</span>
                                  {record.source_system && (
                                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{record.source_system}</span>
                                  )}
                                  {record.fullness_score != null && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      record.fullness_score >= 10 ? 'bg-emerald-100 text-emerald-700' :
                                      record.fullness_score >= 5 ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-500'
                                    }`}>
                                      {record.fullness_score} pts
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 space-y-0.5 mt-1">
                                  {record.email && <div>📧 {record.email}</div>}
                                  {record.personal_email && <div>📧 Personal: {record.personal_email}</div>}
                                  {record.personal_email_2 && <div>📧 Personal 2: {record.personal_email_2}</div>}
                                  {record.university && <div>🎓 {record.university}{record.graduation_year ? ` (${record.graduation_year})` : ''}</div>}
                                  {(record.city || record.state) && <div>📍 {[record.city, record.state].filter(Boolean).join(', ')}</div>}
                                  {record.phone && <div>📞 {record.phone}</div>}
                                </div>
                              </div>
                              {idx === 0 ? (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full whitespace-nowrap">
                                  {isFullest ? 'Recommended Primary' : 'Primary'}
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleMergeDuplicates(group.records[0].id, [record.id])}
                                  disabled={isMerging}
                                  className="px-2 py-1 text-xs bg-rose-100 text-rose-700 rounded hover:bg-rose-200 disabled:opacity-50 flex items-center gap-1"
                                >
                                  {isMerging ? (
                                    <>Merging...</>
                                  ) : (
                                    <>Merge into primary</>
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {group.records.length > 2 && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={() => handleMergeDuplicates(group.records[0].id, group.records.slice(1).map(c => c.id))}
                            disabled={isLoading}
                            className="px-3 py-1 text-xs bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50"
                          >
                            Merge All into Primary
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Pagination */}
                {dedupTotal > 20 && (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-gray-500">Page {dedupPage} of {Math.ceil(dedupTotal / 20)}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => fetchDedupGroups(dedupPage - 1, dedupSearchType)}
                        disabled={dedupPage <= 1 || dedupGroupsLoading}
                        className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => fetchDedupGroups(dedupPage + 1, dedupSearchType)}
                        disabled={dedupPage >= Math.ceil(dedupTotal / 20) || dedupGroupsLoading}
                        className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : dedupGroups.length === 0 && !dedupGroupsLoading && (
              <div className="text-center py-8 text-gray-400 text-sm">
                Click "Find Duplicates" to search for potential duplicate contacts.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Batch List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-700">
            Import Batches {batchTotal > 0 && <span className="text-gray-400 font-normal">({batchTotal})</span>}
          </h3>
          <button
            onClick={() => fetchBatches(batchPage)}
            className="text-xs text-indigo-600 hover:text-indigo-800"
          >
            Refresh
          </button>
        </div>
        {batchesLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Batch ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Source</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Total</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Success</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Failed</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {batches.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{(b.batch_id || b.id)?.toString().substring(0, 12)}...</td>
                    <td className="px-4 py-2">{b.source || '—'}</td>
                    <td className="px-4 py-2"><StatusBadge status={b.status} /></td>
                    <td className="px-4 py-2">{b.total_items}</td>
                    <td className="px-4 py-2 text-green-600">{b.successful_count}</td>
                    <td className="px-4 py-2 text-red-600">{b.failed_count}</td>
                    <td className="px-4 py-2 text-gray-500">{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {batches.length === 0 && (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">No import batches yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {batchTotal > 20 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">Page {batchPage} of {Math.ceil(batchTotal / 20)}</span>
            <div className="flex gap-2">
              <button onClick={() => fetchBatches(batchPage - 1)} disabled={batchPage <= 1} className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Previous</button>
              <button onClick={() => fetchBatches(batchPage + 1)} disabled={batchPage >= Math.ceil(batchTotal / 20)} className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // REVIEWS TAB RENDER
  // ============================================
  const renderReviews = () => (
    <div className="space-y-4">
      {/* Filter */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex gap-3 items-center">
        <label className="text-sm font-medium text-gray-600">Status:</label>
        <select
          value={reviewStatusFilter}
          onChange={(e) => { setReviewStatusFilter(e.target.value); setTimeout(() => fetchReviews(1), 0); }}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm"
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <span className="text-xs text-gray-400 ml-2">
          {reviewTotal} review{reviewTotal !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Review List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        {reviewsLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="divide-y divide-gray-200">
            {reviews.map((review) => (
              <div key={review.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {review.title || review.role_title || 'Role Review'}
                      </span>
                      <StatusBadge status={review.status} />
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Profile: <span className="font-mono text-xs">{review.exec_profile_id?.substring(0, 8)}...</span>
                      {review.company_name && <span> | {review.company_name}</span>}
                    </div>
                    {review.reason && (
                      <div className="text-xs text-gray-400 mt-1">{review.reason}</div>
                    )}
                  </div>
                  {review.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => resolveReview(review.id, 'approve')}
                        className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => resolveReview(review.id, 'reject')}
                        className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {reviews.length === 0 && (
              <div className="p-8 text-center text-gray-400">No reviews in this category</div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // SCORING TAB RENDER
  // ============================================
  const renderScoring = () => (
    <div className="space-y-4">
      {/* Scoring Enable Toggle */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">AI Leadership Scoring</h3>
            <p className="text-xs text-gray-400 mt-1">
              Uses Amazon Bedrock (Claude) to score executive leadership trajectories. Incurs per-profile costs.
            </p>
          </div>
          <button
            onClick={toggleScoringEnabled}
            disabled={scoringEnabledLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              scoringEnabled ? 'bg-purple-600' : 'bg-gray-300'
            } ${scoringEnabledLoading ? 'opacity-50' : ''}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              scoringEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
        <div className={`mt-2 text-xs font-medium ${scoringEnabled ? 'text-green-600' : 'text-gray-400'}`}>
          {scoringEnabled ? 'Scoring enabled' : 'Scoring disabled — enable to run scoring'}
        </div>
      </div>

      {/* Scoring Controls */}
      <div className={`bg-white border border-gray-200 rounded-lg shadow-sm p-4 ${!scoringEnabled ? 'opacity-50' : ''}`}>
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Run Scoring</h3>
            <p className="text-xs text-gray-400 mt-1">
              {scoringQueueTotal} profile{scoringQueueTotal !== 1 ? 's' : ''} in scoring queue
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => triggerScoring(false)}
              disabled={scoringRunning || !scoringEnabled}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {scoringRunning ? 'Running...' : 'Score New Profiles'}
            </button>
            <button
              onClick={() => triggerScoring(true)}
              disabled={scoringRunning || !scoringEnabled}
              className="px-4 py-2 border border-purple-300 text-purple-700 text-sm rounded-lg hover:bg-purple-50 disabled:opacity-50"
            >
              Force Rescore All
            </button>
          </div>
        </div>
      </div>

      {/* Scoring Runs History */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-700">Scoring Runs</h3>
          <button
            onClick={fetchScoringRuns}
            className="text-xs text-purple-600 hover:text-purple-800 font-medium"
          >
            Refresh
          </button>
        </div>
        {scoringRunsLoading ? (
          <LoadingSpinner />
        ) : scoringRuns.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">No scoring runs yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Run ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Scored</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Failed</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cost</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Started</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scoringRuns.map((run) => {
                  const duration = run.started_at && run.completed_at
                    ? Math.round((new Date(run.completed_at) - new Date(run.started_at)) / 1000)
                    : null;
                  return (
                    <tr key={run.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-xs">{run.id.substring(0, 8)}...</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          run.status === 'completed' ? 'bg-green-100 text-green-700' :
                          run.status === 'running' ? 'bg-blue-100 text-blue-700' :
                          run.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {run.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono">{run.profiles_scored ?? '—'}</td>
                      <td className="px-4 py-2 font-mono">{run.profiles_failed ?? '—'}</td>
                      <td className="px-4 py-2 font-mono">{run.total_cost_usd != null ? `$${run.total_cost_usd.toFixed(4)}` : '—'}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {duration != null ? (
                          duration >= 60 ? `${Math.floor(duration / 60)}m ${duration % 60}s` : `${duration}s`
                        ) : (run.status === 'running' ? 'in progress...' : '—')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {scoringRuns.length > 0 && scoringRuns[0].error_message && (
          <div className="px-4 py-3 border-t border-gray-200">
            <p className="text-xs text-red-600 font-medium">Latest run errors:</p>
            <p className="text-xs text-red-500 mt-1 whitespace-pre-wrap">{scoringRuns[0].error_message}</p>
          </div>
        )}
      </div>

      {/* Scoring Queue */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">Scoring Queue</h3>
          <p className="text-xs text-gray-400 mt-0.5">Profiles needing scoring (empty = all profiles have active scores)</p>
        </div>
        {scoringQueueLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Profile</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Reason</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Last Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scoringQueue.map((item) => (
                  <tr key={item.profile_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{item.profile_id?.substring(0, 8)}...</td>
                    <td className="px-4 py-2">{item.full_name || '—'}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{item.reason || 'Never scored'}</td>
                    <td className="px-4 py-2">{item.last_score != null ? item.last_score.toFixed(1) : '—'}</td>
                  </tr>
                ))}
                {scoringQueue.length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-400">All profiles have active scores</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // REPORT CONTENT RENDERER
  // ============================================
  const renderReportContent = (reportKey, data) => {
    if (!data) return <div className="text-gray-400 text-center py-4">No data available</div>;

    // Helper to render a simple table
    const SimpleTable = ({ columns, rows, onRowClick }) => (
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col, i) => (
              <th key={i} className="px-3 py-2 text-left text-xs font-medium text-gray-500">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, idx) => (
            <tr key={idx} className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`} onClick={() => onRowClick && onRowClick(row)}>
              {columns.map((col, i) => (
                <td key={i} className={`px-3 py-2 ${col.className || ''}`}>
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="px-3 py-4 text-center text-gray-400">No data</td></tr>
          )}
        </tbody>
      </table>
    );

    // Data Quality Dashboard
    if (reportKey === 'data-quality') {
      const d = data;
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-indigo-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-indigo-700">{d.total_profiles}</div>
              <div className="text-xs text-indigo-600">Total Profiles</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-700">{d.pct_roles_classified || 0}%</div>
              <div className="text-xs text-green-600">Roles Classified</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-700">{d.profiles_with_roles}</div>
              <div className="text-xs text-blue-600">Profiles with Roles</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-purple-700">{d.linked_to_platform}</div>
              <div className="text-xs text-purple-600">Linked to Platform</div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Field Completeness</h4>
            <div className="space-y-2">
              {[
                { label: 'Name', data: d.name_completeness },
                { label: 'Headline', data: d.headline_completeness },
                { label: 'Location', data: d.location_completeness },
                { label: 'Current Company', data: d.current_company_completeness },
                { label: 'Resolved to Person', data: d.resolved_to_person },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-32">{item.label}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className="bg-indigo-600 h-2 rounded-full" style={{ width: `${item.data?.pct || 0}%` }}></div>
                  </div>
                  <span className="text-xs text-gray-500 w-16 text-right">{item.data?.pct || 0}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div><span className="font-semibold">{d.total_roles}</span> <span className="text-gray-500">Total Roles</span></div>
            <div><span className="font-semibold">{d.avg_roles_per_profile?.toFixed(1)}</span> <span className="text-gray-500">Avg Roles/Profile</span></div>
            <div><span className="font-semibold">{d.profiles_with_education}</span> <span className="text-gray-500">w/ Education</span></div>
          </div>
        </div>
      );
    }

    // Executive Talent Map
    if (reportKey === 'talent-map') {
      if (!Array.isArray(data) || data.length === 0) {
        return <div className="text-gray-400 text-center py-4">No talent map data. Run classification first to populate role levels.</div>;
      }
      return (
        <SimpleTable
          columns={[
            { key: 'level', label: 'Level', render: v => <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">{v}</span> },
            { key: 'function', label: 'Function' },
            { key: 'country', label: 'Country' },
            { key: 'people', label: 'People', className: 'font-semibold' },
            { key: 'companies_represented', label: 'Companies' },
          ]}
          rows={data}
        />
      );
    }

    // Board-Ready Pool
    if (reportKey === 'board-ready') {
      if (!Array.isArray(data) || data.length === 0) {
        return <div className="text-gray-400 text-center py-4">No board-ready candidates found.</div>;
      }
      return (
        <div className="space-y-4">
          {/* Definitions Legend */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Readiness Definitions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium whitespace-nowrap">Experienced Director</span>
                <span className="text-gray-600">2+ board seats held</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium whitespace-nowrap">First Board</span>
                <span className="text-gray-600">1 board seat held</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-medium whitespace-nowrap">Board-Ready (C-suite)</span>
                <span className="text-gray-600">No board seats, but 1+ C-suite roles</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium whitespace-nowrap">Developing</span>
                <span className="text-gray-600">Some executive experience, building toward board readiness</span>
              </div>
            </div>
          </div>
          <SimpleTable
            columns={[
              { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
              { key: 'current_company', label: 'Company' },
              { key: 'board_seats', label: 'Board Seats', className: 'text-center' },
              { key: 'csuite_roles', label: 'C-Suite Roles', className: 'text-center' },
              { key: 'board_readiness', label: 'Readiness', render: v => (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  v === 'Experienced Director' ? 'bg-green-100 text-green-800' :
                  v === 'First Board' ? 'bg-blue-100 text-blue-800' :
                  v === 'Board-Ready (C-suite)' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-600'
                }`}>{v}</span>
              )},
            ]}
            rows={data}
            onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
          />
        </div>
      );
    }

    // Functional Leadership Depth Chart
    if (reportKey === 'depth-chart') {
      return (
        <div className="space-y-4">
          {/* About This Report */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">About This Report</h4>
            <p className="text-sm text-blue-700">
              Shows leadership depth by business function (e.g., Engineering, Finance, Marketing).
              Each row displays the count of alumni at each seniority level within that function.
              Requires current roles to have <code className="bg-blue-100 px-1 rounded">role_function</code> and
              <code className="bg-blue-100 px-1 rounded">role_level</code> populated.
            </p>
            <div className="mt-2 text-sm text-blue-700">
              <strong>Level Definitions:</strong>
              <span className="ml-2">C-Suite (CEO, CFO, etc.)</span>
              <span className="mx-1">•</span>
              <span>EVP/SVP (Executive/Senior VP)</span>
              <span className="mx-1">•</span>
              <span>VP</span>
              <span className="mx-1">•</span>
              <span>Director</span>
              <span className="mx-1">•</span>
              <span>Manager</span>
            </div>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">
              No depth chart data available. Ensure current roles have role_function and role_level values set.
            </div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'function', label: 'Function', className: 'font-medium' },
                { key: 'total', label: 'Total', className: 'text-center font-semibold' },
                { key: 'c_suite', label: 'C-Suite', className: 'text-center' },
                { key: 'evp_svp', label: 'EVP/SVP', className: 'text-center' },
                { key: 'vp', label: 'VP', className: 'text-center' },
                { key: 'director', label: 'Director', className: 'text-center' },
                { key: 'manager', label: 'Manager', className: 'text-center' },
              ]}
              rows={data}
            />
          )}
        </div>
      );
    }

    // Company Footprint
    if (reportKey === 'company-footprint') {
      // Handle response format: array (no search) or {items, search_summary} (with search)
      const footprintItems = Array.isArray(data) ? data : (data?.items || []);
      const searchSummary = Array.isArray(data) ? null : (data?.search_summary || null);

      const sortableColumns = [
        { key: 'alumni_count', label: 'Alumni' },
        { key: 'exec_count', label: 'Executive Roles' },
        { key: 'current_exec_count', label: 'Current Executives' },
        { key: 'currently_there', label: 'Total Current Employees' },
      ];
      const toggleSort = (key) => {
        setFootprintSort(prev =>
          prev.key === key
            ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: 'desc' }
        );
      };
      const sortedData = (footprintItems.length > 0 && footprintSort.key)
        ? [...footprintItems].sort((a, b) => {
            const av = a[footprintSort.key] ?? 0;
            const bv = b[footprintSort.key] ?? 0;
            return footprintSort.dir === 'desc' ? bv - av : av - bv;
          })
        : footprintItems;
      const sortArrow = (key) => {
        if (footprintSort.key !== key) return ' ↕';
        return footprintSort.dir === 'desc' ? ' ↓' : ' ↑';
      };
      return (
        <div>
          <details className="mb-3 text-xs text-gray-500">
            <summary className="cursor-pointer text-gray-400 hover:text-gray-600">Data source note</summary>
            <p className="mt-1 p-2 bg-gray-50 rounded text-gray-500 leading-relaxed">
              Company Footprint counts alumni from <strong>exec_roles</strong> (role records from LinkedIn profiles), grouped by exact company name (case-insensitive).
              Alumni Search counts from <strong>exec_person_contacts</strong> (imported contact records) using partial matching (e.g., "IBM" also finds "IBM Consulting") and also checks each contact's stored current company field.
              Counts may differ for three reasons: (1) different data sources — some contacts have no role records and only appear in Alumni Search; (2) different matching — Alumni Search uses partial matching across all company name variants; (3) both tools count all role levels for their main totals, but Company Footprint's "Executive Roles" and "Current Executives" columns filter to VP and above.
            </p>
          </details>
          {/* Search bar */}
          <div className="flex items-center gap-2 mb-3">
            <input
              type="text"
              value={footprintSearch}
              onChange={(e) => setFootprintSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchReport('company-footprint', footprintSearch.trim() ? { search: footprintSearch.trim() } : {}); }}
              placeholder="Search companies... (e.g. IBM)"
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <button
              onClick={() => fetchReport('company-footprint', footprintSearch.trim() ? { search: footprintSearch.trim() } : {})}
              disabled={reportLoading}
              className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 rounded-lg"
            >
              {reportLoading ? 'Searching...' : 'Search'}
            </button>
            {footprintSearch && (
              <button
                onClick={() => { setFootprintSearch(''); fetchReport('company-footprint'); }}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
              >
                Clear
              </button>
            )}
          </div>
          {/* Search summary */}
          {searchSummary && (
            <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="text-sm font-medium text-indigo-800 mb-1">
                Combined totals across {searchSummary.matching_companies} matching company name variants:
              </div>
              <div className="flex gap-6 text-sm text-indigo-700">
                <span><strong>{searchSummary.total_alumni}</strong> Alumni</span>
                <span><strong>{searchSummary.total_exec_roles}</strong> Executive Roles</span>
                <span><strong>{searchSummary.total_current_execs}</strong> Current Executives</span>
                <span><strong>{searchSummary.total_currently_there}</strong> Total Current Employees</span>
              </div>
            </div>
          )}
          {footprintItems.length === 0 ? (
            <div className="text-gray-400 text-center py-4">
              {footprintSearch ? `No companies found matching "${footprintSearch}".` : 'No company footprint data.'}
            </div>
          ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Company</th>
                {sortableColumns.map(col => (
                  <th
                    key={col.key}
                    className="px-3 py-2 text-center text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.label}{sortArrow(col.key)}
                  </th>
                ))}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Levels</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedData.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{row.company ?? '—'}</td>
                  <td className="px-3 py-2 text-center">{row.alumni_count ?? '—'}</td>
                  <td className="px-3 py-2 text-center">{row.exec_count ?? '—'}</td>
                  <td className="px-3 py-2 text-center font-semibold">{row.current_exec_count ?? '—'}</td>
                  <td className="px-3 py-2 text-center">{row.currently_there ?? '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(row.levels_represented || []).slice(0, 3).map((lv, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">{lv}</span>
                      ))}
                      {(row.levels_represented || []).length > 3 && <span className="text-gray-400 text-xs">+{row.levels_represented.length - 3}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      );
    }

    // Industry Concentration
    if (reportKey === 'industry-concentration') {
      return (
        <div className="space-y-4">
          {/* About This Report */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">About This Report</h4>
            <p className="text-sm text-blue-700">
              Shows which industries alumni are leading, with counts of total alumni and senior leaders
              (VP, SVP, EVP, C-Suite) per industry. Requires current roles to have
              <code className="bg-blue-100 px-1 rounded">company_industry</code> populated.
            </p>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">
              No industry data available. Ensure current roles have company_industry values set.
            </div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'industry', label: 'Industry', className: 'font-medium' },
                { key: 'total_alumni', label: 'Total Alumni', className: 'text-center' },
                { key: 'senior_leaders', label: 'Senior Leaders', className: 'text-center font-semibold' },
                { key: 'pct_senior', label: '% Senior', render: v => `${v}%` },
              ]}
              rows={data}
            />
          )}
        </div>
      );
    }

    // Stale Profiles
    if (reportKey === 'stale-profiles') {
      if (!Array.isArray(data) || data.length === 0) {
        return <div className="text-gray-400 text-center py-4">No stale profiles found.</div>;
      }
      return (
        <SimpleTable
          columns={[
            { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
            { key: 'current_title', label: 'Title' },
            { key: 'current_company', label: 'Company' },
            { key: 'days_since_refresh', label: 'Days Stale', className: 'text-center', render: v => (
              <span className={`font-medium ${v > 180 ? 'text-red-600' : v > 90 ? 'text-yellow-600' : 'text-gray-600'}`}>{v}</span>
            )},
            { key: 'score_band', label: 'Band', render: v => v ? <ScoreBadge band={v} /> : '—' },
          ]}
          rows={data}
          onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
        />
      );
    }

    // On the Move
    if (reportKey === 'on-the-move') {
      if (!Array.isArray(data) || data.length === 0) {
        return <div className="text-gray-400 text-center py-4">No recent role changes detected.</div>;
      }
      return (
        <SimpleTable
          columns={[
            { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
            { key: 'event_type', label: 'Event', render: v => (
              <span className={`px-2 py-0.5 rounded text-xs ${v === 'role_added' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {v === 'role_added' ? 'New Role' : 'Role Ended'}
              </span>
            )},
            { key: 'previous_title', label: 'Previous' },
            { key: 'new_title', label: 'New' },
            { key: 'new_company', label: 'Company' },
            { key: 'detected_at', label: 'Detected', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          ]}
          rows={data}
          onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
        />
      );
    }

    // Visibility Spikes
    if (reportKey === 'visibility-spikes') {
      return (
        <div className="space-y-4">
          {/* Report Explanation */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">About This Report</h4>
            <p className="text-sm text-blue-700 mb-2">
              Detects alumni who have gained significant LinkedIn followers between profile snapshots (default: 500+ new followers).
            </p>
            <div className="text-xs text-blue-600 space-y-1">
              <p><strong>Requirements:</strong></p>
              <ul className="list-disc list-inside ml-2">
                <li>Profiles must be imported/refreshed multiple times to create snapshots</li>
                <li>Each snapshot captures follower count at that point in time</li>
                <li>Report compares consecutive snapshots to detect growth</li>
              </ul>
              <p className="mt-2"><strong>Note:</strong> If empty, profiles may only have one snapshot. Run another import later to enable comparison.</p>
            </div>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">No visibility spikes detected.</div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
                { key: 'headline', label: 'Headline' },
                { key: 'follower_count', label: 'Followers', className: 'text-right' },
                { key: 'follower_increase', label: 'Increase', className: 'text-right font-semibold text-green-600', render: v => `+${v?.toLocaleString()}` },
                { key: 'pct_increase', label: '% Change', render: v => v ? `+${v}%` : '—' },
              ]}
              rows={data}
              onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
            />
          )}
        </div>
      );
    }

    // Open to Work
    if (reportKey === 'open-to-work') {
      const { currently_open = [], recently_signaled = [] } = data;
      return (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Currently Open ({currently_open.length})</h4>
            {currently_open.length === 0 ? (
              <div className="text-gray-400 text-sm">No one currently signaling open to work.</div>
            ) : (
              <SimpleTable
                columns={[
                  { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
                  { key: 'headline', label: 'Headline' },
                  { key: 'current_company', label: 'Company' },
                ]}
                rows={currently_open}
                onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
              />
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Recently Signaled (14 days)</h4>
            {recently_signaled.length === 0 ? (
              <div className="text-gray-400 text-sm">No recent signals.</div>
            ) : (
              <SimpleTable
                columns={[
                  { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
                  { key: 'detected_at', label: 'Detected', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                ]}
                rows={recently_signaled}
                onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
              />
            )}
          </div>
        </div>
      );
    }

    // Velocity Leaderboard
    if (reportKey === 'velocity-leaderboard') {
      if (!Array.isArray(data) || data.length === 0) {
        return <div className="text-gray-400 text-center py-4">No velocity data available.</div>;
      }
      return (
        <SimpleTable
          columns={[
            { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
            { key: 'current_title', label: 'Current Title' },
            { key: 'current_company', label: 'Company' },
            { key: 'role_count', label: 'Roles', className: 'text-center' },
            { key: 'career_span_years', label: 'Years', className: 'text-center' },
            { key: 'velocity_score', label: 'Velocity', className: 'text-center font-semibold text-indigo-600', render: v => v?.toFixed(2) },
          ]}
          rows={data}
          onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
        />
      );
    }

    // Trajectory Patterns
    if (reportKey === 'trajectory-patterns') {
      const patternColors = {
        'Portfolio Director': 'bg-purple-100 text-purple-800',
        'Rocket': 'bg-green-100 text-green-800',
        'Builder': 'bg-blue-100 text-blue-800',
        'Transformer': 'bg-orange-100 text-orange-800',
        'Specialist': 'bg-teal-100 text-teal-800',
        'Operator-to-Board': 'bg-indigo-100 text-indigo-800',
        'Standard': 'bg-gray-100 text-gray-600',
      };
      return (
        <div className="space-y-4">
          {/* Definitions Legend */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Pattern Definitions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-medium whitespace-nowrap">Portfolio Director</span>
                <span className="text-gray-600">3+ board seats held</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium whitespace-nowrap">Rocket</span>
                <span className="text-gray-600">2+ C-suite roles at 2 or fewer companies</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium whitespace-nowrap">Builder</span>
                <span className="text-gray-600">Avg tenure &gt;60 months, 3 or fewer companies</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs font-medium whitespace-nowrap">Transformer</span>
                <span className="text-gray-600">Avg tenure &lt;30 months, 5+ companies</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded text-xs font-medium whitespace-nowrap">Specialist</span>
                <span className="text-gray-600">Single function focus, 5+ roles</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded text-xs font-medium whitespace-nowrap">Operator-to-Board</span>
                <span className="text-gray-600">1+ board seats AND 1+ C-suite roles</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium whitespace-nowrap">Standard</span>
                <span className="text-gray-600">Does not match other patterns</span>
              </div>
            </div>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">No trajectory patterns found.</div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
                { key: 'current_title', label: 'Title' },
                { key: 'career_pattern', label: 'Pattern', render: v => (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${patternColors[v] || 'bg-gray-100 text-gray-600'}`}>{v}</span>
                )},
                { key: 'company_count', label: 'Companies', className: 'text-center' },
                { key: 'role_count', label: 'Roles', className: 'text-center' },
                { key: 'avg_tenure_months', label: 'Avg Tenure', render: v => v ? `${v} mo` : '—' },
              ]}
              rows={data}
              onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
            />
          )}
        </div>
      );
    }

    // Succession Timing
    if (reportKey === 'succession-timing') {
      return (
        <div className="space-y-4">
          {/* Definitions Legend */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Likelihood Definitions</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium whitespace-nowrap">Overdue</span>
                <span className="text-gray-600">Tenure exceeds 75th percentile for their level</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs font-medium whitespace-nowrap">Approaching</span>
                <span className="text-gray-600">Tenure exceeds median for their level</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium whitespace-nowrap">Settled</span>
                <span className="text-gray-600">Tenure at or below median for their level</span>
              </div>
            </div>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">No succession timing data.</div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
                { key: 'title', label: 'Title' },
                { key: 'company', label: 'Company' },
                { key: 'level', label: 'Level' },
                { key: 'current_tenure_months', label: 'Tenure', render: v => v ? `${v} mo` : '—' },
                { key: 'transition_likelihood', label: 'Likelihood', render: v => (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    v === 'Overdue' ? 'bg-red-100 text-red-800' :
                    v === 'Approaching' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>{v}</span>
                )},
              ]}
              rows={data}
              onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
            />
          )}
        </div>
      );
    }

    // Career Outcomes
    if (reportKey === 'career-outcomes') {
      return (
        <div className="space-y-4">
          {/* About This Report */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">About This Report</h4>
            <p className="text-sm text-blue-700">
              This cohort-based analysis tracks career outcomes of NACME alumni. It requires executive profiles
              to be linked to student records (via the student_id field) and those students must have an
              expected graduation date set. The report shows what percentage of each graduation cohort
              reached C-Suite, VP+, or board positions.
            </p>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">
              No career outcomes data available. Ensure executive profiles are linked to student records
              and students have expected graduation dates set.
            </div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'graduation_year', label: 'Grad Year', className: 'font-medium' },
                { key: 'alumni_tracked', label: 'Tracked', className: 'text-center' },
                { key: 'reached_csuite', label: 'C-Suite', className: 'text-center' },
                { key: 'pct_csuite', label: '% C-Suite', render: v => `${v}%` },
                { key: 'reached_vp_plus', label: 'VP+', className: 'text-center' },
                { key: 'pct_vp_plus', label: '% VP+', render: v => `${v}%` },
                { key: 'held_board_seat', label: 'Board', className: 'text-center' },
              ]}
              rows={data}
            />
          )}
        </div>
      );
    }

    // Function Migration
    if (reportKey === 'function-migration') {
      return (
        <div className="space-y-4">
          {/* About This Report */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">About This Report</h4>
            <p className="text-sm text-blue-700">
              This report tracks how careers evolve across business functions (e.g., Engineering → Product,
              Finance → Operations). It compares each person's first role function to their current role function.
              Requires roles with <code className="bg-blue-100 px-1 rounded">role_function</code> populated
              and at least one role marked as current.
            </p>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">
              No function migration data available. Ensure roles have role_function values set
              and at least one current role exists per profile.
            </div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'starting_function', label: 'Started In', className: 'font-medium' },
                { key: 'current_function', label: 'Now In' },
                { key: 'alumni_count', label: 'Count', className: 'text-center font-semibold' },
                { key: 'pct', label: '% of Starting', render: v => `${v}%` },
              ]}
              rows={data}
            />
          )}
        </div>
      );
    }

    // Geographic Flow
    if (reportKey === 'geographic-flow') {
      return (
        <div className="space-y-4">
          {/* About This Report */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">About This Report</h4>
            <p className="text-sm text-blue-700">
              This report tracks where alumni end up relative to where they studied. Origin is determined
              from linked student records (state) or education institution. Requires profiles to have
              <code className="bg-blue-100 px-1 rounded">location_region</code> populated.
            </p>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">
              No geographic flow data available. Ensure profiles have location_region set
              and origin data from student records or education history.
            </div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'origin_region', label: 'Origin', className: 'font-medium' },
                { key: 'current_region', label: 'Current Location' },
                { key: 'alumni_count', label: 'Alumni', className: 'text-center font-semibold' },
              ]}
              rows={data}
            />
          )}
        </div>
      );
    }

    // Warm Intros
    if (reportKey === 'warm-intros') {
      const { target_company, connectors = [], clusters = [] } = data;
      return (
        <div className="space-y-4">
          {/* About This Report */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">About This Report</h4>
            <p className="text-sm text-blue-700">
              Find alumni who can make introductions to a target company. Shows current and former
              employees who could serve as connectors. Click a name to view their full profile.
            </p>
          </div>

          <div className="bg-indigo-50 rounded-lg p-3">
            <span className="text-sm text-indigo-700">Target Company: </span>
            <span className="font-semibold text-indigo-900">{target_company}</span>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Potential Connectors ({connectors.length})</h4>
            {connectors.length === 0 ? (
              <div className="text-gray-400 text-sm">No alumni found at this company.</div>
            ) : (
              <SimpleTable
                columns={[
                  { key: 'name', label: 'Name', className: 'font-medium text-indigo-600' },
                  { key: 'role', label: 'Role' },
                  { key: 'status', label: 'Status', render: v => (
                    <span className={`px-2 py-0.5 rounded text-xs ${v === 'Currently there' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{v}</span>
                  )},
                  { key: 'start_date', label: 'Start', render: v => v ? new Date(v).toLocaleDateString() : '—' },
                ]}
                rows={connectors}
                onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
              />
            )}
          </div>
        </div>
      );
    }

    // Comparable Pool
    if (reportKey === 'comparable-pool') {
      return (
        <div className="space-y-4">
          {/* About This Report */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">About This Report</h4>
            <p className="text-sm text-blue-700">
              Find candidates matching specific criteria for executive search. Shows current role holders
              with their leadership scores and bands. Click a name to view their full profile.
            </p>
          </div>

          {(!Array.isArray(data) || data.length === 0) ? (
            <div className="text-gray-400 text-center py-4">No comparable candidates found.</div>
          ) : (
            <SimpleTable
              columns={[
                { key: 'full_name', label: 'Name', className: 'font-medium text-indigo-600' },
                { key: 'current_role', label: 'Role' },
                { key: 'company', label: 'Company' },
                { key: 'level', label: 'Level' },
                { key: 'function', label: 'Function' },
                { key: 'leadership_score', label: 'Score', className: 'font-mono', render: v => v?.toFixed(1) ?? '—' },
                { key: 'score_band', label: 'Band', render: v => v ? <ScoreBadge band={v} /> : '—' },
              ]}
              rows={data}
              onRowClick={(row) => row.profile_id && navigate(`/admin/executive-intelligence/profile/${row.profile_id}`)}
            />
          )}
        </div>
      );
    }

    // Fallback: JSON display
    return (
      <pre className="text-xs text-gray-700 bg-gray-50 p-4 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  };

  // ============================================
  // REPORTS TAB RENDER
  // ============================================
  const renderReports = () => {
    const categories = [...new Set(REPORT_LIST.map(r => r.category))];

    return (
      <div className="space-y-4">
        {/* Alumni Search */}
        <div className="bg-white border border-teal-200 rounded-lg shadow-sm p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-teal-800">Alumni Search</h3>
            <button
              onClick={() => setShowAlumniSearch(!showAlumniSearch)}
              className="px-3 py-1.5 text-xs border border-teal-300 text-teal-700 rounded-lg hover:bg-teal-50"
            >
              {showAlumniSearch ? 'Hide' : 'Show'}
            </button>
          </div>
          {showAlumniSearch && (
            <div className="space-y-3">
              <p className="text-xs text-teal-600">
                Search alumni by company, industry, and seniority level. Filter combinations narrow results (e.g., all VPs at IBM in Technology).
              </p>
              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer text-gray-400 hover:text-gray-600">Data source note</summary>
                <p className="mt-1 p-2 bg-gray-50 rounded text-gray-500 leading-relaxed">
                  Alumni Search counts from <strong>exec_person_contacts</strong> (imported contact records) using partial matching (e.g., "IBM" also finds "IBM Consulting") and also checks each contact's stored current company field.
                  Company Footprint counts from <strong>exec_roles</strong> (role records from LinkedIn profiles), grouped by exact company name (case-insensitive).
                  Counts may differ for three reasons: (1) different data sources — some contacts have no role records and only appear in Alumni Search; (2) different matching — Alumni Search uses partial matching across all company name variants; (3) both tools count all role levels for their main totals, but Company Footprint's "Executive Roles" and "Current Executives" columns filter to VP and above.
                </p>
              </details>

              {/* Filter Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name / Email</label>
                  <input
                    type="text"
                    value={alumniSearchFilters.search}
                    onChange={(e) => setAlumniSearchFilters(prev => ({ ...prev, search: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && fetchAlumniSearch(1)}
                    placeholder="Search by name or email..."
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-teal-400 focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Company</label>
                  <input
                    type="text"
                    value={alumniSearchFilters.company}
                    onChange={(e) => setAlumniSearchFilters(prev => ({ ...prev, company: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && fetchAlumniSearch(1)}
                    placeholder="e.g., IBM, Google, Deloitte..."
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-teal-400 focus:border-teal-400"
                  />
                </div>
              </div>

              {/* University Search */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="block text-xs font-medium text-gray-600">University</label>
                  <button
                    type="button"
                    onClick={() => setAlumniSearchFilters(prev => ({ ...prev, university_search_all: !prev.university_search_all }))}
                    className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                      alumniSearchFilters.university_search_all
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-teal-600 text-white border-teal-600'
                    }`}
                  >
                    {alumniSearchFilters.university_search_all ? 'All Institutions' : 'NACME University'}
                  </button>
                </div>
                <input
                  type="text"
                  value={alumniSearchFilters.university}
                  onChange={(e) => setAlumniSearchFilters(prev => ({ ...prev, university: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && fetchAlumniSearch(1)}
                  placeholder={alumniSearchFilters.university_search_all
                    ? "Search all LinkedIn education institutions (e.g., Harvard, MIT)..."
                    : "Search NACME scholarship university (e.g., NYU, Howard)..."
                  }
                  className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-teal-400 focus:border-teal-400"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {alumniSearchFilters.university_search_all
                    ? 'Searches all institutions from LinkedIn education history'
                    : 'Searches the institution where alum received NACME scholarship'
                  }
                </p>
              </div>

              {/* Industry multi-select */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Industry</label>
                <div className="flex flex-wrap gap-1.5">
                  {COMMON_INDUSTRIES.filter(i => i !== 'Other').map(ind => (
                    <button
                      key={ind}
                      onClick={() => toggleAlumniIndustry(ind)}
                      className={`px-2 py-0.5 text-[10px] rounded-full border ${
                        alumniSearchFilters.industries.includes(ind)
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
              </div>

              {/* Level multi-select */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Seniority Level</label>
                <div className="flex flex-wrap gap-1.5">
                  {LEVEL_OPTIONS.map(lv => (
                    <button
                      key={lv.value}
                      onClick={() => toggleAlumniLevel(lv.value)}
                      className={`px-2 py-0.5 text-[10px] rounded-full border ${
                        alumniSearchFilters.levels.includes(lv.value)
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {lv.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Controls row */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={alumniSearchFilters.current_roles_only}
                    onChange={(e) => setAlumniSearchFilters(prev => ({ ...prev, current_roles_only: e.target.checked }))}
                  />
                  Current roles only
                </label>
                <div className="flex-1" />
                <button
                  onClick={clearAlumniSearch}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
                <button
                  onClick={() => fetchAlumniSearch(1)}
                  disabled={alumniSearchLoading}
                  className="px-4 py-1.5 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {alumniSearchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {/* Summary Stats */}
              {alumniSearchData && alumniSearchData.summary && (
                <div className="bg-teal-50 rounded-lg p-3 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-lg font-semibold text-teal-900">{alumniSearchData.summary.total}</div>
                    <div className="text-xs text-teal-600">Total Results</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-teal-900">{alumniSearchData.summary.unique_companies}</div>
                    <div className="text-xs text-teal-600">Companies</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-teal-900">{alumniSearchData.summary.unique_universities}</div>
                    <div className="text-xs text-teal-600">Universities</div>
                  </div>
                </div>
              )}

              {/* Results Table */}
              {alumniSearchData && alumniSearchData.items && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {[
                          { key: 'full_name', label: 'Name' },
                          { key: 'university', label: 'University' },
                          { key: 'graduation_year', label: 'Grad Year' },
                          { key: null, label: 'Demographics' },
                          { key: 'current_company', label: 'Current Company' },
                          { key: null, label: 'Matched Role' },
                        ].map((col, i) => (
                          <th
                            key={i}
                            className={`px-3 py-2 text-left text-xs font-medium text-gray-500 ${col.key ? 'cursor-pointer hover:text-gray-800 select-none' : ''}`}
                            onClick={() => {
                              if (!col.key) return;
                              setAlumniSearchSort(prev =>
                                prev.key === col.key
                                  ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                  : { key: col.key, dir: 'asc' }
                              );
                              fetchAlumniSearch(1);
                            }}
                          >
                            {col.label}
                            {col.key && (alumniSearchSort.key === col.key
                              ? (alumniSearchSort.dir === 'asc' ? ' ↑' : ' ↓')
                              : ' ↕')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {alumniSearchData.items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium whitespace-nowrap">
                            {item.full_name || '—'}
                            {(item.email || item.personal_email) && (
                              <div className="text-[10px] text-gray-400 font-normal truncate max-w-[160px]">
                                {item.email || item.personal_email}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {item.university || '—'}
                            {item.matched_institution && item.matched_institution !== item.university && (
                              <div className="text-[10px] text-purple-500 mt-0.5">
                                {item.matched_institution}{item.matched_degree ? ` (${item.matched_degree})` : ''}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{item.graduation_year || '—'}</td>
                          <td className="px-3 py-2 text-gray-600">
                            <div className="text-xs">
                              {[item.gender, item.ethnicity].filter(Boolean).join(' / ') || '—'}
                            </div>
                            {item.major && (
                              <div className="text-[10px] text-gray-400 truncate max-w-[160px]">{item.major}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{item.current_company || '—'}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            {item.matched_company ? (
                              <div>
                                <div className="font-medium text-gray-700">{item.matched_company}</div>
                                {item.matched_title && <div className="text-[10px] text-gray-400">{item.matched_title}</div>}
                                <div className="flex gap-1 mt-0.5">
                                  {item.matched_level && (
                                    <span className="px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded text-[10px]">{item.matched_level}</span>
                                  )}
                                  {item.matched_industry && (
                                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">{item.matched_industry}</span>
                                  )}
                                  {item.is_current_role && (
                                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">current</span>
                                  )}
                                </div>
                              </div>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                      {alumniSearchData.items.length === 0 && (
                        <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-400">No results found</td></tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {alumniSearchData.total_pages > 1 && (
                    <div className="flex items-center justify-between mt-3 px-1">
                      <div className="text-xs text-gray-500">
                        Page {alumniSearchData.page} of {alumniSearchData.total_pages} ({alumniSearchData.total} results)
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => fetchAlumniSearch(alumniSearchPage - 1)}
                          disabled={alumniSearchPage <= 1 || alumniSearchLoading}
                          className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                        >
                          Prev
                        </button>
                        <button
                          onClick={() => fetchAlumniSearch(alumniSearchPage + 1)}
                          disabled={alumniSearchPage >= alumniSearchData.total_pages || alumniSearchLoading}
                          className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Report Selector */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Report</h3>
          {categories.map(cat => (
            <div key={cat} className="mb-3">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">{cat}</div>
              <div className="flex flex-wrap gap-2">
                {REPORT_LIST.filter(r => r.category === cat).map(r => (
                  <button
                    key={r.key}
                    onClick={() => {
                      if (r.key === 'warm-intros') {
                        const company = prompt('Enter target company name:');
                        if (company) fetchReport(r.key, { target_company: company });
                      } else {
                        fetchReport(r.key);
                      }
                    }}
                    className={`px-3 py-1.5 text-xs rounded-lg border ${
                      selectedReport === r.key
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Report Output */}
        {reportLoading && <LoadingSpinner message="Generating report..." />}
        {reportData && !reportLoading && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-gray-700">
                {REPORT_LIST.find(r => r.key === selectedReport)?.name || 'Report'}
              </h3>
              <button
                onClick={() => { setReportData(null); setSelectedReport(null); }}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Clear
              </button>
            </div>
            <div className="overflow-x-auto">
              {renderReportContent(selectedReport, reportData)}
            </div>
          </div>
        )}

        {/* Company Industries Manager */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Company Industries Manager</h3>
            <button
              onClick={() => {
                setShowIndustryManager(!showIndustryManager);
                if (!showIndustryManager) fetchCompaniesMissingIndustries();
              }}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {showIndustryManager ? 'Hide' : 'Show'}
            </button>
          </div>
          {showIndustryManager && (
            <div className="space-y-3">
              {/* Instructions */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">How to Use This Tool</h4>
                <div className="text-sm text-blue-700 space-y-2">
                  <p>
                    The <strong>Industry Concentration</strong> report requires each company to have an industry assigned.
                    Use this tool to assign industries to companies in your executive profiles.
                  </p>
                  <div className="mt-2">
                    <strong>Understanding the Diagnostics:</strong>
                    <ul className="list-disc list-inside mt-1 ml-2 space-y-1">
                      <li><strong>Total Roles</strong> — All role records in exec_roles table</li>
                      <li><strong>Current Roles</strong> — Roles marked as current position (is_current=true)</li>
                      <li><strong>Companies</strong> — Unique company names across all roles</li>
                      <li><strong>With Company Name</strong> — Roles that have company_name populated</li>
                      <li><strong>With Industry</strong> — Roles that already have company_industry set</li>
                    </ul>
                  </div>
                  <div className="mt-2">
                    <strong>How to Assign Industries:</strong>
                    <ul className="list-disc list-inside mt-1 ml-2 space-y-1">
                      <li>Companies missing industries appear in the table below</li>
                      <li>Each row shows a company name and how many roles it has</li>
                      <li>Click the <strong>"Select industry..."</strong> dropdown in that row</li>
                      <li>Choose an industry (e.g., Technology, Healthcare, Financial Services)</li>
                      <li>The industry is saved immediately — the company disappears from the list</li>
                      <li>Once all companies have industries, the Industry Concentration report will show data</li>
                    </ul>
                  </div>
                  <div className="mt-2">
                    <strong>Troubleshooting:</strong>
                    <ul className="list-disc list-inside mt-1 ml-2 space-y-1">
                      <li>If <strong>Total Roles = 0</strong>: Import executive profiles first</li>
                      <li>If <strong>Current Roles = 0</strong>: Role data exists but none marked as current</li>
                      <li>If <strong>With Industry = Total Roles</strong>: All industries already set</li>
                      <li>If no companies appear below but report is empty: Check Current Roles count</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* CSV Import */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-green-800 mb-2">Bulk Import from CSV</h4>
                <p className="text-xs text-green-700 mb-3">
                  Upload the enriched CSV file (<code>company_names_enriched_industry.csv</code>) to bulk-assign industries.
                  Labels are normalized to canonical values automatically. Unknown/Unverified entries are skipped.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvImportFile(e.target.files[0] || null)}
                    className="text-xs"
                  />
                  <label className="flex items-center gap-1.5 text-xs text-green-700">
                    <input
                      type="checkbox"
                      checked={csvForceOverwrite}
                      onChange={(e) => setCsvForceOverwrite(e.target.checked)}
                    />
                    Overwrite existing
                  </label>
                  <button
                    onClick={handleCsvImport}
                    disabled={!csvImportFile || csvImportLoading}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {csvImportLoading ? 'Importing...' : 'Import CSV'}
                  </button>
                </div>
                {csvImportResults && (
                  <div className="mt-3 bg-white rounded-lg p-3 border border-green-200">
                    <h5 className="text-xs font-semibold text-green-800 mb-2">Import Results</h5>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-2">
                      <div className="bg-green-50 rounded p-2 text-center">
                        <div className="font-semibold text-green-900">{csvImportResults.total_csv_rows}</div>
                        <div className="text-green-600">CSV Rows</div>
                      </div>
                      <div className="bg-green-50 rounded p-2 text-center">
                        <div className="font-semibold text-green-900">{csvImportResults.companies_updated}</div>
                        <div className="text-green-600">Companies Updated</div>
                      </div>
                      <div className="bg-green-50 rounded p-2 text-center">
                        <div className="font-semibold text-green-900">{csvImportResults.total_roles_updated}</div>
                        <div className="text-green-600">Roles Updated</div>
                      </div>
                      <div className="bg-green-50 rounded p-2 text-center">
                        <div className="font-semibold text-green-900">{csvImportResults.skipped_unknown}</div>
                        <div className="text-green-600">Skipped (Unknown)</div>
                      </div>
                    </div>
                    {csvImportResults.industry_distribution && Object.keys(csvImportResults.industry_distribution).length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs font-medium text-gray-600 mb-1">Industry Distribution (roles updated):</div>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(csvImportResults.industry_distribution)
                            .sort((a, b) => b[1] - a[1])
                            .map(([industry, count]) => (
                              <span key={industry} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                                {industry}: {count}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                    {csvImportResults.unmapped_labels && csvImportResults.unmapped_labels.length > 0 && (
                      <div className="mt-2 text-xs text-amber-700">
                        <strong>Unmapped labels:</strong> {csvImportResults.unmapped_labels.join(', ')}
                      </div>
                    )}
                    {csvImportResults.skipped_no_match > 0 && (
                      <div className="mt-1 text-xs text-gray-500">
                        {csvImportResults.skipped_no_match} companies had no matching roles in the database.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Diagnostics */}
              {companiesDiagnostics && (
                <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-5 gap-2 text-center">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{companiesDiagnostics.total_roles}</div>
                    <div className="text-xs text-gray-500">Total Roles</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{companiesDiagnostics.current_roles}</div>
                    <div className="text-xs text-gray-500">Current Roles</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{companiesDiagnostics.unique_companies}</div>
                    <div className="text-xs text-gray-500">Companies</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{companiesDiagnostics.roles_with_company}</div>
                    <div className="text-xs text-gray-500">With Company Name</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-indigo-600">{companiesDiagnostics.roles_with_industry}</div>
                    <div className="text-xs text-gray-500">With Industry</div>
                  </div>
                </div>
              )}

              {/* Explanation */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>What does "Roles" mean?</strong> Each company shows the number of role records
                  associated with it in executive profiles. For example, "General Motors — 41 roles" means
                  41 position records exist where someone worked at GM (could be multiple people or multiple
                  positions over time). When you select an industry from the dropdown, all roles for that
                  company are updated at once.
                </p>
              </div>

              {companiesLoading ? (
                <div className="text-center py-4 text-gray-400 text-sm">Loading companies...</div>
              ) : companiesMissingIndustries.length === 0 ? (
                <div className="text-center py-4 text-amber-600 text-sm">
                  {companiesDiagnostics?.total_roles === 0
                    ? 'No roles found in the database. Import executive profiles first.'
                    : companiesDiagnostics?.roles_with_industry === 0
                    ? 'No industries set yet, but no company names found either. Roles may be missing company_name data.'
                    : 'All companies have industries set!'}
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-500 mb-2">
                    {companiesMissingIndustries.length} companies missing industry data — select an industry to assign it
                  </div>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Company</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Roles</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Set Industry</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {companiesMissingIndustries.map((c, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-900">{c.company_name}</td>
                            <td className="px-3 py-2 text-center text-gray-500">{c.role_count}</td>
                            <td className="px-3 py-2">
                              <select
                                className="px-2 py-1 text-xs border border-gray-300 rounded w-full"
                                defaultValue=""
                                onChange={(e) => {
                                  if (e.target.value) {
                                    setCompanyIndustry(c.company_name, e.target.value);
                                  }
                                }}
                                disabled={industryUpdateLoading}
                              >
                                <option value="">Select industry...</option>
                                {COMMON_INDUSTRIES.map(ind => (
                                  <option key={ind} value={ind}>{ind}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Companies With Industries - Toggle to show/edit */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <button
                  onClick={() => {
                    setShowCompaniesWithIndustries(!showCompaniesWithIndustries);
                    if (!showCompaniesWithIndustries) fetchCompaniesWithIndustries();
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  {showCompaniesWithIndustries ? '▼ Hide' : '▶ Show'} companies with industries set ({companiesDiagnostics?.roles_with_industry || 0} roles)
                </button>

                {showCompaniesWithIndustries && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-2">
                      Change or clear industries for companies that already have them set
                    </div>
                    {companiesWithIndustries.length === 0 ? (
                      <div className="text-center py-4 text-gray-400 text-sm">No companies with industries set.</div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Company</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Current Industry</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Roles</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Change/Clear</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {companiesWithIndustries.map((c, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium text-gray-900">{c.company_name}</td>
                                <td className="px-3 py-2">
                                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded text-xs">{c.industry}</span>
                                </td>
                                <td className="px-3 py-2 text-center text-gray-500">{c.role_count}</td>
                                <td className="px-3 py-2">
                                  <div className="flex gap-2">
                                    <select
                                      className="px-2 py-1 text-xs border border-gray-300 rounded flex-1"
                                      defaultValue=""
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          setCompanyIndustry(c.company_name, e.target.value).then(() => {
                                            fetchCompaniesWithIndustries();
                                          });
                                        }
                                      }}
                                      disabled={industryUpdateLoading}
                                    >
                                      <option value="">Change to...</option>
                                      {COMMON_INDUSTRIES.filter(ind => ind !== c.industry).map(ind => (
                                        <option key={ind} value={ind}>{ind}</option>
                                      ))}
                                    </select>
                                    <button
                                      onClick={() => clearCompanyIndustry(c.company_name)}
                                      disabled={industryUpdateLoading}
                                      className="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded border border-red-200"
                                    >
                                      Clear
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // FAVORITES TAB RENDER
  // ============================================
  const renderFavorites = () => (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-700">
            <span className="text-yellow-500 mr-2">★</span>
            Bookmarked Profiles
            {bookmarks.length > 0 && <span className="text-gray-400 font-normal ml-2">({bookmarks.length})</span>}
          </h3>
          <button
            onClick={fetchBookmarks}
            disabled={bookmarksLoading}
            className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded disabled:opacity-50"
          >
            {bookmarksLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {bookmarksLoading ? (
          <LoadingSpinner message="Loading favorites..." />
        ) : bookmarks.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-400">
            <div className="text-4xl mb-2">☆</div>
            <p className="text-sm">No bookmarked profiles yet</p>
            <p className="text-xs mt-1">Click the star icon on search results to add favorites</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Headline</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Company</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Location</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Followers</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Connections</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Score</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Band</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Bookmarked</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bookmarks.map((b) => (
                  <tr key={b.profile_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-indigo-600">
                      <button
                        onClick={() => navigate(`/admin/executive-intelligence/profile/${b.profile_id}`)}
                        className="hover:underline text-left"
                      >
                        {b.full_name || 'Unknown'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-gray-600 max-w-md">{b.headline || '—'}</td>
                    <td className="px-4 py-2">{b.current_company || '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{b.location_raw || '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{b.follower_count != null ? b.follower_count.toLocaleString() : '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{b.connection_count != null ? b.connection_count.toLocaleString() : '—'}</td>
                    <td className="px-4 py-2 font-mono">{b.leadership_score != null ? b.leadership_score.toFixed(1) : '—'}</td>
                    <td className="px-4 py-2"><ScoreBadge band={b.score_band} /></td>
                    <td className="px-4 py-2 text-xs text-gray-400">
                      {b.bookmarked_at ? new Date(b.bookmarked_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleBookmark(b.profile_id, true)}
                          disabled={bookmarkActionLoading[b.profile_id]}
                          className={`text-lg text-yellow-500 hover:text-yellow-600 transition-colors ${
                            bookmarkActionLoading[b.profile_id] ? 'opacity-50' : ''
                          }`}
                          title="Remove from favorites"
                        >
                          ★
                        </button>
                        <button
                          onClick={() => navigate(`/admin/executive-intelligence/profile/${b.profile_id}`)}
                          className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // MAP TAB RENDER
  // ============================================
  const renderMap = () => (
    <div className="space-y-4">
      {/* Instructions Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="text-sm text-blue-800">
            <h4 className="font-semibold mb-2">How to Use the Map & Location Tools</h4>
            <ol className="list-decimal list-inside space-y-1 text-blue-700">
              <li><strong>Geocode Profiles:</strong> Click "Geocode 100 Profiles" to fetch coordinates for profiles that have location data but no lat/long. This uses an external API (rate-limited).</li>
              <li><strong>Parse States (Fast):</strong> If profiles have coordinates but missing state data, click this to extract state info from the location string without API calls.</li>
              <li><strong>Normalize Cities:</strong> Cleans up city names by removing redundant state suffixes (e.g., "Brooklyn, New York" becomes "Brooklyn") and "County" suffixes.</li>
              <li><strong>Filter by Location:</strong> Use the Country, State, and City dropdowns to filter the map. Filters cascade: selecting a country updates available states, selecting a state updates available cities.</li>
              <li><strong>View on Map:</strong> Geocoded profiles appear as markers. Click a marker to see profile details and open the full profile.</li>
            </ol>
            <p className="mt-2 text-xs text-blue-600">Tip: Run these tools in order (Geocode → Parse States → Normalize Cities) to ensure complete and clean location data.</p>
          </div>
        </div>
      </div>

      {/* Geocoding Stats & Controls */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Geocoding Status</h3>
            {geocodingLoading ? (
              <p className="text-sm text-gray-400">Loading stats...</p>
            ) : geocodingStats ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Total Profiles:</span>{' '}
                    <span className="font-medium">{geocodingStats.total_profiles?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">With Coordinates:</span>{' '}
                    <span className="font-medium text-green-600">{geocodingStats.with_coordinates?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Pending:</span>{' '}
                    <span className="font-medium text-amber-600">
                      {(geocodingStats.by_status?.pending || 0).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Failed:</span>{' '}
                    <span className="font-medium text-red-600">{geocodingStats.by_status?.failed || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">N/A (Remote):</span>{' '}
                    <span className="font-medium text-gray-400">{geocodingStats.by_status?.not_applicable || 0}</span>
                  </div>
                </div>
                {/* Location details stats */}
                <div className="flex flex-wrap gap-4 text-sm border-t border-gray-100 pt-2">
                  <div>
                    <span className="text-gray-500">With City:</span>{' '}
                    <span className="font-medium">{geocodingStats.with_city?.toLocaleString() || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">With State:</span>{' '}
                    <span className="font-medium">{geocodingStats.with_state?.toLocaleString() || 0}</span>
                    {geocodingStats.with_coordinates - geocodingStats.with_state > 0 && (
                      <button
                        onClick={fetchMissingStateData}
                        className="ml-2 text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        ({geocodingStats.with_coordinates - geocodingStats.with_state} missing - view)
                      </button>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500">With Country:</span>{' '}
                    <span className="font-medium">{geocodingStats.with_country?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No stats available</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => runGeocoding(100, false)}
              disabled={geocodingRunning}
              className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
            >
              {geocodingRunning ? 'Geocoding...' : 'Geocode 100 Profiles'}
            </button>
            {((geocodingStats?.with_city || 0) > (geocodingStats?.with_state || 0)) && (
              <button
                onClick={parseLocationFields}
                disabled={parsingLocations || geocodingRunning}
                className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                title="Fast: Parse location strings to extract state (no API calls)"
              >
                {parsingLocations ? 'Parsing...' : 'Parse States (Fast)'}
              </button>
            )}
            <button
              onClick={normalizeCityNames}
              disabled={normalizingCities || geocodingRunning}
              className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50"
              title="Clean up city names by removing redundant state suffixes and 'County'"
            >
              {normalizingCities ? 'Normalizing...' : 'Normalize Cities'}
            </button>
            <button
              onClick={() => { fetchMapProfiles(); fetchGeocodingStats(); fetchMapLocationOptions(); }}
              disabled={mapLoading}
              className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Location Filters - Cascading: Country → State → City */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Location Filters</h3>
            <p className="text-xs text-gray-500 mb-3">Select a country to see its states, then a state to see its cities.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Country</label>
                <select
                  value={mapLocationFilters.countries[0] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Reset state and city when country changes
                    const updated = { countries: val ? [val] : [], states: [], cities: [] };
                    setMapLocationFilters(updated);
                    // Refresh state/city options based on new country
                    fetchMapLocationOptions(val || null, null);
                    fetchMapProfiles(updated);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                  disabled={mapLocationOptionsLoading}
                >
                  <option value="">All Countries</option>
                  {mapLocationOptions.countries.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">State / Region</label>
                <select
                  value={mapLocationFilters.states[0] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const country = mapLocationFilters.countries[0] || null;
                    // Reset city when state changes
                    const updated = { ...mapLocationFilters, states: val ? [val] : [], cities: [] };
                    setMapLocationFilters(updated);
                    // Refresh city options based on country + state
                    fetchMapLocationOptions(country, val || null);
                    fetchMapProfiles(updated);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                  disabled={mapLocationOptionsLoading}
                >
                  <option value="">All States</option>
                  {mapLocationOptions.states.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                <select
                  value={mapLocationFilters.cities[0] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const updated = { ...mapLocationFilters, cities: val ? [val] : [] };
                    setMapLocationFilters(updated);
                    fetchMapProfiles(updated);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                  disabled={mapLocationOptionsLoading}
                >
                  <option value="">All Cities</option>
                  {mapLocationOptions.cities.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 pt-6">
            <button
              onClick={downloadMapExport}
              disabled={mapExportLoading || mapLoading}
              title="Download a CSV of every constituent matching the current filters (name, title, company, LinkedIn, grad year, major, leadership score, emails, phone)"
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 whitespace-nowrap"
            >
              {mapExportLoading ? 'Exporting…' : `Export CSV${mapProfiles.length ? ` (${mapProfiles.length})` : ''}`}
            </button>
            <button
              onClick={() => {
                setMapLocationFilters({ cities: [], states: [], countries: [] });
                fetchMapLocationOptions(); // Reset to show all options
                fetchMapProfiles({ cities: [], states: [], countries: [] });
              }}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg"
            >
              Clear Filters
            </button>
          </div>
        </div>
        {(mapLocationFilters.cities.length > 0 || mapLocationFilters.states.length > 0 || mapLocationFilters.countries.length > 0) && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">Active filters: </span>
            {mapLocationFilters.countries[0] && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 mr-1">
                {mapLocationFilters.countries[0]}
              </span>
            )}
            {mapLocationFilters.states[0] && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 mr-1">
                {mapLocationFilters.states[0]}
              </span>
            )}
            {mapLocationFilters.cities[0] && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 mr-1">
                {mapLocationFilters.cities[0]}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Radius Filter */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div className="flex-1 min-w-[260px]">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Radius Filter</h3>
            <p className="text-xs text-gray-500 mb-3">
              Show only constituents within a set distance of a point — useful for a metro area or event venue.
              Combines with the location filters above and applies to the CSV export.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <button
                onClick={() => setRadiusPickMode(m => !m)}
                className={`px-3 py-1.5 text-xs rounded-lg border ${
                  radiusPickMode
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'
                }`}
              >
                {radiusPickMode ? 'Click the map to set center…' : (radiusCenter ? 'Change center' : 'Set center on map')}
              </button>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Radius (miles)</label>
                <select
                  value={radiusMiles}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setRadiusMiles(val);
                    if (radiusCenter) {
                      setRadiusActive(true);
                      fetchMapProfiles(null, { lat: radiusCenter.lat, lng: radiusCenter.lng, miles: val });
                    }
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded text-xs"
                >
                  {[10, 25, 50, 100, 150, 250].map(m => (
                    <option key={m} value={m}>{m} mi</option>
                  ))}
                </select>
              </div>
              {radiusCenter && !radiusActive && (
                <button
                  onClick={applyRadius}
                  className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  Apply
                </button>
              )}
              {radiusCenter && (
                <button
                  onClick={clearRadius}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg"
                >
                  Clear radius
                </button>
              )}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); geocodeRadiusCenter(); }}
              className="flex items-end gap-2 mt-3"
            >
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Or type a city, address, or ZIP</label>
                <input
                  type="text"
                  value={radiusSearchText}
                  onChange={(e) => setRadiusSearchText(e.target.value)}
                  placeholder="e.g., Atlanta, GA or 30301"
                  className="px-2 py-1.5 border border-gray-300 rounded text-xs w-56"
                />
              </div>
              <button
                type="submit"
                disabled={radiusSearchLoading || !radiusSearchText.trim()}
                className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                {radiusSearchLoading ? 'Finding…' : 'Go'}
              </button>
            </form>
          </div>
          {radiusCenter && (
            <div className="text-xs text-gray-500 pt-1">
              <div>
                Center:{' '}
                <span className="font-medium text-gray-700">
                  {radiusCenter.lat.toFixed(4)}, {radiusCenter.lng.toFixed(4)}
                </span>
              </div>
              {radiusActive && (
                <div className="mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded font-medium bg-blue-100 text-blue-700">
                    Within {radiusMiles} mi
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Map Legend */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Legend</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500 border-2 border-white shadow"></div>
            <span className="text-gray-600">Bookmarked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-600 border-2 border-white shadow"></div>
            <span className="text-gray-600">Executive</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-600 border-2 border-white shadow"></div>
            <span className="text-gray-600">Senior</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-600 border-2 border-white shadow"></div>
            <span className="text-gray-600">Established</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-500 border-2 border-white shadow"></div>
            <span className="text-gray-600">Other/Unscored</span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-700">
            Executive Profiles Map
            {mapProfiles.length > 0 && (
              <span className="text-gray-400 font-normal ml-2">({mapProfiles.length} on map)</span>
            )}
          </h3>
        </div>

        {mapLoading ? (
          <div className="h-[600px] flex items-center justify-center">
            <LoadingSpinner message="Loading map..." />
          </div>
        ) : mapProfiles.length === 0 ? (
          <div className="h-[400px] flex flex-col items-center justify-center text-gray-400">
            <div className="text-4xl mb-2">◎</div>
            <p className="text-sm">No geocoded profiles yet</p>
            <p className="text-xs mt-1">Click "Geocode 100 Profiles" to start geocoding</p>
          </div>
        ) : (
          <div style={{ height: '600px' }}>
            <MapContainer
              center={[39.8283, -98.5795]}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <RadiusClickHandler active={radiusPickMode} onPick={handleRadiusPick} />
              {radiusCenter && <MapFlyTo center={radiusCenter} zoom={radiusZoom(radiusMiles)} />}
              {radiusCenter && (
                <>
                  <Circle
                    center={[radiusCenter.lat, radiusCenter.lng]}
                    radius={radiusMiles * 1609.34}
                    pathOptions={{ color: '#2563eb', weight: 2, fillColor: '#3b82f6', fillOpacity: 0.08 }}
                  />
                  <CircleMarker
                    center={[radiusCenter.lat, radiusCenter.lng]}
                    radius={6}
                    pathOptions={{ color: '#1d4ed8', fillColor: '#1d4ed8', fillOpacity: 1 }}
                  />
                </>
              )}
              {mapProfiles.map((profile) => (
                <Marker
                  key={profile.id}
                  position={[profile.latitude, profile.longitude]}
                  icon={createExecMarkerIcon(profile)}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="flex items-start gap-3">
                        {profile.profile_picture_url ? (
                          <img
                            src={profile.profile_picture_url}
                            alt={profile.full_name}
                            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-gray-500 text-lg font-medium">
                              {profile.full_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-0.5 truncate">{profile.full_name}</h4>
                          {profile.current_title && (
                            <p className="text-sm text-gray-600 truncate">{profile.current_title}</p>
                          )}
                          {profile.current_company && (
                            <p className="text-sm text-gray-500 truncate">{profile.current_company}</p>
                          )}
                        </div>
                      </div>
                      {profile.location_raw && (
                        <p className="text-xs text-gray-400 mt-2">{profile.location_raw}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {profile.score_band && (
                          <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                            profile.score_band === 'executive' ? 'bg-purple-100 text-purple-700' :
                            profile.score_band === 'senior' ? 'bg-blue-100 text-blue-700' :
                            profile.score_band === 'established' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {profile.score_band}
                          </span>
                        )}
                        {profile.leadership_score != null && (
                          <span className="text-xs text-gray-500">
                            Score: {profile.leadership_score.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <button
                          onClick={() => toggleBookmark(profile.id, profile.is_bookmarked)}
                          disabled={bookmarkActionLoading[profile.id]}
                          className={`text-lg transition-colors ${
                            profile.is_bookmarked
                              ? 'text-yellow-500 hover:text-yellow-600'
                              : 'text-gray-300 hover:text-yellow-400'
                          } ${bookmarkActionLoading[profile.id] ? 'opacity-50' : ''}`}
                          title={profile.is_bookmarked ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          {profile.is_bookmarked ? '★' : '☆'}
                        </button>
                        <button
                          onClick={() => navigate(`/admin/executive-intelligence/profile/${profile.id}`)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          View Profile →
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // ANALYTICS TAB RENDER
  // ============================================
  const renderAnalytics = () => (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-600">Trend Period:</label>
            <select
              value={trendDays}
              onChange={(e) => setTrendDays(parseInt(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
            </select>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
            >
              Refresh
            </button>
            <button
              onClick={triggerAnalyticsCompute}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
            >
              Compute Today's Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Trend Data */}
      {analyticsLoading ? (
        <LoadingSpinner message="Loading analytics..." />
      ) : analyticsData?.trends && Array.isArray(analyticsData.trends) && analyticsData.trends.length > 0 ? (
        <div className="space-y-4">
          {/* Live summary stats (real-time, not from stale snapshots) */}
          {(() => {
            const live = analyticsData.live_summary || analyticsData.trends[analyticsData.trends.length - 1];
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Profiles" value={live.total_profiles} color="indigo" />
                <StatCard label="Avg Leadership Score" value={live.avg_leadership_score?.toFixed(1)} color="purple" />
                <StatCard label="Median Score" value={live.median_leadership_score?.toFixed(1)} color="blue" />
                <StatCard label="Profiles Scored" value={live.profiles_scored} color="green" />
              </div>
            );
          })()}

          {/* Band Distribution (live) */}
          {(() => {
            const live = analyticsData.live_summary || analyticsData.trends[analyticsData.trends.length - 1];
            const bands = live.band_distribution || {};
            return (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Score Band Distribution <span className="font-normal text-gray-400">(AI-scored)</span></h3>
                <div className="grid grid-cols-5 gap-3">
                  {Object.entries(bands).map(([band, count]) => (
                    <div key={band} className="text-center">
                      <div className="text-lg font-bold text-gray-800">{count}</div>
                      <ScoreBadge band={band} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Level Distribution (latest) */}
          {(() => {
            const latest = analyticsData.trends[analyticsData.trends.length - 1];
            const levels = latest.level_distribution || {};
            return (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Level Distribution <span className="font-normal text-gray-400">(count of titles, unscored)</span></h3>
                <div className="grid grid-cols-5 gap-3">
                  {Object.entries(levels).map(([level, count]) => (
                    <div key={level} className="text-center">
                      <div className="text-lg font-bold text-gray-800">{count}</div>
                      <div className="text-xs text-gray-500">{level.replace('_', ' ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Trend Table */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Daily Snapshots ({analyticsData.trends.length} days)</h3>
            </div>
            <div className="overflow-x-auto max-h-64">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Profiles</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Scored</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Avg Score</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Median</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[...analyticsData.trends].reverse().map((row) => (
                    <tr key={row.date} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-600">{row.date}</td>
                      <td className="px-4 py-2">{row.total_profiles}</td>
                      <td className="px-4 py-2">{row.profiles_scored}</td>
                      <td className="px-4 py-2 font-mono">{row.avg_leadership_score?.toFixed(1) ?? '—'}</td>
                      <td className="px-4 py-2 font-mono">{row.median_leadership_score?.toFixed(1) ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center text-gray-400">
          No analytics data available. Click "Compute Today's Analytics" to generate the first snapshot.
        </div>
      )}
    </div>
  );

  // ============================================
  // CALIBRATION TAB RENDER
  // ============================================
  const renderCalibration = () => (
    <div className="space-y-4">
      {/* Alert Summary Cards */}
      {calibrationAlertSummary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-800">{calibrationAlertSummary.total_open}</div>
            <div className="text-xs text-gray-500">Open Alerts</div>
          </div>
          <div className="bg-white border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">{calibrationAlertSummary.by_severity?.critical || 0}</div>
            <div className="text-xs text-red-500">Critical</div>
          </div>
          <div className="bg-white border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">{calibrationAlertSummary.by_severity?.warning || 0}</div>
            <div className="text-xs text-yellow-500">Warning</div>
          </div>
          <div className="bg-white border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{calibrationAlertSummary.by_severity?.info || 0}</div>
            <div className="text-xs text-blue-500">Info</div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Calibration Controls</h3>
            <p className="text-xs text-gray-500 mt-1">Run calibration to detect scoring anomalies and drift</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => triggerCalibration('distribution')}
              disabled={calibrationTriggerLoading}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Distribution Only
            </button>
            <button
              onClick={() => triggerCalibration('drift')}
              disabled={calibrationTriggerLoading}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Drift Only
            </button>
            <button
              onClick={() => triggerCalibration('full')}
              disabled={calibrationTriggerLoading}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {calibrationTriggerLoading ? 'Running...' : 'Run Full Calibration'}
            </button>
          </div>
        </div>
      </div>

      {/* Open Alerts */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-700">Open Alerts ({calibrationAlerts.length})</h3>
          <button
            onClick={fetchCalibrationAlerts}
            className="text-xs text-indigo-600 hover:underline"
          >
            Refresh
          </button>
        </div>
        {calibrationAlertsLoading ? (
          <div className="p-4 text-center text-gray-400">Loading alerts...</div>
        ) : calibrationAlerts.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No open alerts. Scoring looks healthy!</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {calibrationAlerts.map((alert) => (
              <div key={alert.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                        alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        alert.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-500">{alert.alert_type}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-800">{alert.message}</p>
                    {alert.details && (
                      <pre className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded overflow-x-auto">
                        {JSON.stringify(alert.details, null, 2)}
                      </pre>
                    )}
                    <div className="mt-2 text-xs text-gray-400">
                      Created: {new Date(alert.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => acknowledgeAlert(alert.id)}
                      className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Acknowledge
                    </button>
                    <button
                      onClick={() => {
                        const notes = window.prompt('Resolution notes (optional):');
                        if (notes !== null) resolveAlert(alert.id, notes);
                      }}
                      className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Calibration Runs */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-700">Recent Calibration Runs</h3>
          <button
            onClick={fetchCalibrationRuns}
            className="text-xs text-indigo-600 hover:underline"
          >
            Refresh
          </button>
        </div>
        {calibrationRunsLoading ? (
          <div className="p-4 text-center text-gray-400">Loading runs...</div>
        ) : calibrationRuns.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No calibration runs yet. Runs are triggered automatically after scoring batches.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Trigger</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Warnings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {calibrationRuns.map((run) => (
                  <tr key={run.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-600">
                      {run.completed_at ? new Date(run.completed_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                        run.trigger === 'post_batch' ? 'bg-purple-100 text-purple-700' :
                        run.trigger === 'manual' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {run.trigger}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{run.test_type}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                        run.status === 'completed' ? 'bg-green-100 text-green-700' :
                        run.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {run.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {run.warnings?.length > 0 ? (
                        <span className="text-yellow-600 font-medium">{run.warnings.length}</span>
                      ) : (
                        <span className="text-green-600">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Human Reference Scores */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Human Reference Scores</h3>
            {referenceStats && (
              <p className="text-xs text-gray-500 mt-0.5">
                {referenceStats.total_count} / {referenceStats.target_count} profiles ({referenceStats.progress_percent}% complete)
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={runReferenceComparison}
              disabled={calibrationTriggerLoading || (referenceStats?.total_count || 0) < 5}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 disabled:opacity-50"
              title={referenceStats?.total_count < 5 ? 'Need at least 5 reference scores' : 'Compare AI scores against human references'}
            >
              Run Comparison
            </button>
            <button
              onClick={() => setShowAddReferenceModal(true)}
              className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
            >
              + Add Reference
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {referenceStats && (
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${referenceStats.is_complete ? 'bg-green-500' : 'bg-indigo-500'}`}
                  style={{ width: `${Math.min(referenceStats.progress_percent, 100)}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 w-16">{referenceStats.total_count}/{referenceStats.target_count}</span>
            </div>
            {referenceStats.band_coverage && Object.keys(referenceStats.band_coverage).length > 0 && (
              <div className="flex gap-2 mt-2">
                {['emerging', 'developing', 'established', 'senior', 'executive'].map(band => (
                  <span key={band} className="text-xs text-gray-500">
                    {band}: <span className="font-medium">{referenceStats.band_coverage[band] || 0}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reference scores table */}
        {referenceScoresLoading ? (
          <div className="p-4 text-center text-gray-400">Loading reference scores...</div>
        ) : referenceScores.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p>No reference scores yet.</p>
            <p className="text-xs mt-1">Add human-assessed scores for 25 diverse profiles to enable calibration comparison.</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-64">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Profile</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Human Score</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Band</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Scored By</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {referenceScores.map((ref) => (
                  <tr key={ref.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-800">{ref.profile_name || 'Unknown'}</div>
                      {ref.profile_headline && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">{ref.profile_headline}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono font-medium">{ref.human_score.toFixed(1)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                        ref.human_band === 'executive' ? 'bg-purple-100 text-purple-700' :
                        ref.human_band === 'senior' ? 'bg-blue-100 text-blue-700' :
                        ref.human_band === 'established' ? 'bg-green-100 text-green-700' :
                        ref.human_band === 'developing' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {ref.human_band}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{ref.scored_by}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">
                      {ref.scored_at ? new Date(ref.scored_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => deleteReferenceScore(ref.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* How Calibration Works */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">How Calibration Works</h3>

        <div className="space-y-4 text-sm text-gray-600">
          <p>
            Calibration monitors the AI scoring system to ensure scores remain accurate, consistent, and meaningful over time.
            It runs automatically after each scoring batch and can be triggered manually.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {/* Distribution Analysis */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-800 mb-2">Distribution Analysis</h4>
              <p className="text-xs text-indigo-700 mb-2">
                Examines how scores are spread across the 0-10 scale to detect unhealthy patterns:
              </p>
              <ul className="text-xs text-indigo-600 space-y-1 ml-4 list-disc">
                <li><strong>Variance Check:</strong> Flags if scores cluster too tightly (low variance), suggesting the model isn't differentiating between profiles</li>
                <li><strong>Band Dominance:</strong> Alerts when &gt;60% of scores fall in a single band (emerging 0-2, developing 2-4, established 4-6, senior 6-8, executive 8-10)</li>
                <li><strong>Edge Clustering:</strong> Detects if too many scores pile up at extremes (0-1 or 9-10)</li>
              </ul>
            </div>

            {/* Drift Detection */}
            <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2">Drift Detection</h4>
              <p className="text-xs text-purple-700 mb-2">
                Compares recent scores against a 30-day historical baseline to detect systematic changes:
              </p>
              <ul className="text-xs text-purple-600 space-y-1 ml-4 list-disc">
                <li><strong>Mean Drift:</strong> Alerts if average score shifts ≥0.3 points (warning) or ≥0.5 points (critical) from baseline</li>
                <li><strong>Score Inflation:</strong> Detected when mean increases significantly—may indicate prompt or model changes</li>
                <li><strong>Score Deflation:</strong> Detected when mean decreases significantly—profiles may be under-valued</li>
                <li><strong>Band Shift:</strong> Flags when any band's percentage changes ≥10-20% from baseline</li>
              </ul>
            </div>
          </div>

          {/* Alert Severities */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="font-semibold text-gray-700 mb-2">Alert Severities</h4>
            <div className="flex gap-4 text-xs">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
                <span><strong>Critical:</strong> Immediate attention needed—scoring may be unreliable</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full bg-yellow-500"></span>
                <span><strong>Warning:</strong> Review recommended—potential issue detected</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
                <span><strong>Info:</strong> Notable change—no action required</span>
              </div>
            </div>
          </div>

          {/* When to Act */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="font-semibold text-gray-700 mb-2">When to Take Action</h4>
            <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
              <li><strong>Variance too low:</strong> Review the scoring prompt—it may need more differentiating criteria</li>
              <li><strong>Band dominance:</strong> Check if profile quality changed or if the prompt anchoring examples need adjustment</li>
              <li><strong>Score drift:</strong> If intentional (prompt update), acknowledge the alert. If unexpected, investigate recent changes</li>
              <li><strong>Prompt change alert:</strong> Consider re-scoring existing profiles if the prompt change was significant</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // BROKERAGE TAB RENDER (Phase 10)
  // ============================================
  const renderBrokerage = () => (
    <div className="space-y-6">
      {/* Sub-navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <button
          onClick={() => setBrokerageView('queue')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
            brokerageView === 'queue'
              ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📋 Request Queue
        </button>
        <button
          onClick={() => setBrokerageView('templates')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
            brokerageView === 'templates'
              ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ✉️ Templates
        </button>
        <button
          onClick={() => setBrokerageView('analytics')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
            brokerageView === 'analytics'
              ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📊 Analytics
        </button>
      </div>

      {/* Queue View */}
      {brokerageView === 'queue' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">Introduction Request Queue</h3>
            <div className="flex gap-2">
              {selectedRequests.length > 0 && (
                <button
                  onClick={() => setShowBulkPanel(true)}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
                >
                  Bulk Actions ({selectedRequests.length})
                </button>
              )}
            </div>
          </div>

          {/* Staff Workload Summary */}
          {brokerageQueue.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Staff Workload</h4>
                <span className="text-xs text-gray-400">{brokerageQueue.length} request{brokerageQueue.length !== 1 ? 's' : ''} shown</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const counts = {};
                  let unassigned = 0;
                  brokerageQueue.forEach(r => {
                    if (!r.assigned_to) {
                      unassigned++;
                    } else {
                      const name = r.assigned_to_name || `Staff #${r.assigned_to}`;
                      counts[r.assigned_to] = counts[r.assigned_to] || { name, count: 0 };
                      counts[r.assigned_to].count++;
                    }
                  });
                  return (
                    <>
                      <button
                        onClick={() => setQueueFilters(f => ({ ...f, assigned_to: 0 }))}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          queueFilters.assigned_to === 0
                            ? 'bg-amber-200 text-amber-900 ring-2 ring-amber-400'
                            : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                        }`}
                      >
                        Unassigned <span className="font-bold">{unassigned}</span>
                      </button>
                      {Object.entries(counts).map(([id, { name, count }]) => (
                        <button
                          key={id}
                          onClick={() => setQueueFilters(f => ({ ...f, assigned_to: parseInt(id) }))}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            queueFilters.assigned_to === parseInt(id)
                              ? 'bg-indigo-200 text-indigo-900 ring-2 ring-indigo-400'
                              : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
                          }`}
                        >
                          {name} <span className="font-bold">{count}</span>
                        </button>
                      ))}
                      {queueFilters.assigned_to !== null && (
                        <button
                          onClick={() => setQueueFilters(f => ({ ...f, assigned_to: null }))}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                        >
                          Show All
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Filter Bar */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex flex-wrap gap-3 items-end">
              {/* Status Filter */}
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={queueFilters.status.length === 1 ? queueFilters.status[0] : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setQueueFilters(f => ({ ...f, status: val ? [val] : [] }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Active</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="TRIAGE">Triage</option>
                  <option value="IN_REVIEW">In Review</option>
                  <option value="APPROVED">Approved</option>
                  <option value="STAGED">Staged</option>
                  <option value="SENT">Sent</option>
                  <option value="AWAITING">Awaiting</option>
                  <option value="CONSENTED">Consented</option>
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="CONNECTED">Connected</option>
                </select>
              </div>

              {/* Type Filter */}
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  value={queueFilters.request_type.length === 1 ? queueFilters.request_type[0] : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setQueueFilters(f => ({ ...f, request_type: val ? [val] : [] }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Types</option>
                  <option value="MENTORSHIP_UP">Mentorship Up</option>
                  <option value="PEER_MID_LEVEL">Peer Mid-Level</option>
                  <option value="BOARD_PATHWAY">Board Pathway</option>
                </select>
              </div>

              {/* Assigned To Filter */}
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To</label>
                <select
                  value={queueFilters.assigned_to === null ? '' : queueFilters.assigned_to}
                  onChange={(e) => {
                    const val = e.target.value;
                    setQueueFilters(f => ({ ...f, assigned_to: val === '' ? null : parseInt(val) }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All</option>
                  <option value="0">Unassigned</option>
                  {staffMembers.map(s => (
                    <option key={s.id} value={s.id}>{s.full_name}</option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Sort By</label>
                <div className="flex gap-1">
                  <select
                    value={queueFilters.sort_by}
                    onChange={(e) => setQueueFilters(f => ({ ...f, sort_by: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="priority_score">Priority</option>
                    <option value="created_at">Created</option>
                    <option value="updated_at">Updated</option>
                  </select>
                  <button
                    onClick={() => setQueueFilters(f => ({ ...f, sort_dir: f.sort_dir === 'desc' ? 'asc' : 'desc' }))}
                    className="px-3 py-2 border border-gray-300 rounded-r-lg text-sm hover:bg-gray-50 font-medium"
                    title={queueFilters.sort_dir === 'desc' ? 'High to Low' : 'Low to High'}
                  >
                    {queueFilters.sort_dir === 'desc' ? '↓' : '↑'}
                  </button>
                </div>
              </div>

              {/* Reset Filters */}
              {(queueFilters.status.length > 0 || queueFilters.request_type.length > 0 || queueFilters.assigned_to !== null || queueFilters.sort_by !== 'priority_score' || queueFilters.sort_dir !== 'desc') && (
                <button
                  onClick={() => setQueueFilters({ status: [], request_type: [], assigned_to: null, sort_by: 'priority_score', sort_dir: 'desc' })}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Request Queue Table */}
          {brokerageQueueLoading ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-500">Loading queue...</p>
            </div>
          ) : brokerageQueue.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
              <p className="text-lg mb-2">📭 No Pending Requests</p>
              <p className="text-sm">
                There are no introduction requests in the queue at this time.
              </p>
              <button
                onClick={fetchBrokerageQueue}
                className="mt-4 px-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                🔄 Refresh Queue
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
                <span className="text-sm text-gray-600">{brokerageQueue.length} request{brokerageQueue.length !== 1 ? 's' : ''}</span>
                <button
                  onClick={fetchBrokerageQueue}
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                >
                  🔄 Refresh
                </button>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedRequests.length === brokerageQueue.length && brokerageQueue.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRequests(brokerageQueue.map(r => r.id));
                          } else {
                            setSelectedRequests([]);
                          }
                        }}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requester</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {brokerageQueue.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRequests.includes(request.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRequests([...selectedRequests, request.id]);
                            } else {
                              setSelectedRequests(selectedRequests.filter(id => id !== request.id));
                            }
                          }}
                          className="w-4 h-4 text-indigo-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono text-gray-600">{request.id?.substring(0, 8)}...</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          ({'PEER_MID_LEVEL':'bg-blue-100 text-blue-800','PEER_SENIOR':'bg-blue-100 text-blue-800','MENTORSHIP_UP':'bg-purple-100 text-purple-800','MENTORSHIP_HIGH':'bg-purple-100 text-purple-800','BOARD_PATHWAY':'bg-amber-100 text-amber-800','HBCU_MENTORSHIP':'bg-teal-100 text-teal-800'})[request.request_type] || 'bg-gray-100 text-gray-800'
                        }`}>
                          {({'PEER_MID_LEVEL':'Peer Networking','PEER_SENIOR':'Peer Networking (Senior)','MENTORSHIP_UP':'Seek Mentorship','MENTORSHIP_HIGH':'Offer Mentorship','BOARD_PATHWAY':'Board Pathway','HBCU_MENTORSHIP':'HBCU Mentorship'})[request.request_type] || request.request_type?.replace(/_/g, ' ') || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          request.status === 'SUBMITTED' ? 'bg-yellow-100 text-yellow-800' :
                          request.status === 'TRIAGE' ? 'bg-orange-100 text-orange-800' :
                          request.status === 'IN_REVIEW' ? 'bg-blue-100 text-blue-800' :
                          request.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                          request.status === 'STAGED' ? 'bg-indigo-100 text-indigo-800' :
                          request.status === 'SENT' ? 'bg-purple-100 text-purple-800' :
                          request.status === 'AWAITING' ? 'bg-pink-100 text-pink-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden mr-2">
                            <div
                              className={`h-full ${
                                request.priority_score >= 0.8 ? 'bg-red-500' :
                                request.priority_score >= 0.6 ? 'bg-orange-500' :
                                request.priority_score >= 0.4 ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}
                              style={{ width: `${(request.priority_score || 0.5) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500">{((request.priority_score || 0.5) * 100).toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-600">{request.requester_name || 'Unknown'}</span>
                          {request.requester_tier && (
                            <span className={`ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              request.requester_tier.tier_level >= 3 ? 'bg-amber-100 text-amber-700' :
                              request.requester_tier.tier_level >= 2 ? 'bg-indigo-100 text-indigo-700' :
                              request.requester_tier.tier_level >= 1 ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {request.requester_tier.tier_name || 'Community'}
                            </span>
                          )}
                          {/* Quick upgrade button — shows for alumni with tier < 3 */}
                          {request.requester_alumni_user_id && (!request.requester_tier || request.requester_tier.tier_level < 3) && (
                            upgradingRequestId === request.id ? (
                              <span className="ml-1 inline-flex items-center gap-1">
                                <select
                                  value={upgradeTierSlug}
                                  onChange={(e) => setUpgradeTierSlug(e.target.value)}
                                  className="text-[10px] px-1 py-0.5 border border-amber-300 rounded bg-white dark:bg-gray-700 dark:text-gray-100"
                                >
                                  <option value="">Tier...</option>
                                  {queueTiers.filter(t => t.level > (request.requester_tier?.tier_level || 0)).map(t => (
                                    <option key={t.slug} value={t.slug}>{t.name}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => handleQuickUpgrade(request.requester_alumni_user_id, upgradeTierSlug, request.requester_name)}
                                  disabled={!upgradeTierSlug || upgradeLoading}
                                  className="text-[10px] px-1.5 py-0.5 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white rounded font-medium"
                                  title="Grant membership"
                                >
                                  {upgradeLoading ? '...' : 'Go'}
                                </button>
                                <button
                                  onClick={() => { setUpgradingRequestId(null); setUpgradeTierSlug(''); }}
                                  className="text-[10px] text-gray-400 hover:text-gray-600"
                                  title="Cancel"
                                >
                                  ✕
                                </button>
                              </span>
                            ) : (
                              <button
                                onClick={() => { setUpgradingRequestId(request.id); setUpgradeTierSlug(''); }}
                                className="ml-1 p-0.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded"
                                title="Quick upgrade tier"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                            )
                          )}
                          {/* Show refresh button if name looks like test data or "Unknown" */}
                          {request.requester_person_id && (
                            request.requester_name === 'Test User' ||
                            request.requester_name === 'Unknown' ||
                            !request.requester_name
                          ) && (
                            <button
                              onClick={() => refreshRequesterDisplayName(request.requester_person_id)}
                              className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                              title="Refresh display name from LinkedIn/profile data"
                            >
                              🔄
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500">
                          {request.created_at ? new Date(request.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {request.assigned_to ? (
                            <>
                              <span className="text-sm text-gray-700 font-medium">{request.assigned_to_name || `Staff #${request.assigned_to}`}</span>
                              {/* Release button — only for own claims */}
                              {request.assigned_to === currentAdminId && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API_URL}/api/staff/brokerage/requests/${request.id}/release`, {}, authHeaders());
                                      fetchBrokerageQueue();
                                    } catch (err) {
                                      alert('Failed to release: ' + (err.response?.data?.detail || err.message));
                                    }
                                  }}
                                  className="px-2 py-0.5 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded inline-flex items-center gap-1 w-fit"
                                >
                                  Release
                                </button>
                              )}
                              {/* Superadmin reassign */}
                              {isSuperAdmin && request.assigned_to !== currentAdminId && (
                                <div className="flex items-center gap-1 mt-1">
                                  <select
                                    value={assignStaffSelection[request.id] || ''}
                                    onChange={(e) => setAssignStaffSelection(prev => ({ ...prev, [request.id]: e.target.value }))}
                                    className="text-xs border border-gray-300 rounded px-1 py-0.5 w-28"
                                  >
                                    <option value="">Reassign...</option>
                                    {staffMembers.map(s => (
                                      <option key={s.id} value={s.id}>{s.full_name}</option>
                                    ))}
                                  </select>
                                  {assignStaffSelection[request.id] && (
                                    <button
                                      disabled={assigningRequestId === request.id}
                                      onClick={async () => {
                                        setAssigningRequestId(request.id);
                                        try {
                                          await axios.post(`${API_URL}/api/staff/brokerage/bulk-operation`, {
                                            request_ids: [request.id],
                                            operation: 'assign',
                                            params: { staff_id: parseInt(assignStaffSelection[request.id]) }
                                          }, authHeaders());
                                          setAssignStaffSelection(prev => ({ ...prev, [request.id]: '' }));
                                          fetchBrokerageQueue();
                                        } catch (err) {
                                          alert('Failed to reassign: ' + (err.response?.data?.detail || err.message));
                                        } finally {
                                          setAssigningRequestId(null);
                                        }
                                      }}
                                      className="px-1.5 py-0.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                      {assigningRequestId === request.id ? '...' : 'Go'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="text-xs text-gray-400 italic">Unassigned</span>
                              {/* Superadmin: assign to specific staff */}
                              {isSuperAdmin && (
                                <div className="flex items-center gap-1 mt-1">
                                  <select
                                    value={assignStaffSelection[request.id] || ''}
                                    onChange={(e) => setAssignStaffSelection(prev => ({ ...prev, [request.id]: e.target.value }))}
                                    className="text-xs border border-gray-300 rounded px-1 py-0.5 w-28"
                                  >
                                    <option value="">Assign to...</option>
                                    {staffMembers.map(s => (
                                      <option key={s.id} value={s.id}>{s.full_name}</option>
                                    ))}
                                  </select>
                                  {assignStaffSelection[request.id] && (
                                    <button
                                      disabled={assigningRequestId === request.id}
                                      onClick={async () => {
                                        setAssigningRequestId(request.id);
                                        try {
                                          await axios.post(`${API_URL}/api/staff/brokerage/bulk-operation`, {
                                            request_ids: [request.id],
                                            operation: 'assign',
                                            params: { staff_id: parseInt(assignStaffSelection[request.id]) }
                                          }, authHeaders());
                                          setAssignStaffSelection(prev => ({ ...prev, [request.id]: '' }));
                                          fetchBrokerageQueue();
                                        } catch (err) {
                                          alert('Failed to assign: ' + (err.response?.data?.detail || err.message));
                                        } finally {
                                          setAssigningRequestId(null);
                                        }
                                      }}
                                      className="px-1.5 py-0.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                      {assigningRequestId === request.id ? '...' : 'Go'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {/* Claim — all staff can self-assign unassigned requests */}
                          {!request.assigned_to && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.post(`${API_URL}/api/staff/brokerage/requests/${request.id}/assign`, {}, authHeaders());
                                  fetchBrokerageQueue();
                                } catch (err) {
                                  alert('Failed to claim: ' + (err.response?.data?.detail || err.message));
                                }
                              }}
                              className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            >
                              Claim
                            </button>
                          )}
                          <button
                            onClick={() => openRequestDetail(request.id)}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={() => {
                alert('To view relationship memory, first search for an executive in the Search tab, then click on their profile to access memory details.');
              }}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-sm transition-all text-left"
            >
              <span className="text-xl">🧠</span>
              <p className="font-medium text-gray-800 mt-2">View Relationship Memory</p>
              <p className="text-xs text-gray-500">Select an executive from Search tab first</p>
            </button>
            <button
              onClick={() => {
                alert('Staff performance metrics will be available once the brokerage system has processed requests.');
              }}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-sm transition-all text-left"
            >
              <span className="text-xl">📈</span>
              <p className="font-medium text-gray-800 mt-2">Staff Performance</p>
              <p className="text-xs text-gray-500">View metrics after processing requests</p>
            </button>
            <button
              onClick={() => setBrokerageView('analytics')}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-sm transition-all text-left"
            >
              <span className="text-xl">📊</span>
              <p className="font-medium text-gray-800 mt-2">Full Analytics</p>
              <p className="text-xs text-gray-500">Dashboard and reports</p>
            </button>
          </div>
        </div>
      )}

      {/* Templates View */}
      {brokerageView === 'templates' && (
        <OutreachTemplateEditor darkMode={false} onClose={() => setBrokerageView('queue')} />
      )}

      {/* Analytics View */}
      {brokerageView === 'analytics' && (
        <BrokerageAnalyticsDashboard darkMode={false} />
      )}

      {/* Modals */}
      {showBulkPanel && (
        <BulkOperationsPanel
          selectedRequests={selectedRequests}
          staffMembers={staffMembers}
          onClose={() => setShowBulkPanel(false)}
          onOperationComplete={(result) => {
            setShowBulkPanel(false);
            setSelectedRequests([]);
            // Refresh queue
          }}
          darkMode={false}
        />
      )}

      {showMemoryViewer && selectedExecId && (
        <RelationshipMemoryViewer
          execPersonId={selectedExecId}
          execName={selectedExecName}
          onClose={() => {
            setShowMemoryViewer(false);
            setSelectedExecId(null);
          }}
          darkMode={false}
        />
      )}

      {showStaffPerformance && selectedStaffId && (
        <StaffPerformanceView
          staffId={selectedStaffId}
          staffName={selectedStaffName}
          onClose={() => {
            setShowStaffPerformance(false);
            setSelectedStaffId(null);
          }}
          darkMode={false}
        />
      )}

      {/* Request Workflow Modal */}
      {showRequestDetail && selectedRequestId && (
        <RequestWorkflowModal
          requestId={selectedRequestId}
          onClose={() => { setShowRequestDetail(false); setSelectedRequestId(null); }}
          onRefreshQueue={fetchBrokerageQueue}
        />
      )}
    </div>
  );

  // ============================================
  // ACCESS CONTROL GATE
  // ============================================
  if (!accessChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-6">You do not have permission to access Executive Intelligence tools. Contact a super admin to request access.</p>
          <button
            onClick={() => navigate('/admin')}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          >
            Return to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  // No visible tabs for this admin
  if (adminPermissions && visibleTabs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Tabs Available</h2>
          <p className="text-gray-600 mb-6">No Executive Intelligence tabs are enabled for your account. Contact a super admin to request access to specific tools.</p>
          <button
            onClick={() => navigate('/admin')}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          >
            Return to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Executive Intelligence</h1>
              <p className="text-xs text-gray-500 mt-0.5">Alumni Career Tracking Platform</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/admin')}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 font-medium"
              >
                &larr; Admin
              </button>
              {isSuperAdmin && (<>
                <button
                  onClick={() => navigate('/admin/outreach-manager')}
                  className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-xs font-medium"
                >
                  Outreach Manager
                </button>
                <button
                  onClick={() => navigate('/admin/crm')}
                  className="px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 text-xs font-medium"
                >
                  CRM
                </button>
                <button
                  onClick={() => { changeTab('imports'); navigate('/admin/executive-intelligence?tab=imports&section=unprovisioned'); }}
                  className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-xs font-medium"
                >
                  Research
                </button>
                <button
                  onClick={() => changeTab('brokerage')}
                  className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-xs font-medium"
                >
                  Brokerage
                </button>
                <button
                  onClick={() => navigate('/admin/swee')}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium"
                >
                  SWEE
                </button>
                <button
                  onClick={() => navigate('/admin/membership-manager')}
                  className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 text-xs font-medium"
                >
                  Membership
                </button>
              </>)}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 overflow-x-auto pb-0 -mb-px">
            {visibleTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => changeTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-1.5">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error Banner */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between items-center">
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 text-lg">&times;</button>
          </div>
        )}
        {successMsg && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex justify-between items-center">
            <span className="text-sm">{successMsg}</span>
            <button onClick={() => setSuccessMsg(null)} className="text-green-500 hover:text-green-700 text-lg">&times;</button>
          </div>
        )}

        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'search' && renderSearch()}
        {activeTab === 'map' && renderMap()}
        {activeTab === 'favorites' && renderFavorites()}
        {activeTab === 'imports' && renderImports()}
        {activeTab === 'reviews' && renderReviews()}
        {activeTab === 'scoring' && renderScoring()}
        {activeTab === 'calibration' && renderCalibration()}
        {activeTab === 'reports' && renderReports()}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'brokerage' && renderBrokerage()}
      </div>

      {/* Score Bands Definition Modal */}
      {showScoreBandsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-800">Leadership Score Bands</h2>
              <button
                onClick={() => setShowScoreBandsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                Leadership scores range from 0 to 10 and are grouped into five bands based on career progression,
                scope of responsibility, and potential value to NACME's mission.
              </p>

              {/* Executive */}
              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800">Executive</span>
                  <span className="text-sm text-gray-500">Score: 8.0 – 10.0</span>
                </div>
                <p className="mt-1 text-sm text-gray-700">
                  C-suite executives, board members, or those with enterprise-wide leadership responsibility.
                  Typically have P&L ownership exceeding $100M, public company board seats, or impact spanning 5,000+ employees.
                  Highest potential for board engagement, major donor cultivation, and strategic partnerships.
                </p>
              </div>

              {/* Senior */}
              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800">Senior</span>
                  <span className="text-sm text-gray-500">Score: 6.0 – 7.9</span>
                </div>
                <p className="mt-1 text-sm text-gray-700">
                  VP-level executives or equivalent with significant organizational impact.
                  Often have P&L ownership of $10M+, private company board experience, or lead enterprise functions
                  (HR, Legal, DEI, Finance) affecting 1,000+ employees. Strong candidates for advisory roles and mentorship programs.
                </p>
              </div>

              {/* Established */}
              <div className="border-l-4 border-green-500 pl-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-800">Established</span>
                  <span className="text-sm text-gray-500">Score: 4.0 – 5.9</span>
                </div>
                <p className="mt-1 text-sm text-gray-700">
                  Director-level professionals with solid leadership track records.
                  Typically manage departments or multiple teams with budget authority and 20+ reports.
                  Good candidates for mentorship, speaking engagements, and alumni network leadership.
                </p>
              </div>

              {/* Developing */}
              <div className="border-l-4 border-yellow-500 pl-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">Developing</span>
                  <span className="text-sm text-gray-500">Score: 2.0 – 3.9</span>
                </div>
                <p className="mt-1 text-sm text-gray-700">
                  Manager-level professionals growing into leadership roles.
                  May lead small teams or serve as senior individual contributors at larger organizations.
                  Emerging talent worth tracking for future engagement as their careers progress.
                </p>
              </div>

              {/* Emerging */}
              <div className="border-l-4 border-gray-400 pl-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-gray-100 text-gray-800">Emerging</span>
                  <span className="text-sm text-gray-500">Score: 0.0 – 1.9</span>
                </div>
                <p className="mt-1 text-sm text-gray-700">
                  Early-career professionals with limited leadership experience to date.
                  Individual contributors or those just beginning their management journey.
                  May show promise but need more career development before significant NACME engagement.
                </p>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  <strong>Note:</strong> Scores are computed by AI based on career trajectory, advancement velocity,
                  scope of responsibility, and recency of senior experience. HBCU education and technical-to-executive
                  career paths receive modest score boosts aligned with NACME's mission.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowScoreBandsModal(false)}
                className="w-full px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Reference Score Modal */}
      {showAddReferenceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Add Human Reference Score</h2>
              <button
                onClick={() => {
                  setShowAddReferenceModal(false);
                  setReferenceFormData({ profile_id: '', human_score: '', rationale: '', trajectory_score: '', velocity_score: '', scope_score: '', recency_score: '' });
                  setProfileSearchQuery('');
                  setProfileSearchResults([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Profile Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Profile *</label>
                {referenceFormData.profile_id ? (
                  <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded p-2">
                    <span className="text-sm text-indigo-800">
                      {profileSearchResults.find(p => p.id === referenceFormData.profile_id)?.name || 'Selected Profile'}
                    </span>
                    <button
                      onClick={() => {
                        setReferenceFormData({ ...referenceFormData, profile_id: '' });
                        setProfileSearchQuery('');
                      }}
                      className="text-indigo-600 hover:text-indigo-800 text-sm"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={profileSearchQuery}
                      onChange={(e) => {
                        setProfileSearchQuery(e.target.value);
                        searchProfilesForReference(e.target.value);
                      }}
                      placeholder="Search by name..."
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                    {profileSearchLoading && (
                      <p className="text-xs text-gray-500 mt-1">Searching...</p>
                    )}
                    {profileSearchResults.length > 0 && (
                      <div className="mt-2 border border-gray-200 rounded max-h-40 overflow-y-auto">
                        {profileSearchResults.map((profile) => (
                          <button
                            key={profile.id}
                            onClick={() => {
                              setReferenceFormData({ ...referenceFormData, profile_id: profile.id });
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="text-sm font-medium text-gray-800">{profile.name}</div>
                            {profile.headline && (
                              <div className="text-xs text-gray-500 truncate">{profile.headline}</div>
                            )}
                            {profile.current_score && (
                              <div className="text-xs text-indigo-600 mt-0.5">
                                AI Score: {profile.current_score.toFixed(1)} ({profile.current_band})
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Human Score */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Human Score (0-10) *</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={referenceFormData.human_score}
                  onChange={(e) => setReferenceFormData({ ...referenceFormData, human_score: e.target.value })}
                  placeholder="e.g., 7.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
                {referenceFormData.human_score && (
                  <p className="text-xs text-gray-500 mt-1">
                    Band: {
                      parseFloat(referenceFormData.human_score) >= 8 ? 'Executive' :
                      parseFloat(referenceFormData.human_score) >= 6 ? 'Senior' :
                      parseFloat(referenceFormData.human_score) >= 4 ? 'Established' :
                      parseFloat(referenceFormData.human_score) >= 2 ? 'Developing' :
                      'Emerging'
                    }
                  </p>
                )}
              </div>

              {/* Rationale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rationale (optional)</label>
                <textarea
                  value={referenceFormData.rationale}
                  onChange={(e) => setReferenceFormData({ ...referenceFormData, rationale: e.target.value })}
                  placeholder="Why did you assign this score?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>

              {/* Optional Sub-scores */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sub-scores (optional)</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Trajectory</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={referenceFormData.trajectory_score}
                      onChange={(e) => setReferenceFormData({ ...referenceFormData, trajectory_score: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Velocity</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={referenceFormData.velocity_score}
                      onChange={(e) => setReferenceFormData({ ...referenceFormData, velocity_score: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Scope</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={referenceFormData.scope_score}
                      onChange={(e) => setReferenceFormData({ ...referenceFormData, scope_score: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Recency</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={referenceFormData.recency_score}
                      onChange={(e) => setReferenceFormData({ ...referenceFormData, recency_score: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddReferenceModal(false);
                  setReferenceFormData({ profile_id: '', human_score: '', rationale: '', trajectory_score: '', velocity_score: '', scope_score: '', recency_score: '' });
                  setProfileSearchQuery('');
                  setProfileSearchResults([]);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={addReferenceScore}
                disabled={referenceFormLoading || !referenceFormData.profile_id || !referenceFormData.human_score}
                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {referenceFormLoading ? 'Adding...' : 'Add Reference Score'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Missing State Data Modal */}
      {showMissingStateModal && missingStateData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Profiles Missing State Data</h3>
                <p className="text-sm text-gray-500">
                  {missingStateData.total_missing_state} profiles have coordinates but no state/region
                </p>
              </div>
              <button
                onClick={() => setShowMissingStateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location Raw</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {missingStateData.profiles.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{p.full_name}</td>
                      <td className="px-3 py-2 text-gray-600">{p.location_raw || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{p.location_city || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-red-500">{p.location_region || <span className="text-red-300">missing</span>}</td>
                      <td className="px-3 py-2 text-gray-600">{p.location_country || <span className="text-gray-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {missingStateData.profiles.length === 0 && (
                <p className="text-center text-gray-500 py-8">No profiles missing state data</p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500">
                Showing {missingStateData.showing} of {missingStateData.total_missing_state} profiles.
                The "Location Raw" column shows the original LinkedIn location string.
                If it doesn't contain state info (e.g., just "San Francisco"), the geocoder cannot extract it.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search Outreach Assign Modal */}
      {showSearchAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Assign Outreach ({searchSelected.size} contact{searchSelected.size !== 1 ? 's' : ''})</h2>
              <button onClick={() => setShowSearchAssignModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To *</label>
                <select
                  value={searchAssignForm.assigned_to_id}
                  onChange={(e) => setSearchAssignForm(f => ({ ...f, assigned_to_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  <option value="">Select staff member...</option>
                  {unprovisionedAssignableAdmins.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name || a.email}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
                  <select
                    value={searchAssignForm.goal}
                    onChange={(e) => setSearchAssignForm(f => ({ ...f, goal: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  >
                    <option value="engagement">Engagement</option>
                    <option value="reactivation">Reactivation</option>
                    <option value="upgrade">Upgrade</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="retention">Retention</option>
                    <option value="event_invite">Event Invite</option>
                    <option value="feedback">Feedback</option>
                    <option value="introduction">Introduction</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={searchAssignForm.priority}
                    onChange={(e) => setSearchAssignForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={searchAssignForm.due_date}
                  onChange={(e) => setSearchAssignForm(f => ({ ...f, due_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purpose / Notes</label>
                <textarea
                  value={searchAssignForm.purpose}
                  onChange={(e) => setSearchAssignForm(f => ({ ...f, purpose: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  placeholder="Optional notes about this outreach..."
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowSearchAssignModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSearchAssignOutreach}
                disabled={searchAssigning || !searchAssignForm.assigned_to_id}
                className="px-4 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 disabled:opacity-50"
              >
                {searchAssigning ? 'Assigning...' : `Assign ${searchSelected.size} Contact${searchSelected.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LinkedIn Comment Import Modal */}
      {showCommentImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Import LinkedIn Comments</h3>
                <button onClick={() => setShowCommentImportModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Profiles to import</span>
                <span className="text-sm font-semibold text-gray-900">{searchSelected.size}</span>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-600">Max pages per profile</label>
                <select
                  value={commentImportMaxPages}
                  onChange={(e) => setCommentImportMaxPages(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                >
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} page{n > 1 ? 's' : ''} (~{n * 100} comments)</option>)}
                </select>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-sky-700">Estimated results</span>
                  <span className="font-medium text-sky-900">{(searchSelected.size * commentImportMaxPages * 100).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-sky-700">Estimated cost</span>
                  <span className="font-medium text-sky-900">${(searchSelected.size * commentImportMaxPages * 100 * 0.005).toFixed(2)}</span>
                </div>
              </div>
              {commentCostSummary && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Monthly budget</span>
                    <span className="font-medium text-gray-900">${commentCostSummary.budget_usd?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Spent this month</span>
                    <span className="font-medium text-gray-900">${commentCostSummary.spent_this_month_usd?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Remaining</span>
                    <span className={`font-medium ${commentCostSummary.remaining_usd < (searchSelected.size * commentImportMaxPages * 100 * 0.005) ? 'text-red-600' : 'text-green-600'}`}>
                      ${commentCostSummary.remaining_usd?.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              {commentCostSummary && commentCostSummary.remaining_usd < (searchSelected.size * commentImportMaxPages * 100 * 0.005) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 font-medium">Over budget! Estimated cost exceeds remaining monthly budget. Reduce profiles or pages, or increase the budget in admin settings.</p>
                </div>
              )}
              {commentImportResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium text-green-800">Import Complete</p>
                  <p className="text-xs text-green-700">
                    {commentImportResult.profiles_skipped_no_linkedin > 0 && `${commentImportResult.profiles_skipped_no_linkedin} skipped (no LinkedIn). `}
                    Processed: {commentImportResult.status === 'completed' ? 'All profiles done.' : commentImportResult.status}
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCommentImportModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {commentImportResult ? 'Close' : 'Cancel'}
              </button>
              {!commentImportResult && (
                <button
                  onClick={() => { handleBulkCommentImport(); }}
                  disabled={commentImporting || (commentCostSummary && commentCostSummary.remaining_usd < (searchSelected.size * commentImportMaxPages * 100 * 0.005))}
                  className="px-4 py-2 text-sm text-white bg-sky-600 rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {commentImporting ? 'Importing...' : `Import Comments ($${(searchSelected.size * commentImportMaxPages * 100 * 0.005).toFixed(2)})`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExecutiveIntelligence;
